export default [
  {    "id": "reading-account-manager-essity",
    "title": "Being an Account Manager at Essity",
    "subtitle": "Workplace",
    "category": "reading-exercises",
    "theme": "Workplace",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about what it's like to work as an account manager at Essity.",
    "readingcompTitle": "Being an Account Manager at Essity",
    "topic": "Workplace",
    "image": "/Images/accountmanager1.webp",
    "vocabulary": [
      "distributor - a company that buys products and sells them to other businesses",
      "long-term cooperation - working together for a long time",
      "solutions - ways to solve problems"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Being an Account Manager at Essity</p>",
          "<p>An account manager at Essity works mainly with distributors, not with final customers. Distributors buy Essity products and sell them to hospitals, care homes, shops, and other businesses. The account manager helps distributors do their job well.</p>",
          "<p>Communication is a very important part of the job. Account managers talk with distributors by email, phone, and in meetings. They discuss prices, orders, promotions, and product information. They also explain company rules and help when there are questions or problems.</p>",
          "<p>Sometimes distributors have difficulties, such as delivery delays, stock problems, or customer complaints. The account manager listens carefully and works with other teams, like logistics or customer service, to find solutions. The job can be challenging, but it is rewarding to build strong partnerships and see good results.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Who does the account manager at Essity mainly work with?",
            "choices": [
              "A. Final customers",
              "B. Hospitals only",
              "C. Distributors",
              "D. Shops"
            ]
          },
          {
            "number": "2",
            "question": "What do distributors do?",
            "choices": [
              "A. Make Essity products",
              "B. Sell products to other businesses",
              "C. Work in customer service",
              "D. Deliver products to homes"
            ]
          },
          {
            "number": "3",
            "question": "How does the account manager communicate with distributors?",
            "choices": [
              "A. Only by email",
              "B. Only in meetings",
              "C. By email, phone, and meetings",
              "D. By letters"
            ]
          },
          {
            "number": "4",
            "question": "What problems can distributors have?",
            "choices": [
              "A. Office cleaning",
              "B. Delivery times and stock levels",
              "C. Marketing budgets",
              "D. Hiring new staff"
            ]
          },
          {
            "number": "5",
            "question": "Why is the job rewarding?",
            "choices": [
              "A. Because it is easy",
              "B. Because it has no pressure",
              "C. Because it builds strong partnerships",
              "D. Because it requires no communication"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-common-myths-people-still-believe",
    "title": "Common Myths People Still Believe",
    "subtitle": "society",
    "category": "reading-exercises",
    "theme": "society",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about common myths people still believe and why they're false.",
    "readingcompTitle": "Common Myths People Still Believe",
    "topic": "society",
    "image": "/Images/belief.webp",
    "vocabulary": [
      "jurisdiction - a legal area with its own rules and authority",
      "neuroimaging - technology that creates images of brain activity",
      "archaeological evidence - physical remains that provide proof about past cultures",
      "causal relationship - when one thing directly causes another",
      "propaganda - information used to influence opinions or beliefs",
      "myth - a widely held but false belief"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Many everyday beliefs persist even though they are not supported by facts. The belief that humans use only 10% of their brains is false; neuroimaging demonstrates widespread activity even during simple tasks. Another myth claims that hair and nails continue to grow after death. They do not—skin simply dries and pulls back, creating the illusion of growth.</p>",
          "<p>Popular culture adds more confusion. Many people believe that China’s Great Wall can be seen from space, but it is not visible to the naked eye from low Earth orbit due to its narrow width and natural coloring. The iconic image of Vikings wearing horned helmets is also a myth created by 19th-century artwork, not by archaeological evidence.</p>",
          "<p>Health and behavior myths are equally persistent. Sugar is often blamed for hyperactivity in children, yet controlled studies show no consistent causal relationship. The excitement of parties or holidays is usually the real factor. Cracking knuckles is believed to cause arthritis, but research does not support this. The idea that swallowed gum remains in the stomach for seven years is also incorrect; gum passes through the digestive system normally.</p>",
          "<p>Animals are frequent subjects of misunderstanding. Bulls are not angered by the color red, because they cannot perceive it; they react to movement instead. Lightning is often said never to strike the same place twice, yet tall buildings and towers are struck repeatedly. Even carrots have their own myth, as wartime propaganda once promoted them as a way to improve night vision. While carrots support eye health, they do not grant exceptional night-time abilities. Finally, shaving does not make hair grow back thicker or faster. The blunt edge may feel coarser, but growth patterns remain unchanged.</p>"
        ],
        "questions": []
      }
    ]
  },
  {    "id": "reading-the-joy-and-science-of-gift-giving",
    "title": "The Joy and Science of Gift Giving",
    "subtitle": "society",
    "category": "reading-exercises",
    "theme": "society",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about the history, psychology, and culture of gift giving.",
    "readingcompTitle": "The Joy and Science of Gift Giving",
    "topic": "society",
    "image": "/Images/giftgivng.webp",
    "vocabulary": [
      "archaeologist - a person who studies ancient cultures and artifacts",
      "ritualized - done according to a fixed set of rules or customs",
      "gratitude - the feeling of being thankful",
      "reward center - a part of the brain that responds to pleasure",
      "helper’s high - a feeling of happiness from helping others",
      "etiquette - rules for polite behavior",
      "bond - a strong connection between people",
      "thoughtfulness - caring about others’ needs or feelings"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Gift giving is something almost everyone does, yet it is more than just wrapping paper and bows. From ancient times to today, humans have exchanged gifts to connect, celebrate, and even negotiate. Archaeologists have found evidence that gifts were used to build trust and alliances in prehistoric societies.</p>",
          "<p>Different cultures have their own gift-giving traditions. In Japan, giving gifts is highly ritualized and shows respect and gratitude. In Western cultures, holidays like Christmas and birthdays are the main occasions for exchanging presents. Despite differences, the main idea is universal: gifts show that we care.</p>",
          "<p>Psychologists have studied why giving gifts makes us feel good. It can activate reward centers in our brains, producing a feeling called the “helper’s high.” A thoughtful gift can communicate more than words, saying, “I see you, I know you, and I value you.”</p>",
          "<p>Gift giving can also be stressful—choosing the perfect gift, worrying about etiquette, or staying within a budget. But even imperfect gifts carry thoughtfulness that matters more than material value. The act of giving strengthens social bonds and reminds us of our need to connect.</p>",
          "<p>Next time you wrap a present, remember: it’s not just about the object inside. It’s about thought, connection, and the joy of making someone else smile.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is one reason humans have given gifts since ancient times?",
            "choices": [
              "a) To build trust and alliances",
              "b) To increase taxes",
              "c) To decorate homes",
              "d) To practice writing"
            ]
          },
          {
            "number": "2",
            "question": "How is gift giving different in Japan compared to Western cultures?",
            "choices": [
              "a) Gifts are mainly for birthdays in Japan",
              "b) Japanese gift giving is highly ritualized",
              "c) Western cultures never give gifts",
              "d) Japan does not have holidays for gifts"
            ]
          },
          {
            "number": "3",
            "question": "What is a “helper’s high”?",
            "choices": [
              "a) Feeling happy from helping others",
              "b) A type of gift box",
              "c) A Japanese holiday",
              "d) A brain illness"
            ]
          },
          {
            "number": "4",
            "question": "Why can gift giving feel stressful?",
            "choices": [
              "a) Because wrapping paper is expensive",
              "b) Choosing the perfect gift and worrying about etiquette",
              "c) Gifts must be handmade",
              "d) Only large gifts are allowed"
            ]
          },
          {
            "number": "5",
            "question": "According to the text, what matters most about gifts?",
            "choices": [
              "a) Their material value",
              "b) Their size",
              "c) Thoughtfulness and connection",
              "d) The store where they were bought"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-why-european-workers-have-better-vacation-policies-than-amer",
    "title": "Why European Workers Have Better Vacation Policies Than Americans",
    "subtitle": "work culture",
    "category": "reading-exercises",
    "theme": "work culture",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about why European workers get more paid vacation than American workers.",
    "readingcompTitle": "Why European Workers Have Better Vacation Policies Than Americans",
    "topic": "work culture",
    "image": "/Images/holidays.webp",
    "vocabulary": [
      "paid time off (PTO) - vacation days you are paid for when you don't work",
      "directive - an official rule or order",
      "productive - able to produce or do a lot",
      "employment rate - the percentage of people who have a job",
      "career growth - progress in your job over time",
      "legal protection - laws that protect workers' rights",
      "holiday - a public day when people do not go to work",
      "stereotype - a fixed idea about a group of people",
      "fall behind - to not make progress as fast as others",
      "policy - an official set of rules"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>The United States is the only advanced economy in the world that does not guarantee paid time off. Although around 80% of American workers receive some paid vacation days from employers, almost half of them do not use all the days they are given. Many workers feel pressure not to take time off, fearing they will fall behind or harm their careers.</p>",
          "<h3>Vacation policies in Europe</h3>",
          "<p>In contrast, European countries offer much more paid vacation. The European Union Working Time Directive, created in the early 1990s, requires all EU countries to provide at least 20 paid vacation days per year. Some countries, like France, go even further. France gives workers a minimum of 30 paid vacation days, plus additional paid public holidays.</p>",
          "<h3>Vacation as part of the culture</h3>",
          "<p>For many Europeans, taking holidays is a normal and expected part of life. In France, for example, it is common for people to take most of August off. An American living in Paris said that people talk about their vacations all the time because rest is seen as essential, not optional.</p>",
          "<h3>More vacation, more productivity</h3>",
          "<p>Some Americans believe countries like France are less hardworking, but the data shows the opposite. The employment rate for prime-age workers (25 to 54 years old) is higher in France than in the U.S., and French workers produce more per hour. Experts argue that rest can improve productivity and support a stronger economy.</p>",
          "<h3>Why Americans avoid taking time off</h3>",
          "<p>Even when Americans do have paid vacation days, many are afraid to use them. According to a Pew Research Center survey, about half of U.S. workers worry they will fall behind if they take time off. Nearly one-fifth think it will hurt their chances of career growth, and 16% fear they could even lose their job. Without strong legal protections, some workers choose not to risk it.</p>",
          "<p>Understanding these differences helps explain why Americans often skip vacations while Europeans take full advantage of their time off.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main difference between the U.S. and Europe regarding paid vacation?",
            "choices": [
              "a) The U.S. gives more paid holidays than Europe",
              "b) Europe guarantees paid time off by law, but the U.S. does not",
              "c) Americans take more vacations than Europeans",
              "d) Europeans do not receive paid vacation days"
            ]
          },
          {
            "number": "2",
            "question": "How many minimum paid vacation days does the EU require?",
            "choices": [
              "a) 10 days",
              "b) 20 days",
              "c) 25 days",
              "d) 5 days"
            ]
          },
          {
            "number": "3",
            "question": "What is said about French vacation culture?",
            "choices": [
              "a) Most people work through August",
              "b) People rarely discuss vacations",
              "c) Taking vacations is an important part of life",
              "d) Workers usually avoid using their days off"
            ]
          },
          {
            "number": "4",
            "question": "What does the text say about productivity in France?",
            "choices": [
              "a) France has lower productivity than the U.S.",
              "b) French workers are more productive per hour",
              "c) French workers work longer hours",
              "d) Productivity is not measured in France"
            ]
          },
          {
            "number": "5",
            "question": "Why do many Americans not use all their vacation days?",
            "choices": [
              "a) They don’t enjoy traveling",
              "b) They prefer to save money",
              "c) They fear falling behind or hurting their careers",
              "d) They receive too many vacation days"
            ]
          },
          {
            "number": "6",
            "question": "What percentage of U.S. workers fear losing their job if they take time off?",
            "choices": [
              "a) 50%",
              "b) 30%",
              "c) 16%",
              "d) 2%"
            ]
          },
          {
            "number": "7",
            "question": "What does the text suggest about European vacation policies?",
            "choices": [
              "a) They reduce productivity",
              "b) They are the same as in the U.S.",
              "c) They support work-life balance and productivity",
              "d) They are unpopular with workers"
            ]
          },
          {
            "number": "8",
            "question": "What type of protection is missing for American workers?",
            "choices": [
              "a) Strong legal protection for taking vacation",
              "b) Health insurance",
              "c) Safety equipment",
              "d) Extra holiday pay"
            ]
          },
          {
            "number": "9",
            "question": "Which group is referred to as 'prime-age workers'?",
            "choices": [
              "a) 18–25",
              "b) 25–54",
              "c) 55–70",
              "d) 65 and older"
            ]
          },
          {
            "number": "10",
            "question": "What is one stereotype mentioned about France?",
            "choices": [
              "a) That French workers take too little vacation",
              "b) That the culture is lazy",
              "c) That people work too many hours",
              "d) That France has no public holidays"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-tipping-culture-how-it-started-who-it-hurts",
    "title": "Tipping Culture: How It Started & Who It Hurts",
    "subtitle": "society",
    "category": "reading-exercises",
    "theme": "society",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about the history of tipping culture and who it harms.",
    "readingcompTitle": "Tipping Culture: How It Started & Who It Hurts",
    "topic": "society",
    "image": "/Images/tipping.webp",
    "vocabulary": [
      "loophole - a way to avoid a rule or responsibility",
      "pressure - a feeling of being forced to do something",
      "wages - money workers earn",
      "service industry - jobs like restaurants, hotels, cafés, salons",
      "racism - unfair treatment of people based on race",
      "exploitation - treating someone unfairly for profit",
      "minimum wage - the lowest legal pay",
      "service charge - extra fee added by a business",
      "tipflation - rising expectations for tipping"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Tipping feels normal today, but it didn’t start in the U.S. It began in Europe as a way for rich people to show off their wealth. It wasn’t about fairness or good service — it was a status symbol. When wealthy Americans brought the habit home in the late 1800s, most people hated it. Newspapers called it un-American and unfair because workers should be paid by employers, not by customers.</p>",
          "<p>Businesses eventually realized tipping could save them money. Instead of paying proper wages, they paid workers very little and let customers cover the rest. This system became even more harmful after the Civil War. Many Black workers in hotels, railroads, and restaurants were given jobs with no real pay and were told to live on tips alone. This racist practice helped create today’s tipped minimum wage, which can still be as low as $2.13 an hour.</p>",
          "<p>By the 1900s, tipping was normal in restaurants and hotels because it kept prices low and profits high. For workers, it meant unstable income and the pressure to always smile and please customers. Today tipping culture is even stronger: tip screens appear everywhere, expectations keep rising, and many people feel guilty saying no. Meanwhile, employers avoid raising wages by depending on tips instead.</p>",
          "<p>In many countries, workers earn a real salary and tipping is optional. But in the U.S., millions of workers still rely on tips to survive. Some restaurants have tried no-tipping systems or automatic service charges so staff can earn steady pay, but these ideas often face backlash.</p>",
          "<p>The real solution is better wage laws. Tipping can be a nice gesture, but it shouldn’t be the only thing keeping workers afloat. The problem isn’t generous customers — it’s employers who still depend on an outdated system built on inequality.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Where did tipping originally come from?",
            "choices": [
              "a) The U.S.",
              "b) Medieval Europe",
              "c) South America",
              "d) Asia"
            ]
          },
          {
            "number": "2",
            "question": "Why did U.S. businesses support tipping?",
            "choices": [
              "a) It made work easier",
              "b) It allowed them to pay lower wages",
              "c) Customers demanded it",
              "d) It improved service"
            ]
          },
          {
            "number": "3",
            "question": "How did racism influence tipping culture?",
            "choices": [
              "a) Black workers earned more",
              "b) Black workers were paid nothing and relied on tips",
              "c) It ended tipping",
              "d) It created higher minimum wages"
            ]
          },
          {
            "number": "4",
            "question": "What is a modern problem with tipping?",
            "choices": [
              "a) It is banned in cafés",
              "b) Customers must tip online only",
              "c) Tip expectations keep increasing",
              "d) Only hotels use tips now"
            ]
          },
          {
            "number": "5",
            "question": "What does the text say is the real solution?",
            "choices": [
              "a) More tip screens",
              "b) Higher menu prices",
              "c) Better wage laws",
              "d) Bigger restaurants"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-popular-myths-about-sleep-debunked",
    "title": "Popular Myths About Sleep, Debunked",
    "subtitle": "sleep",
    "category": "reading-exercises",
    "theme": "sleep",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about common sleep myths and what science says about them.",
    "readingcompTitle": "Popular Myths About Sleep, Debunked",
    "topic": "sleep",
    "image": "/Images/sleepmyths.webp",
    "vocabulary": [
      "myth - a false belief or idea",
      "circadian rhythm - the body’s natural 24-hour cycle of sleep and wakefulness",
      "melatonin - a hormone that helps control sleep",
      "deprivation - lack or shortage of something needed",
      "diligent - showing care and effort",
      "endorphins - chemicals in the body that make you feel good",
      "adrenaline - a hormone that increases energy and alertness",
      "insomnia - difficulty falling or staying asleep",
      "immune system - the body’s defense against illness",
      "recovery - returning to a normal state after difficulty"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Sleep is essential for our health — it helps our brains function, supports the heart, and strengthens the immune system. Yet, many people don’t get enough of it, partly because of common myths. Sleep scientist Rebecca Robbins from Harvard Medical School has studied these misconceptions and explained the truth behind them. Here are six of the most common sleep myths, debunked by science.</p>",
          "<h3>1. It doesn’t matter what time of day you sleep</h3>",
          "<p>This is false. Our bodies follow a natural clock called the circadian rhythm, which is influenced by sunlight. Morning light tells our brains to stop producing melatonin, helping us wake up, while darkness signals it’s time to sleep. People who work night shifts often struggle because their rhythms are disrupted. To reduce the impact, experts recommend avoiding sunlight after a night shift and using blackout curtains for better rest.</p>",
          "<h3>2. One night of poor sleep will have lasting effects</h3>",
          "<p>Missing one night’s sleep isn’t the end of the world. It can cause short-term effects like tiredness and difficulty concentrating, but the body recovers after a few nights of normal rest. The key is to return to your usual sleep schedule as soon as possible instead of stressing about it.</p>",
          "<h3>3. Falling asleep instantly means you’re a good sleeper</h3>",
          "<p>Actually, the opposite may be true. It normally takes about 15 to 20 minutes to fall asleep. If you fall asleep the moment your head hits the pillow, it might mean you’re severely sleep-deprived. Just like someone who eats too fast after starving, falling asleep too quickly can signal an unhealthy pattern.</p>",
          "<h3>4. You can survive on less than five hours of sleep</h3>",
          "<p>Some people claim they function perfectly on just a few hours, but science disagrees. Adults generally need seven to nine hours each night for good physical and mental health. Chronic sleep deprivation increases the risk of obesity, diabetes, high blood pressure, and even heart disease. People who sleep less also make more mistakes at work and feel less focused during the day.</p>",
          "<h3>5. Watching TV helps you relax before bed</h3>",
          "<p>While watching your favorite show might feel relaxing, it can actually harm your sleep. Bright screens reduce melatonin production, making it harder to fall asleep. Emotional or stressful content can raise cortisol levels and make you more alert. If you enjoy a light comedy before bed, that’s fine — but it’s better to keep screens out of the bedroom and maintain a cool, dark environment.</p>",
          "<h3>6. Exercising before bed will stop you from sleeping</h3>",
          "<p>For years, people were told to avoid exercise at night, but research now shows that evening workouts can be perfectly fine. Exercise helps reduce stress, which is a major cause of insomnia. It also promotes deeper sleep. Unless you’re doing an intense workout right before bed, physical activity in the evening is not harmful — in fact, it may even help you sleep better.</p>",
          "<p>In short, healthy sleep isn’t about following myths or strict rules. It’s about finding a balanced routine that fits your life. Aim for seven to nine hours, stay consistent with your schedule, and avoid screens and stress before bedtime — your body and mind will thank you.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main purpose of the text?",
            "choices": [
              "a) To describe why people dislike sleeping",
              "b) To explain and correct common false beliefs about sleep",
              "c) To show why people should avoid exercise at night",
              "d) To promote sleep medication"
            ]
          },
          {
            "number": "2",
            "question": "According to the text, what controls our natural sleep cycle?",
            "choices": [
              "a) The amount of food we eat",
              "b) The circadian rhythm influenced by sunlight",
              "c) Our work schedule",
              "d) The temperature in our bedroom"
            ]
          },
          {
            "number": "3",
            "question": "What should night shift workers do to protect their sleep?",
            "choices": [
              "a) Sleep in bright rooms",
              "b) Get sunlight right after work",
              "c) Use blackout curtains and avoid sunlight after shifts",
              "d) Take caffeine before sleeping"
            ]
          },
          {
            "number": "4",
            "question": "What does falling asleep too quickly usually indicate?",
            "choices": [
              "a) Good sleep hygiene",
              "b) A well-rested body",
              "c) Chronic sleep deprivation",
              "d) A healthy bedtime routine"
            ]
          },
          {
            "number": "5",
            "question": "How much sleep do most adults need per night?",
            "choices": [
              "a) About five hours",
              "b) Between seven and nine hours",
              "c) More than ten hours",
              "d) It depends on the person’s diet"
            ]
          },
          {
            "number": "6",
            "question": "What effect can watching TV before bed have?",
            "choices": [
              "a) It increases melatonin levels",
              "b) It relaxes the brain for deeper sleep",
              "c) It decreases melatonin and raises stress hormones",
              "d) It improves focus and memory"
            ]
          },
          {
            "number": "7",
            "question": "What do recent studies suggest about exercising in the evening?",
            "choices": [
              "a) It always causes poor sleep",
              "b) It can help reduce stress and improve sleep quality",
              "c) It should be avoided completely",
              "d) It makes people too tired to fall asleep"
            ]
          },
          {
            "number": "8",
            "question": "What is the overall message of the article?",
            "choices": [
              "a) Sleep myths can harm our health, but science offers better solutions",
              "b) Sleeping less helps improve productivity",
              "c) People should stop exercising and watching TV",
              "d) Sleep problems cannot be solved without medication"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-the-moral-choices-of-self-driving-cars",
    "title": "The Moral Choices of Self-Driving Cars",
    "subtitle": "ethics",
    "category": "reading-exercises",
    "theme": "ethics",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about the ethical dilemmas self-driving cars may face.",
    "readingcompTitle": "The Moral Choices of Self-Driving Cars",
    "topic": "ethics",
    "image": "/Images/selfdriving.webp",
    "vocabulary": [
      "swerve - to turn suddenly to avoid something",
      "prioritize - to give more importance to something",
      "sacrificing - giving up something valuable for another purpose",
      "instinctive - based on feeling rather than careful thought",
      "policymakers - people who make rules or laws for society",
      "penalized - punished unfairly or given a disadvantage",
      "dilemma - a difficult choice between two options",
      "targeting system - a system that selects or identifies something or someone",
      "random - not planned, decided by chance",
      "navigate - to move through or find a path in a difficult situation"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Imagine a time in the near future when you are driving on the highway in a self-driving car. You are surrounded by other cars when suddenly, a large, heavy object falls from a truck in front of you. Your car cannot stop in time, so it must make a decision: go straight and hit the object, <b>swerve</b> left into an SUV, or swerve right into a motorcycle.</p>",
          "<p>Should the car <b>prioritize</b> your safety by hitting the motorcycle? Should it avoid swerving, even if that means crashing into the object and <b>sacrificing</b> your life? Or should it hit the SUV, which is designed to protect passengers well? What is the right choice?</p>",
          "<p>If a person were driving, their reaction would be natural and <b>instinctive</b>. But if a programmer tells the car how to react in this situation, it seems like a planned decision rather than an accident.</p>",
          "<p>Self-driving cars are expected to reduce accidents by removing human mistakes. They may also help with traffic, reduce pollution, and make driving less stressful. However, accidents will still happen. When they do, the outcome may have already been decided by programmers or <b>policymakers</b>.</p>",
          "<p>For example, imagine the same situation, but now there are two motorcyclists: one wearing a helmet and one without. Which one should the car hit? If it crashes into the one with a helmet because they are more likely to survive, is that fair? It seems like the responsible rider is being <b>penalized</b>. But if the car avoids the helmeted rider and crashes into the one without a helmet, it is no longer just minimizing harm—it is making a moral <b>dilemma</b>.</p>",
          "<p>In both examples, the car’s programming acts like a <b>targeting system</b>. It chooses who will suffer, even though the victims did nothing wrong.</p>",
          "<p>New technology creates many difficult ethical questions.</p>",
          "<p>For instance, if you had to buy a car, would you choose one that always tries to save the most lives, or one that always protects you first? What if the car could analyze people inside the vehicles and make decisions based on their lives? Would that be fair? Or would a <b>random</b> decision be better? And who should make these rules—programmers, companies, or governments?</p>",
          "<p>Real life may not play out exactly like these examples, but that is not the point. These thought experiments help us understand ethical problems, just as science experiments help us understand the physical world. Thinking about these issues now will help us <b>navigate</b> the future of technology and make better decisions along the way.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main problem described in the text?",
            "choices": [
              "a) How to design faster cars",
              "b) How self-driving cars should make moral decisions in dangerous situations",
              "c) How to reduce traffic jams",
              "d) How to stop people from speeding"
            ]
          },
          {
            "number": "2",
            "question": "Why is a human driver's reaction different from a self-driving car's reaction?",
            "choices": [
              "a) Humans follow strict rules programmed in advance",
              "b) Humans act instinctively, while cars act based on programming",
              "c) Humans always make better choices than machines",
              "d) Cars can’t detect moving objects"
            ]
          },
          {
            "number": "3",
            "question": "Who might decide how a car reacts in an unavoidable accident?",
            "choices": [
              "a) Policymakers and programmers",
              "b) The passengers",
              "c) The victims",
              "d) The car company’s customers"
            ]
          },
          {
            "number": "4",
            "question": "In the helmet example, what ethical issue does the author raise?",
            "choices": [
              "a) Whether technology can detect helmets accurately",
              "b) Whether responsible behavior should lead to punishment",
              "c) Whether motorcyclists should wear helmets",
              "d) Whether the car can predict survival rates"
            ]
          },
          {
            "number": "5",
            "question": "What is the main purpose of this text?",
            "choices": [
              "a) To explain how self-driving cars work mechanically",
              "b) To encourage people to buy self-driving cars",
              "c) To make readers think about the ethical decisions technology may have to make",
              "d) To argue that self-driving cars should be banned"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-luck-loss-and-the-rise-of-online-gambling",
    "title": "Luck, Loss, and the Rise of Online Gambling",
    "subtitle": "gambling",
    "category": "reading-exercises",
    "theme": "gambling",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about the rise of online gambling and its risks.",
    "readingcompTitle": "Luck, Loss, and the Rise of Online Gambling",
    "topic": "gambling",
    "image": "/Images/gambling.webp",
    "vocabulary": [
      "wager - to bet money on an event or game",
      "anonymity - being unknown or unidentifiable",
      "addiction - the inability to stop doing something harmful",
      "odds - the chances of winning or losing a bet",
      "dopamine - a brain chemical linked to pleasure and reward",
      "fallacy - a mistaken belief or idea",
      "stigma - negative feelings or shame about something",
      "regulation - government control or rule",
      "bias - a tendency to think in a particular way",
      "interface - the design or layout of a website or app"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Gambling has changed dramatically in recent years. What once happened in casinos and betting shops now takes place on smartphones and computers. People can now wager on sports, politics, or even the weather. The Covid-19 lockdown accelerated this move online, giving players easy access and anonymity. In 2024, Europe’s regulated gambling market was worth about €137 billion, with online gambling making up around 40% of it.</p>",
          "<p>Online gambling’s popularity is partly due to its convenience and constant availability. Studies show that moving even 10% of gambling online can increase the risk of addiction. Many sites use psychological tricks to keep users playing—celebrating ‘near misses’, offering free bets, or making deposits easy but withdrawals difficult. These features stimulate dopamine in the brain and make players feel they are in control when they are not.</p>",
          "<p>Advertisements, especially in sports, have helped normalize gambling and reduce its stigma. But experts warn that this growing accessibility can lead to financial and mental health problems, especially among young people. Setting limits, taking breaks, and stronger regulations can help ensure that gambling stays entertainment—not a dangerous habit.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main reason gambling has become more popular recently?",
            "choices": [
              "a) More casinos have opened",
              "b) Online access has made gambling easier and more private",
              "c) People have more free time",
              "d) Governments encourage gambling"
            ]
          },
          {
            "number": "2",
            "question": "What happened to gambling during the Covid-19 lockdown?",
            "choices": [
              "a) It decreased because casinos closed",
              "b) It stopped completely",
              "c) It moved mostly online",
              "d) It became illegal in Europe"
            ]
          },
          {
            "number": "3",
            "question": "What psychological effect makes people continue betting after losing?",
            "choices": [
              "a) Fear of missing out",
              "b) Sunk cost fallacy",
              "c) Hyperbolic discounting",
              "d) Optimism bias"
            ]
          },
          {
            "number": "4",
            "question": "How do gambling companies make users feel more in control?",
            "choices": [
              "a) By showing real odds",
              "b) By using designs that celebrate near-misses or wins",
              "c) By limiting how often users can play",
              "d) By offering fewer games"
            ]
          },
          {
            "number": "5",
            "question": "According to the text, how can gambling be made safer?",
            "choices": [
              "a) By banning all ads",
              "b) By letting users set limits and using better regulation",
              "c) By giving more free bets",
              "d) By increasing prizes"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-top-10-innovations-in-air-traffic-control",
    "title": "Top 10 Innovations in Air Traffic Control",
    "subtitle": "aviation technology and air traffic management",
    "category": "reading-exercises",
    "theme": "aviation technology and air traffic management",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about ten new technologies changing air traffic control.",
    "readingcompTitle": "Top 10 Innovations in Air Traffic Control",
    "topic": "aviation technology and air traffic management",
    "image": "/Images/air_traffic_control.webp",
    "vocabulary": [
      "evolving - developing gradually, especially from a simple to a more complex form",
      "orchestrate - to arrange or direct elements to produce a desired effect, like a conductor with music",
      "choreograph - to plan and organize carefully, often used metaphorically for complex coordination",
      "surveillance - close observation, especially of people or objects in order to gather information",
      "automation - the use of technology to perform tasks with minimal human intervention",
      "optimize - to make as effective, perfect, or useful as possible",
      "predictive - relating to the ability to foresee or estimate future events based on data",
      "integration - the process of combining parts to make a unified whole",
      "simulation - imitation of a real-world process or system over time, often for training or testing",
      "connectivity - the state or quality of being connected, especially in networks or systems"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>As the skies get busier, air traffic control (ATC) is evolving with cutting-edge technology to ensure smoother, safer flights. Controllers are now like DJs, mixing data and systems to orchestrate the skies efficiently.</p>",
          "<p>1. <strong>NextGen Air Traffic Control Systems</strong>: The FAA’s NextGen system introduces satellite-based tracking and automated routing, transforming air traffic management from maps to GPS-level precision.</p>",
          "<p>2. <strong>Automatic Dependent Surveillance-Broadcast (ADS-B)</strong>: ADS-B broadcasts real-time aircraft data, giving controllers and pilots a clear view of each flight’s position, even in remote areas.</p>",
          "<p>3. <strong>Integrated Flight Management Systems (FMS)</strong>: FMS optimizes flight plans and makes real-time adjustments, acting like a flight planner that never sleeps.</p>",
          "<p>4. <strong>Data Link Communications</strong>: Digital communication technologies, such as CPDLC, reduce radio errors and ensure clear information exchange between pilots and controllers.</p>",
          "<p>5. <strong>Surface Management Systems</strong>: These systems choreograph airport ground operations using radar and GPS, keeping aircraft moving smoothly on runways and taxiways.</p>",
          "<p>6. <strong>Enhanced Traffic Flow Management Systems (TFMS)</strong>: TFMS uses real-time data and predictive analytics to prevent air traffic delays, acting like a traffic cop for the skies.</p>",
          "<p>7. <strong>Artificial Intelligence (AI) and Machine Learning</strong>: AI assists in predicting traffic patterns, optimizing routes, and handling routine tasks with high accuracy.</p>",
          "<p>8. <strong>Remote Towers</strong>: Remote towers allow controllers to manage smaller airports from afar using cameras and sensors, providing a bird’s-eye view of operations.</p>",
          "<p>9. <strong>Virtual ATC Systems</strong>: These systems provide realistic simulations for training, replicating complex traffic scenarios for controllers.</p>",
          "<p>10. <strong>Internet of Things (IoT) Integration</strong>: IoT connects devices and sensors to optimize air traffic and airport operations in real-time.</p>",
          "<p>As aviation advances, these innovations are shaping safer, more efficient, and connected skies. NextGen, ADS-B, AI, and IoT are transforming air traffic control behind the scenes, ensuring smoother journeys for all travelers.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Which technology broadcasts real-time aircraft data for controllers and pilots?",
            "choices": [
              "a) NextGen",
              "b) ADS-B",
              "c) FMS",
              "d) IoT"
            ]
          },
          {
            "number": "2",
            "question": "What is the main purpose of Surface Management Systems?",
            "choices": [
              "a) Predicting traffic patterns",
              "b) Managing airport ground operations efficiently",
              "c) Training air traffic controllers",
              "d) Digital communication with pilots"
            ]
          },
          {
            "number": "3",
            "question": "How does AI assist in air traffic control?",
            "choices": [
              "a) By physically guiding aircraft on the runway",
              "b) Predicting traffic patterns and optimizing routes",
              "c) Replacing human controllers entirely",
              "d) Broadcasting flight data"
            ]
          },
          {
            "number": "4",
            "question": "Which system allows controllers to manage remote airports without being physically present?",
            "choices": [
              "a) TFMS",
              "b) Remote Towers",
              "c) Virtual ATC Systems",
              "d) FMS"
            ]
          },
          {
            "number": "5",
            "question": "Why is IoT important in modern air traffic control?",
            "choices": [
              "a) It provides real-time data from connected devices to improve efficiency",
              "b) It replaces radar systems",
              "c) It trains air traffic controllers",
              "d) It broadcasts aircraft positions manually"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-the-uncertain-future-of-handwriting",
    "title": "The Uncertain Future of Handwriting",
    "subtitle": "education / technology",
    "category": "reading-exercises",
    "theme": "education / technology",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about how handwriting is declining in the digital age.",
    "readingcompTitle": "The Uncertain Future of Handwriting",
    "topic": "education / technology",
    "image": "/Images/handwriting.webp",
    "vocabulary": [
      "handwriting - writing done with a pen or pencil",
      "cursive - joined-up, flowing handwriting",
      "automaticity - performing a task without conscious thought",
      "decline - decrease or reduction",
      "cognitive - related to thinking or learning",
      "voice recognition software (VRS) - technology that converts spoken words to text",
      "digital - using computers or electronic devices",
      "motor skills - coordination of muscles to perform tasks",
      "aesthetes - people who appreciate beauty or art",
      "manuscript - a handwritten document"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Computers and tablets are changing how we write. Typing dominates today, and children often interact with screens from birth. Handwriting, once essential, is now declining, with some countries reducing cursive lessons in schools in favor of typing skills.</p>",
          "<p>Anne Trubek, author on the history of handwriting, argues that handwriting teaches automaticity, freeing mental bandwidth to think about ideas while writing. She believes keyboards provide faster automaticity for students, allowing them more time to focus on word choice and sentence structure.</p>",
          "<p>Critics worry handwriting’s decline may result in a loss of history and personal touch. Trubek counters that historic manuscripts are often unreadable and that care can be shown in other ways. Similarly, Finland emphasizes typing for modern life, and research shows handwriting develops motor skills, visual recognition, and memory retention.</p>",
          "<p>Voice recognition software, such as Siri or Google Docs’ voice typing, may supplement writing, but handwriting remains valuable for a personal, tactile experience. Experts suggest cursive could survive as an art form or hobby, even if daily usage diminishes. Handwriting continues to offer cognitive, artistic, and personal benefits in a digital age.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Why is handwriting declining in schools?",
            "choices": [
              "a) Typing is prioritized for modern life",
              "b) Students refuse to learn cursive",
              "c) Computers prevent writing",
              "d) Handwriting is illegal"
            ]
          },
          {
            "number": "2",
            "question": "What does 'automaticity' in handwriting mean?",
            "choices": [
              "a) Doing a task without conscious thought",
              "b) Writing as quickly as possible",
              "c) Learning typing before writing",
              "d) Copying text from books"
            ]
          },
          {
            "number": "3",
            "question": "What are cognitive benefits of handwriting?",
            "choices": [
              "a) Improving motor skills, memory, and thinking",
              "b) Typing faster on a keyboard",
              "c) Listening skills",
              "d) Only artistic ability"
            ]
          },
          {
            "number": "4",
            "question": "How might voice recognition software affect writing?",
            "choices": [
              "a) It may supplement but not replace handwriting",
              "b) It eliminates the need for typing",
              "c) It makes handwriting obsolete immediately",
              "d) It worsens reading skills"
            ]
          },
          {
            "number": "5",
            "question": "Why might cursive survive in the future?",
            "choices": [
              "a) As an art form or hobby",
              "b) Because it is required in schools",
              "c) To replace keyboards",
              "d) Due to legal mandates"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-the-10-biggest-music-myths-true-or-false",
    "title": "The 10 Biggest Music Myths: True or False?",
    "subtitle": "music industry",
    "category": "reading-exercises",
    "theme": "music industry",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about ten common myths about the music industry.",
    "readingcompTitle": "The 10 Biggest Music Myths: True or False?",
    "topic": "music industry",
    "image": "/Images/musicmyths.webp",
    "vocabulary": [
      "myth - a false belief or idea",
      "talent - a natural skill or ability",
      "hit - a very successful song",
      "amplifier (amp) - a device that makes music louder",
      "conspiracy - a secret plan or theory",
      "streaming - listening or watching online without downloading",
      "algorithm - a computer process that makes automatic decisions",
      "fan base - a group of loyal followers or supporters",
      "dead - no longer active or successful",
      "fake - not real or true"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>There are many myths about the music industry. Some people believe that only young, naturally talented artists can be successful, or that one hit song is enough for a lifetime. Others think that the music business is dying or that social media followers guarantee fame. Let’s look at ten popular myths and the truth behind them.</p>",
          "<h3>1. Age makes or breaks a music career</h3>",
          "<p>It doesn’t matter if you’re 14 or 91 – success depends on your fans, not your age. Stars like Grace VanderWaal started young, while India’s Lata Mangeshkar sang until her 90s. Age is not a rule for success.</p>",
          "<h3>2. You have to have a natural talent</h3>",
          "<p>Talent helps, but hard work matters more. Many artists become successful because they practice every day and learn constantly. Relying only on talent is risky – effort is what builds a real career.</p>",
          "<h3>3. One hit song is enough</h3>",
          "<p>One hit might make you famous for a short time, but it rarely creates a long career. Music trends change quickly, and artists need to plan their next steps to stay successful.</p>",
          "<h3>4. Big amps mean a big sound</h3>",
          "<p>At concerts, the large walls of amplifiers are often fake. The real sound comes from smaller amps and speakers hanging above the stage. Big walls look exciting but don’t make the sound louder.</p>",
          "<h3>5. The worst band in the world</h3>",
          "<p>Some websites list bands like Nickelback or even Pink Floyd as “the worst.” But this shows that online algorithms can’t really understand music taste. It’s all opinion – and fans will always disagree!</p>",
          "<h3>6. Who played the solos?</h3>",
          "<p>Many fans think George Harrison played the guitar solos in “While My Guitar Gently Weeps.” In fact, it was Eric Clapton, who recorded them using Harrison’s guitar. Even Beatles fans are surprised by this fact!</p>",
          "<h3>7. Elvis is alive</h3>",
          "<p>Some fans believe Elvis Presley’s death in 1977 was faked and that he still lives secretly. The truth: Elvis died of health problems at home. Stories about wax figures and air-conditioned coffins are pure fiction.</p>",
          "<h3>8. The music industry is dead</h3>",
          "<p>Album sales are lower, but the music industry is very active thanks to streaming. In fact, more people listen to more music than ever before. Even Mozart’s music became a best-seller again in 2016!</p>",
          "<h3>9. Likes equal ticket sales</h3>",
          "<p>Having thousands of social media followers doesn’t mean people will buy tickets. Real success comes from loyal fans who support artists in the long term, not just from likes online.</p>",
          "<h3>10. Streaming platforms are in the music business</h3>",
          "<p>Spotify, YouTube, and TikTok may look like music companies, but they are actually data businesses. They collect information about users to sell advertising. Artists earn very little from streams, while the platforms earn money from data.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main idea of the text?",
            "choices": [
              "a) Music myths are always true",
              "b) The music industry is full of false ideas and surprises",
              "c) Only young artists can be successful",
              "d) Streaming has ended music careers"
            ]
          },
          {
            "number": "2",
            "question": "According to the text, what really matters more than talent?",
            "choices": [
              "a) Expensive instruments",
              "b) Hard work and consistency",
              "c) Being naturally gifted",
              "d) Luck"
            ]
          },
          {
            "number": "3",
            "question": "Why is one hit song not enough for a long career?",
            "choices": [
              "a) Because fans don’t like famous songs",
              "b) Because the music industry is too small",
              "c) Because trends change very quickly",
              "d) Because hits are illegal now"
            ]
          },
          {
            "number": "4",
            "question": "What is surprising about big amp walls at rock concerts?",
            "choices": [
              "a) They are often fake and only for show",
              "b) They make the band sound terrible",
              "c) They are dangerous for the audience",
              "d) They belong to the lighting crew"
            ]
          },
          {
            "number": "5",
            "question": "Who really played the solo in 'While My Guitar Gently Weeps'?",
            "choices": [
              "a) Paul McCartney",
              "b) George Harrison",
              "c) Eric Clapton",
              "d) John Lennon"
            ]
          },
          {
            "number": "6",
            "question": "What do conspiracy theories about Elvis claim?",
            "choices": [
              "a) He lives secretly and his coffin was fake",
              "b) He moved to another country",
              "c) He never became famous",
              "d) He plays concerts online"
            ]
          },
          {
            "number": "7",
            "question": "How has streaming changed the music industry?",
            "choices": [
              "a) It killed live performances",
              "b) It made music more available and popular",
              "c) It stopped people from listening to old music",
              "d) It reduced all artist earnings equally"
            ]
          },
          {
            "number": "8",
            "question": "Why don’t social media likes always mean real success?",
            "choices": [
              "a) Because likes are more important than fans",
              "b) Because many followers can be fake",
              "c) Because artists don’t need fans anymore",
              "d) Because likes increase ticket prices"
            ]
          },
          {
            "number": "9",
            "question": "What kind of business are streaming platforms actually in?",
            "choices": [
              "a) Data and advertising",
              "b) Concert promotion",
              "c) CD production",
              "d) Music teaching"
            ]
          },
          {
            "number": "10",
            "question": "What does the article suggest about the music industry overall?",
            "choices": [
              "a) It is dead and finished",
              "b) It is alive and full of change",
              "c) It only works for young people",
              "d) It depends only on luck"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-the-strongman-prime-minister-losing-power",
    "title": "The 'Strongman' Prime Minister Losing Power",
    "subtitle": "elections",
    "category": "reading-exercises",
    "theme": "elections",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about Viktor Orbán's weakening hold on power in Hungary.",
    "readingcompTitle": "The 'Strongman' Prime Minister Losing Power",
    "topic": "elections",
    "image": "/Images/orban.webp",
    "vocabulary": [
      "illiberal democracy - a system with elections but limited freedoms",
      "corruption - dishonest or illegal behavior, especially by officials",
      "nepotism - giving jobs or advantages to friends or family members",
      "opposition - people or parties who disagree with and compete against the government",
      "poll - a survey measuring public opinion",
      "conservative - preferring traditional values and limited change",
      "liberal - supporting individual freedoms and social progress",
      "economy - the system of money, trade, and jobs in a country",
      "authoritarian - controlling power very strictly, allowing little freedom",
      "democratic reform - changes that make a country more free and fair"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Hungarian Prime Minister Viktor Orbán has been one of Europe’s most powerful leaders for almost 20 years. He calls his system an <strong>“illiberal democracy”</strong> — a mix of democracy and strong government control. Orbán promotes conservative values and has often attacked liberal ideas.</p>",
          "<p>Many world leaders admire him, including Donald Trump, who called him “a smart and tough man.” But now Orbán’s power at home seems to be weakening, as new opposition voices appear and public support decreases.</p>",
          "<p>The main challenge comes from <strong>Peter Magyar</strong>, a 44-year-old former ally of Orbán. In 2024, he became famous for criticizing corruption and <strong>nepotism</strong> in Orbán’s government during a popular online interview. His new party, <strong>Tisza</strong>, now leads in most polls.</p>",
          "<p>Many Hungarians are frustrated with poor public services — hospitals, schools, and railways — and they blame the government. Magyar promises to rebuild the country “brick by brick.”</p>",
          "<p>Orbán has ruled Hungary for <strong>19 of the last 35 years</strong>. He often wins by focusing on what his voters fear, such as migration, “woke” culture, or pressure from the EU. He also changed the election system to make it easier for his party, <strong>Fidesz</strong>, to win large majorities in Parliament.</p>",
          "<p>He recently tried to ban the <strong>Budapest Pride</strong> march, but over 100,000 people joined in protest, showing that resistance is growing. At the same time, Hungary’s <strong>economy</strong> is struggling, prices are rising, and wages remain low.</p>",
          "<p>Orbán has close relations with <strong>Vladimir Putin</strong> and says this helps Hungary get cheap gas and oil. But many citizens and European leaders criticize these ties, especially during the war in Ukraine.</p>",
          "<p>The next election will take place in <strong>April 2026</strong>. For the first time in years, Orbán’s party may lose. Both Fidesz and Tisza have around two million supporters, and undecided voters will decide the result.</p>",
          "<p>If Peter Magyar wins, Hungary could see major <strong>democratic reforms</strong> and the end of Orbán’s long rule. The country stands at a crossroads — one path leads to more democracy, the other to more <strong>authoritarian</strong> control.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What type of political system does Viktor Orbán promote?",
            "choices": [
              "a) Liberal democracy",
              "b) Illiberal democracy",
              "c) Communism",
              "d) Monarchy"
            ]
          },
          {
            "number": "2",
            "question": "Who is the new political challenger mentioned in the text?",
            "choices": [
              "a) Donald Trump",
              "b) J.D. Vance",
              "c) Peter Magyar",
              "d) Katalin Novak"
            ]
          },
          {
            "number": "3",
            "question": "Why are many Hungarians unhappy with Orbán’s government?",
            "choices": [
              "a) Because of poor public services and corruption",
              "b) Because Hungary joined the EU",
              "c) Because of higher taxes on foreign companies",
              "d) Because there are too many elections"
            ]
          },
          {
            "number": "4",
            "question": "What event showed that opposition to Orbán is growing?",
            "choices": [
              "a) The cancellation of a government meeting",
              "b) The large Budapest Pride march despite a ban",
              "c) The fall of oil prices",
              "d) The new trade deal with Russia"
            ]
          },
          {
            "number": "5",
            "question": "What could happen if Peter Magyar’s Tisza party wins the 2026 election?",
            "choices": [
              "a) Hungary could become more authoritarian",
              "b) Hungary could return to communism",
              "c) Hungary could see more democratic reforms",
              "d) Hungary could leave the European Union"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-the-four-forms-of-value",
    "title": "The Four Forms of Value",
    "subtitle": "value",
    "category": "reading-exercises",
    "theme": "value",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about the four main types of value that make things valuable.",
    "readingcompTitle": "The Four Forms of Value",
    "topic": "value",
    "image": "/Images/value.webp",
    "vocabulary": [
      "usable value - the practical or functional usefulness of something",
      "aesthetic value - the beauty or artistic appeal of an object",
      "rare value - worth created by scarcity or limited supply",
      "emotional value - personal or sentimental importance attached to something",
      "intrinsic - belonging naturally; essential or inherent",
      "practicality - the quality of being sensible and useful",
      "sentimental - related to emotions or personal memories",
      "brand - a company or product identity that influences perception",
      "supply and demand - the relationship between how much is available and how much people want",
      "status - a person’s social or professional standing or importance"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>What makes something valuable? Why are diamonds more valuable than the rocks in my yard? There are four main things that affect the value of something: usable value, rare value, aesthetic value, and emotional value.</p>",
          "<p><strong>Usable value</strong> refers to how practical or functional something is. Does it fill a physical need? For example, a can opener has clear usable value because it serves a practical purpose. Engineers and inventors increase usable value by making things work better, faster, or more efficiently.</p>",
          "<p><strong>Aesthetic value</strong> comes from an object’s beauty or design. This is what art is all about, but it can also apply to everyday things like cups, chairs, or cars. Artists and designers increase aesthetic value by improving an object’s visual appeal and style.</p>",
          "<p><strong>Rare value</strong> is created by scarcity. If something is rare, people often perceive it as more valuable, even if it isn’t actually better. Gemstones are a classic example. Investors and economists can increase rare value by limiting supply or controlling demand.</p>",
          "<p><strong>Emotional value</strong> comes from personal attachment. A stuffed toy from your childhood or a wedding ring might be worth little to others but priceless to you. Brands also use emotional value to make products more desirable. Marketers and advertisers do this by connecting emotions, memories, or identity to their products.</p>",
          "<p>Most things include more than one type of value. The more areas they cover, the more valuable they become. For instance, sunsets are beautiful, but because they are common and serve no practical function, their value is limited. Diamonds, on the other hand, combine rarity, beauty, and emotional meaning. They symbolize love, wealth, and status — which gives them their high price.</p>",
          "<p>High-end art also combines several types of value. It is rare, often beautiful, and carries emotional meaning as a symbol of creativity or prestige. To make something more valuable, you can improve it in one or more of these four areas — function, beauty, rarity, or emotion.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What are the four forms of value described in the text?",
            "choices": [
              "a) Functional, financial, artistic, and symbolic value",
              "b) Usable, aesthetic, rare, and emotional value",
              "c) Practical, spiritual, social, and economic value",
              "d) Common, rare, artistic, and traditional value"
            ]
          },
          {
            "number": "2",
            "question": "How can engineers increase the usable value of a product?",
            "choices": [
              "a) By making it more efficient and functional",
              "b) By making it more beautiful",
              "c) By limiting its availability",
              "d) By attaching emotions to it through marketing"
            ]
          },
          {
            "number": "3",
            "question": "What is the main reason diamonds are valuable, according to the text?",
            "choices": [
              "a) They are functional and useful in daily life",
              "b) They have beauty, rarity, and emotional meaning",
              "c) They are made from expensive materials",
              "d) They are commonly found and easy to replace"
            ]
          },
          {
            "number": "4",
            "question": "Why does the text say sunsets have little value despite being beautiful?",
            "choices": [
              "a) They don’t inspire emotion in people",
              "b) They are common and serve no practical purpose",
              "c) They are difficult to observe clearly",
              "d) They are often forgotten because they are ugly"
            ]
          },
          {
            "number": "5",
            "question": "What is one way marketers can increase the emotional value of a product?",
            "choices": [
              "a) By improving its design and color",
              "b) By connecting it to people’s feelings or memories",
              "c) By limiting the number of products available",
              "d) By reducing its price"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-enron-and-the-california-blackouts",
    "title": "Enron and the California Blackouts",
    "subtitle": "business ethics and energy markets",
    "category": "reading-exercises",
    "theme": "business ethics and energy markets",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about how Enron manipulated the California energy market.",
    "readingcompTitle": "Enron and the California Blackouts",
    "topic": "business ethics and energy markets",
    "image": "/Images/enron.webp",
    "vocabulary": [
      "deregulation - the removal of government controls over an industry to encourage competition",
      "loophole - a gap or ambiguity in a rule or law that allows it to be exploited",
      "manipulation - controlling or influencing something unfairly or dishonestly",
      "wholesale market - a system where electricity is bought and sold in bulk before reaching consumers",
      "withholding supply - intentionally reducing available goods or energy to drive up prices",
      "congestion - an overload on a power grid or network",
      "futures contract - a financial agreement to buy or sell a commodity at a set price in the future",
      "rolling blackout - a temporary and planned power outage to prevent a total system failure",
      "accounting fraud - falsifying financial records to hide losses or inflate profits",
      "settlement - a legal agreement that resolves a dispute, often involving compensation"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>In the late 1990s, California changed how electricity was managed. The state introduced a deregulated market, forcing utilities to sell their power plants and buy electricity on an open market. Prices for consumers were frozen, but wholesale prices could fluctuate freely. This system aimed to increase competition, but it also created opportunities for manipulation.</p>",
          "<p>Enron, a Texas-based energy company, became one of the most aggressive players in this new market. Its traders developed schemes with code names like <em>Death Star</em>, <em>Ricochet</em>, and <em>Fat Boy</em>. These tactics involved creating artificial shortages, rerouting electricity, or pretending to relieve grid congestion — all designed to inflate prices without producing more energy.</p>",
          "<p>In one scheme, Enron exported electricity out of California and then imported it back at a higher price, making it appear as if the state had to buy expensive out-of-state power. In another, it overstated demand and then sold the “extra” electricity back to the grid at inflated rates. By withholding supply and exploiting market rules, Enron caused prices to skyrocket and power shortages to worsen.</p>",
          "<p>As a result, California suffered rolling blackouts in 2000 and 2001. Utilities lost billions, and consumers faced enormous bills. Investigations later revealed that Enron and other companies had intentionally made the crisis worse for profit. Some traders were recorded laughing about shutting down power plants and “stealing from Grandma Millie.”</p>",
          "<p>When Enron’s broader accounting frauds were uncovered, the company collapsed in 2001, wiping out investors and employees. Several executives were prosecuted, and California eventually won settlements for damages. The crisis became a symbol of how deregulation, without effective oversight, can allow corporations to exploit public systems for private gain.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Do you think deregulation was a good idea in this case, or did it make the energy market too easy to manipulate?",
            "choices": [
              "a) Deregulation was necessary to create competition",
              "b) It was a good idea but poorly managed",
              "c) It made the market too easy to manipulate",
              "d) It didn’t really change anything"
            ]
          },
          {
            "number": "2",
            "question": "Who do you believe was most responsible for the blackouts?",
            "choices": [
              "a) Enron and other energy traders",
              "b) The California government for weak regulations",
              "c) The utilities for poor planning",
              "d) All of the above shared responsibility"
            ]
          },
          {
            "number": "3",
            "question": "How can governments balance competition and regulation to prevent similar crises?",
            "choices": [
              "a) By introducing stricter oversight and transparency",
              "b) By letting markets regulate themselves",
              "c) By avoiding deregulation altogether",
              "d) By allowing only a few trusted companies to trade energy"
            ]
          },
          {
            "number": "4",
            "question": "Should traders or executives face harsher penalties when corporate behavior harms the public?",
            "choices": [
              "a) Yes, stronger punishment would deter unethical behavior",
              "b) No, financial penalties are enough",
              "c) Only if there is clear criminal intent",
              "d) It depends on the size of the damage"
            ]
          },
          {
            "number": "5",
            "question": "What lessons can other industries learn from the Enron scandal?",
            "choices": [
              "a) The importance of transparency and ethical leadership",
              "b) That deregulation always leads to corruption",
              "c) That markets should never be competitive",
              "d) That companies should focus on short-term profits"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-air-traffic-management-flexibility",
    "title": "Air Traffic Management Flexibility",
    "subtitle": "aviation and innovation",
    "category": "reading-exercises",
    "theme": "aviation and innovation",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about how air traffic management must adapt to future challenges.",
    "readingcompTitle": "Air Traffic Management Flexibility",
    "topic": "aviation and innovation",
    "image": "/Images/airdelay1.webp",
    "vocabulary": [
      "resilience - the ability to recover quickly from difficulties",
      "elastic cost base - expenses that can be adjusted depending on demand",
      "scenario planning - preparing for different possible future situations",
      "redeployment - moving staff to different roles or tasks",
      "fragmented - divided into separate and disconnected parts",
      "collaboration - working together with others to achieve common goals",
      "virtual centres - digital ATC facilities not tied to one location",
      "system-wide information management - coordinated data sharing across aviation systems",
      "cybersecurity - protection of systems and networks from digital attacks",
      "contingency - a plan for unexpected events or emergencies"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>The COVID-19 crisis showed how vulnerable air traffic management is to sudden shocks. ANSPs now face challenges like staff shortages, fluctuating traffic, and the need for greener operations. To adapt, they must build both resilience and flexibility into their business models.</p>",
          "<p>Flexibility means being able to scale operations up or down depending on demand. This involves elastic staffing models, smarter contracts with suppliers, and better resource planning. Instead of firefighting when systems reach their limits, ANSPs need to plan ahead with scenario and contingency planning.</p>",
          "<p>Airspace reform is also key. Many regions still operate with fragmented and outdated structures. Cross-border cooperation, sector merging, and projects like Borealis or iTEC show how collaboration can cut costs, improve efficiency, and support innovation.</p>",
          "<p>Technology will play a central role: digital towers, virtual centres, AI, automation, and system-wide data sharing can enable remote operations, flexible staffing, and faster decision-making. These tools can create scalability and improve resilience, but also raise new challenges around human factors and cybersecurity.</p>",
          "<p>The article concludes that real flexibility depends on both technology and proactive planning. By investing in adaptable systems, collaborative approaches, and flexible workforces, ANSPs can prepare for future volatility and shocks, ensuring safer and more sustainable air traffic management.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the biggest barrier to making ANSPs more flexible?",
            "choices": [
              "a) People and staffing issues",
              "b) Technology limitations",
              "c) Regulations and policies",
              "d) Financial constraints"
            ]
          },
          {
            "number": "2",
            "question": "How should ANSPs handle flexibility in staffing?",
            "choices": [
              "a) Outsource more tasks",
              "b) Keep everything in-house",
              "c) Use a balance of outsourcing and in-house staff",
              "d) Avoid major changes to staffing"
            ]
          },
          {
            "number": "3",
            "question": "Why is cross-border collaboration important for European airspace?",
            "choices": [
              "a) It reduces costs and increases efficiency",
              "b) It improves safety and innovation",
              "c) It helps manage fragmented airspace",
              "d) All of the above"
            ]
          },
          {
            "number": "4",
            "question": "Would you trust digital towers and virtual centres compared to traditional ones?",
            "choices": [
              "a) Yes, they are just as safe",
              "b) Yes, but only with strong cybersecurity",
              "c) Maybe, depending on regulations",
              "d) No, traditional towers are safer"
            ]
          },
          {
            "number": "5",
            "question": "What is the most important lesson from the COVID-19 crisis for ANSPs?",
            "choices": [
              "a) Build more financial reserves",
              "b) Invest in flexible systems and planning",
              "c) Keep staff levels low during crises",
              "d) Avoid technological change"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-18-hilarious-parenting-stories",
    "title": "18 Hilarious Parenting Stories",
    "subtitle": "family and humor",
    "category": "reading-exercises",
    "theme": "family and humor",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read 18 funny short stories about things kids have said.",
    "readingcompTitle": "18 Hilarious Parenting Stories",
    "topic": "family and humor",
    "image": "/Images/parents1.webp",
    "vocabulary": [
      "guilt trip - making someone feel guilty to get something",
      "oblige - to agree to do something",
      "remark - a spoken comment",
      "diva - someone who acts dramatically",
      "pixie dust - magical powder from fairy tales",
      "emphatically - in a strong, clear way",
      "chain of command - system of authority",
      "ego - a person’s sense of self-importance",
      "repertoire - a collection of skills or abilities",
      "servant - a person who serves someone else"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Parenting is full of surprises, and children often say the funniest things. These short stories show how kids see the world in creative, unexpected, and hilarious ways.</p>",
          "<p>For example, one mother tried to remind her son of her hard work giving birth to him. Instead of feeling guilty, he asked: “Really Mom, how long are you gonna ride that scooter?” Another child, when asked about the famous artist Henri Matisse, simply said: “Well, he tried his best!”</p>",
          "<p>In church, a little girl told the preacher, “Calm down Pawpaw!” when he raised his voice. Another child misunderstood his mom’s words about hunting people—he thought hunting was only for animals. A three-year-old, asked what she would do in an emergency, said she would eat snacks from the kitchen!</p>",
          "<p>Sometimes children make innocent mistakes. One boy jumped from the stairs to fly like Peter Pan, later whispering, “Must not have been enough pixie dust.” At a funeral, another boy noticed people throwing golf balls in the grave and shouted, “Good thing your friend wasn’t a bowler!”</p>",
          "<p>Grandchildren and siblings can also be brutally honest. One girl promised not to tell her grandpa about a car accident—but instead told him, “Whatever you do, don’t look in the garage!” Another child explained that Jesus died on the cross simply because “his dad told him he had to.”</p>",
          "<p>Other kids use humor to get what they want. A three-year-old claimed to be four years old just to get extra whipped cream. One daughter asked her mom how she could fly around the world if she was really the tooth fairy. A boy proudly said his new classmate spoke ‘Swedish and human.’ And another girl, when introduced to her baby sister, said, “I told you I wanted a puppy!”</p>",
          "<p>Parents are not safe from jokes either. One little boy, after his dad ran for a bus, whispered in amazement: “Daddy, I didn’t know you could run.” Another child, asked to say something nice about dinner, simply said, “Thanks mom for trying.” And when told not to die in the basement after their dog passed away, a boy proved kids sometimes miss the point completely.</p>",
          "<p>Finally, one grandson observed that his grandfather treated his wife like a queen. Asked what that made him, the child quickly answered, “A servant.” Parenting may not always be easy, but it is never boring with children’s funny and honest words.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What do these parenting stories mostly show?",
            "choices": [
              "a) How strict parents can be",
              "b) How children often say funny things",
              "c) How teachers discipline kids",
              "d) How to raise children perfectly"
            ]
          },
          {
            "number": "2",
            "question": "What did Logan’s mother remind him of when he didn’t share his dressing?",
            "choices": [
              "a) That she bought the pizza",
              "b) That she gave birth to him",
              "c) That he should be polite",
              "d) That sharing is important"
            ]
          },
          {
            "number": "3",
            "question": "How did the five-year-old boy describe Matisse’s art?",
            "choices": [
              "a) Beautiful",
              "b) Confusing",
              "c) He tried his best",
              "d) Very modern"
            ]
          },
          {
            "number": "4",
            "question": "What did a girl shout at her grandfather, the preacher?",
            "choices": [
              "a) Stop!",
              "b) Be quiet!",
              "c) Calm down Pawpaw!",
              "d) Speak louder!"
            ]
          },
          {
            "number": "5",
            "question": "What did one three-year-old say she would do if her mom couldn’t wake up?",
            "choices": [
              "a) Call 911",
              "b) Run outside",
              "c) Eat snacks",
              "d) Go to a neighbor"
            ]
          },
          {
            "number": "6",
            "question": "Why did a boy say his attempt to fly failed?",
            "choices": [
              "a) He was too heavy",
              "b) He had no wings",
              "c) Not enough pixie dust",
              "d) The stairs were too short"
            ]
          },
          {
            "number": "7",
            "question": "Why did a boy think it was funny that Bob was a golfer?",
            "choices": [
              "a) People dropped golf balls into the grave",
              "b) Everyone played golf at the funeral",
              "c) He wanted to play golf too",
              "d) The preacher talked about golf"
            ]
          },
          {
            "number": "8",
            "question": "What did a child say when asked why Jesus died on the cross?",
            "choices": [
              "a) To save people",
              "b) Because his dad told him to",
              "c) Because he was tired",
              "d) Because people were angry"
            ]
          },
          {
            "number": "9",
            "question": "What language did one boy say his new Swedish classmate spoke?",
            "choices": [
              "a) Swedish and English",
              "b) Swedish and French",
              "c) Swedish and human",
              "d) Swedish and Spanish"
            ]
          },
          {
            "number": "10",
            "question": "What did a grandson say his grandfather was, since he treated his wife like a queen?",
            "choices": [
              "a) A king",
              "b) A servant",
              "c) A prince",
              "d) A hero"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-defining-fast-fashion",
    "title": "Defining Fast Fashion",
    "subtitle": "fashion and sustainability",
    "category": "reading-exercises",
    "theme": "fashion and sustainability",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about what fast fashion is and how it works.",
    "readingcompTitle": "Defining Fast Fashion",
    "topic": "fashion and sustainability",
    "image": "/Images/fastfashion1.webp",
    "vocabulary": [
      "reflect - to show or represent",
      "trendy - currently popular or fashionable",
      "affordable - not too expensive",
      "replace - to buy or get new items instead of old ones",
      "cycle - a repeated process",
      "constantly - all the time, without stopping"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Fast fashion is a business model that allows clothing companies to produce trendy, affordable clothes at an extremely fast rate. It is the rapid production of inexpensive clothing designed to reflect the latest trends. These clothes are often produced using low-cost materials and cheap labour, which allows brands to sell them at very affordable prices.</p>",
          "<p>Unlike traditional fashion, which focused on high-quality, long-lasting materials, fast fashion encourages people to buy more and replace their clothes more frequently. In simple terms, fast fashion is all about speed, affordability, and trendiness. Instead of waiting for a new fashion season, companies introduce fresh styles weekly, or even daily.</p>",
          "<p>Fast fashion companies pay close attention to the latest trends seen on social media, and also what celebrity influencers are wearing. They quickly design similar styles and produce them. Because fast fashion is designed to be cheap and trendy, the clothing is not made to last. Many fast fashion items lose their shape, fade, or tear after just a few washes.</p>",
          "<p>This creates a cycle where customers constantly buy and replace their clothing, leading to waste and environmental issues. Several global brands have embraced fast fashion. Zara, known for releasing new clothes twice a month; H&M, which can be found in almost every major city; and Shein, an online retailer that uses algorithms and real-time data to create new designs extremely quickly.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main goal of fast fashion companies?",
            "choices": [
              "a) Produce high-quality clothes slowly",
              "b) Produce trendy, affordable clothes quickly",
              "c) Focus only on luxury brands",
              "d) Reduce clothing sales"
            ]
          },
          {
            "number": "2",
            "question": "How do fast fashion brands keep their clothing affordable?",
            "choices": [
              "a) Using low-cost materials and cheap labour",
              "b) Raising prices every season",
              "c) Only selling online",
              "d) Hiring celebrity designers"
            ]
          },
          {
            "number": "3",
            "question": "What is different about fast fashion compared to traditional fashion?",
            "choices": [
              "a) Fast fashion focuses on durability",
              "b) Fast fashion encourages frequent purchases",
              "c) Traditional fashion is cheaper",
              "d) Traditional fashion uses algorithms"
            ]
          },
          {
            "number": "4",
            "question": "How often do fast fashion companies introduce new styles?",
            "choices": [
              "a) Once a year",
              "b) Once per season",
              "c) Weekly or even daily",
              "d) Every five years"
            ]
          },
          {
            "number": "5",
            "question": "Which factor influences fast fashion trends the most?",
            "choices": [
              "a) Newspaper advertisements",
              "b) Social media and celebrity influencers",
              "c) Weather forecasts",
              "d) Local fashion schools"
            ]
          },
          {
            "number": "6",
            "question": "Why do many fast fashion items lose their shape or tear quickly?",
            "choices": [
              "a) Because they are expensive",
              "b) Because they are made cheaply",
              "c) Because customers wash them incorrectly",
              "d) Because they are handmade"
            ]
          },
          {
            "number": "7",
            "question": "What cycle does fast fashion create?",
            "choices": [
              "a) Customers repair old clothes constantly",
              "b) Customers buy and replace clothing frequently",
              "c) Clothing is donated regularly",
              "d) Clothes last for decades"
            ]
          },
          {
            "number": "8",
            "question": "Which of the following brands are examples of fast fashion?",
            "choices": [
              "a) Zara, H&M, Shein",
              "b) Gucci, Prada, Chanel",
              "c) Levi's, Wrangler, Carhartt",
              "d) Patagonia, North Face, Columbia"
            ]
          },
          {
            "number": "9",
            "question": "How does Shein produce new designs quickly?",
            "choices": [
              "a) Using algorithms and real-time data",
              "b) By hiring more tailors",
              "c) By recycling old clothes",
              "d) By collaborating with luxury brands"
            ]
          },
          {
            "number": "10",
            "question": "What is one negative consequence of fast fashion?",
            "choices": [
              "a) It reduces clothing waste",
              "b) It creates environmental issues",
              "c) It encourages saving money",
              "d) It slows trend cycles"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-the-future-of-work",
    "title": "The Future of Work",
    "subtitle": "work and technology",
    "category": "reading-exercises",
    "theme": "work and technology",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about how work is changing with technology.",
    "readingcompTitle": "The Future of Work",
    "topic": "work and technology",
    "image": "/Images/future1.webp",
    "vocabulary": [
      "remote - working from home or another place outside the office",
      "flexible - able to change easily",
      "automation - machines or software doing work instead of people",
      "skills - abilities you learn and use",
      "collaboration - working together with others",
      "global - relating to the whole world",
      "digital - using computers or the internet",
      "balance - a situation where different parts are equal or fair"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>The Future of Work</h1>",
          "<p>Work is changing quickly. New technology, like fast internet and smart machines, is creating new jobs and changing old ones. Many people can now work remotely, from home or anywhere with Wi-Fi.</p>",
          "<p>In the future, jobs will need more digital skills. People will have to learn how to use new tools and keep learning all their lives. Collaboration will also be important, because many projects will connect workers across the world.</p>",
          "<p>Automation will take over some simple tasks, but humans will still be needed for creative thinking, problem solving, and communication. Flexible work, such as choosing your hours, may give people a better balance between work and free time.</p>",
          "<p>The future of work brings both challenges and opportunities. If people learn new skills and stay open to change, they can succeed in this global, digital world.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What allows many people to work remotely today?",
            "choices": [
              "a) Better coffee",
              "b) Fast internet and technology",
              "c) Long holidays",
              "d) Local offices"
            ]
          },
          {
            "number": "2",
            "question": "Why will digital skills be important in the future?",
            "choices": [
              "a) Because they are fun",
              "b) Because jobs will need new tools and learning",
              "c) Because books are disappearing",
              "d) Because schools will close"
            ]
          },
          {
            "number": "3",
            "question": "Which tasks are machines most likely to do?",
            "choices": [
              "a) Creative thinking",
              "b) Problem solving",
              "c) Simple, repeated work",
              "d) Communication with people"
            ]
          },
          {
            "number": "4",
            "question": "What is one possible benefit of flexible work?",
            "choices": [
              "a) More free time and balance",
              "b) More stress",
              "c) Less teamwork",
              "d) Fewer jobs"
            ]
          },
          {
            "number": "5",
            "question": "Do you think you would enjoy working remotely? Why or why not?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-effective-meetings",
    "title": "Effective Meetings",
    "subtitle": "business skills",
    "category": "reading-exercises",
    "theme": "business skills",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read a checklist for running effective meetings.",
    "readingcompTitle": "Effective Meetings",
    "topic": "business skills",
    "image": "/Images/meeting.webp",
    "vocabulary": [
      "agenda - a list of topics to be discussed",
      "participant - a person who takes part in a meeting",
      "discussion - a conversation about a topic",
      "decision - a conclusion reached after consideration",
      "action - a task assigned to someone",
      "dysfunctional - not operating normally or properly",
      "constructive - helpful and intended to improve",
      "engagement - involvement or participation"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>___ 1. Participants knew the purpose and agenda for the meeting in advance. ___ </p>",
          "<p>___ 2. The right participants were at the meeting; they arrived on time and stayed for the duration. ___ </p>",
          "<p>___ 3. The meeting started on time and ended on time or early. ___ </p>",
          "<p>___ 4. The meeting’s purpose, desired products, and agenda were all reviewed at the start of the meeting. ___ </p>",
          "<p>___ 5. People spoke openly, honestly and respectfully, with one conversation occurring at a time. ___ </p>",
          "<p>___ 6. The discussions stayed focused and on topic. ___ </p>",
          "<p>___ 7. All participants were engaged in the meeting; no one dominated the discussion. ___ </p>",
          "<p>___ 8. There was adequate dialogue and debate prior to making decisions. ___ </p>",
          "<p>___ 9. Disagreements were handled constructively. ___ </p>",
          "<p>___ 10. Any dysfunctional behavior (e.g., doing other work, speaking with disrespect) was addressed. ___ </p>",
          "<p>___ 11. Prior to ending the meeting, all issues, decisions, and actions were reviewed. ___ </p>",
          "<p>___ 12. The meeting produced a valuable result that could not have been achieved without a meeting. ___ </p>",
          "<p>___ 13. Following the meeting, a summary of decisions and actions was distributed to all participants. ___ </p>",
          "<p>___ 14. A follow-up process was used to ensure all assigned actions were accomplished. ___ </p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Why is it important for participants to know the agenda in advance?",
            "choices": [
              "a) So they can skip the meeting",
              "b) To prepare and contribute effectively",
              "c) To arrive late",
              "d) To avoid discussion"
            ]
          },
          {
            "number": "2",
            "question": "What should be reviewed at the start of the meeting?",
            "choices": [
              "a) The office rules",
              "b) The meeting’s purpose, agenda, and desired products",
              "c) The previous day’s lunch",
              "d) The participants’ schedules"
            ]
          },
          {
            "number": "3",
            "question": "How should disagreements be handled?",
            "choices": [
              "a) Ignored",
              "b) Constructively",
              "c) Heated arguments",
              "d) Postponed indefinitely"
            ]
          },
          {
            "number": "4",
            "question": "What should happen after the meeting?",
            "choices": [
              "a) Everyone leaves without notice",
              "b) A summary of decisions and actions is distributed",
              "c) Participants meet privately",
              "d) No follow-up is needed"
            ]
          },
          {
            "number": "5",
            "question": "What makes a meeting valuable?",
            "choices": [
              "a) It is long",
              "b) It produces results that could not have been achieved without it",
              "c) Only one person speaks",
              "d) No agenda is followed"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-the-power-of-brands",
    "title": "The Power of Brands",
    "subtitle": "consumer behavior",
    "category": "reading-exercises",
    "theme": "consumer behavior",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about how brands influence our choices and identity.",
    "readingcompTitle": "The Power of Brands",
    "topic": "consumer behavior",
    "image": "/Images/brands.webp",
    "vocabulary": [
      "brand - a product name that is well known",
      "identity - the way a person sees themselves",
      "loyalty - strong support for something",
      "subconscious - happening in the mind without awareness",
      "affiliation - connection or link with something",
      "empathy - ability to share or understand another's feelings",
      "influence - power to affect decisions or actions",
      "rational - logical and reasonable"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>The Power of Brands</h1>",
          "<p>Brands are more than products. Coke is just soda, Tylenol just medicine, and Levi’s just jeans, yet people pay more for these names. This is because brands tell stories that shape how we think and act.</p>",
          "<p>Studies show brands can affect us without us noticing. For example, people who saw the Apple logo performed better on creative tasks than those who saw IBM’s. Apple is linked with being cool and creative.</p>",
          "<p>Choosing a brand can also mean choosing an identity. Nike suggests performance, while Under Armour suggests the underdog spirit. People often defend their favorite brands as if they were family.</p>",
          "<p>Brain studies found Apple fans reacted to bad news about Apple the same way they would to bad news about loved ones. Samsung users, however, only showed positive feelings when Apple had bad news, showing rivalry rather than loyalty.</p>",
          "<p>Even people who claim not to care about brands often follow an ‘anti-brand’ identity. Brands now play a role once held by communities or traditions, becoming part of who we are. To make smart choices, it helps to pause and ask: Why am I buying this product?</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Why do people often pay more for certain brands?",
            "choices": [
              "a) Because they are always better quality",
              "b) Because brands tell stories and shape identity",
              "c) Because they are the only products available",
              "d) Because stores make them cheaper"
            ]
          },
          {
            "number": "2",
            "question": "What effect did the Apple logo have in the study?",
            "choices": [
              "a) It made people run faster",
              "b) It made people more creative",
              "c) It made people like Apple less",
              "d) It made people think about IBM"
            ]
          },
          {
            "number": "3",
            "question": "How did Apple fans react in brain studies?",
            "choices": [
              "a) They ignored Apple news",
              "b) They showed empathy like for family",
              "c) They showed no reaction",
              "d) They reacted only when Samsung had bad news"
            ]
          },
          {
            "number": "4",
            "question": "What is an ‘anti-brand’ brand?",
            "choices": [
              "a) A brand that sells no products",
              "b) A way of rejecting brands but still showing identity",
              "c) A brand that copies others",
              "d) A secret brand with no name"
            ]
          },
          {
            "number": "5",
            "question": "Do you think brands influence your choices? Why or why not?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-why-people-like-sports",
    "title": "Why People Like Sports",
    "subtitle": "health and leisure",
    "category": "reading-exercises",
    "theme": "health and leisure",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about why people enjoy playing and watching sports.",
    "readingcompTitle": "Why People Like Sports",
    "topic": "health and leisure",
    "image": "/Images/parents-football-match1.webp",
    "vocabulary": [
      "competition - sports event",
      "improve - make better",
      "exercise - Physical activity that keeps the body healthy and strong.",
      "require - must do",
      "cooperate - work together",
      "individual - not in a group",
      "stay - not change, not move",
      "athlete - person who does sports"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Why People Like Sports</h1>",
          "<p>Sports are popular all over the world. People of all ages enjoy them, either by playing or by watching. Some people like sports because they are fun and exciting, while others enjoy them as a way to stay healthy.</p>",
          "<p>Playing sports is a good form of exercise. It helps people improve their fitness and gives them more energy in daily life. Sports like football, basketball, or tennis can also reduce stress and make people feel happier.</p>",
          "<p>Another reason people like sports is teamwork. Many sports require players to cooperate and trust each other. This builds friendships and teaches important life skills. Even individual sports, like running or swimming, help people set goals and stay motivated.</p>",
          "<p>Sports are also exciting for spectators. Watching a live match can bring people together and create a strong feeling of community. For many, supporting their favorite team or athlete is a tradition they share with family and friends.</p>",
          "<p>Finally, sports can inspire people. Professional athletes show what is possible with hard work and dedication. At the same time, amateurs enjoy sports simply because they love the activity. Whether playing or watching, sports give people joy, energy, and motivation.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is one reason people play sports?",
            "choices": [
              "a) To get more sleep",
              "b) To improve fitness",
              "c) To learn languages",
              "d) To earn money easily"
            ]
          },
          {
            "number": "2",
            "question": "What do many sports teach people?",
            "choices": [
              "a) How to travel",
              "b) How to cook",
              "c) How to work as a team",
              "d) How to save money"
            ]
          },
          {
            "number": "3",
            "question": "Why do spectators enjoy sports?",
            "choices": []
          },
          {
            "number": "4",
            "question": "Who is a professional athlete?",
            "choices": [
              "a) A person who watches sports",
              "b) Someone who plays for fun",
              "c) Someone who plays for money",
              "d) A coach who teaches sports"
            ]
          },
          {
            "number": "5",
            "question": "Do you prefer playing or watching sports? Why?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-five-power-and-electric-trends-that-will-shape-the-future",
    "title": "Five Power and Electric Trends That Will Shape the Future",
    "subtitle": "technology and innovation",
    "category": "reading-exercises",
    "theme": "technology and innovation",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about five trends shaping the future of power and electronics.",
    "readingcompTitle": "Five Power and Electric Trends That Will Shape the Future",
    "topic": "technology and innovation",
    "image": "/Images/future1.webp",
    "vocabulary": [
      "strive - work hard",
      "influences - has an effect on",
      "sensor - A device that detects and measures things like heat or movement.",
      "eliminates - removes",
      "untangling - taking the knots out of",
      "reliable - won't break down"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Five Power and Electric Trends That Will Shape the Future</h1>",
          "<p>As technology continues its ever-changing wave of innovations and updates, engineers must strive to stay ahead of electrical engineering trends. Why? Because the future of electronics influences several industries, such as automotive, healthcare, manufacturing, telecommunications, banking and finance, retail, education, energy, aerospace, and security. In short, regardless of the industry, engineers will be responsible for building new ways of interacting with technology.</p>",
          "<h2>1. Wireless Power Transfer</h2>",
          "<p>Have you ever worried about forgetting to pack your charger when getting ready to leave on a trip? Or maybe you did forget it. This trend in electrical engineering eliminates any need for concern or inconvenience.</p>",
          "<p>Though still in its early stages of development and production, wireless power transfer is a promising innovation for the future of electronics. In short, wireless power transfer (WPT), also known as wireless energy transfer, is the transmission of electrical energy from a power source to a receiver without the use of interconnecting wires. WPT systems use time-varying electromagnetic fields for energy transmission. These systems ride along the same fields and waves as wireless communication devices. Essentially, a receiver in a device picks up the power, which allows for contactless charging, powering, and data communication.</p>",
          "<p><strong>Simple and Convenient:</strong> Developed by MIT physicists, wireless power transfer eliminates the need for chasing chargers or untangling dozens of cords and cables. This technology transfers power to devices through plastic, granite, wood, and even the air.</p>",
          "<p><strong>Innovative Uses:</strong> If you think wireless power transfer is limited to your phone or computer, think again. Electric vehicle charging docks, security software, and heart pumps have all been discussed as potential use cases for wireless power transfer. Smart homes, industrial machinery, wearables, and even remote areas can all benefit from this technology.</p>",
          "<h2>2. Wearable Tech</h2>",
          "<p>While wearable technology is nothing new, its constant innovation and new iterations require the industry to think on its feet (literally) to meet consumer demand and to stay ahead of electrical engineering trends.</p>",
          "<p><strong>Heart Monitors for the People:</strong> Some would argue that the original wearable tech piece was the first pair of eyeglasses. But we’d suggest something along the lines of a step counter with a digital display or a Garmin sports watch. What began as an activity-tracking device has grown into wearable pieces that send and receive texts, make calls, and alert wearers of possible health conditions.</p>",
          "<p><strong>Forward-Thinking Fashion:</strong> Wearable tech can become a life-saving device. SolePower boots, for example, are designed to reduce workplace injuries. They monitor the wearer’s real-time location, environmental conditions, and fatigue. Smart clothing also incorporates sensors into fabric to track signals like heart rate, temperature, and respiration. Brands like Sensoria and Spire Health give feedback to users and doctors in real time.</p>",
          "<h2>3. Electric Power Distribution and Supply</h2>",
          "<p>Almost gone are the days of customer reliance on power from a single, localized power company. Today’s power generation industry trends dictate a better, more efficient way to generate power with smart grids and microgrids.</p>",
          "<p><strong>Smart Grids:</strong> A smart grid uses digital technology to monitor and manage the flow of electricity, improving efficiency and reliability. It allows renewable energy sources and storage to support a more sustainable supply.</p>",
          "<p><strong>Microgrids:</strong> Microgrids are self-contained power systems for communities. They provide reliability, cost savings, sustainability, efficiency, and scalability. Communities can even sell surplus energy back to the main grid.</p>",
          "<h2>4. Electric Vehicles</h2>",
          "<p>Despite years of mass production, electric vehicles (EVs) are still rising in popularity. They are supported by governments through tax incentives and more charging stations. The Biden administration, for example, wants half of all U.S. vehicles sold in 2030 to be electric.</p>",
          "<p><strong>Record-Breaking Popularity:</strong> Companies like Tesla, Ford, Toyota, and GM are heavily investing in electric vehicles. Consumers are drawn to sustainability and lower emissions.</p>",
          "<p><strong>Multiple Vehicle Options:</strong> EVs include all-electric, plug-in hybrids, and hybrid models. Demand is expected to grow to over 125 million EVs by 2030, creating space for further innovation.</p>",
          "<h2>5. The Internet of Things (IoT)</h2>",
          "<p>The IoT is a network of connected devices that communicate with each other. It includes phones, sensors, actuators, and more. Data from IoT can be analyzed to control and automate processes.</p>",
          "<p><strong>Room for Innovation:</strong> Companies like Amazon, IBM, Microsoft, Intel, Cisco, and Samsung need engineers to develop and maintain IoT systems. With 5G networks, IoT will become faster and more reliable, supporting applications like augmented and virtual reality.</p>",
          "<p><strong>Everyday Uses:</strong> IoT improves smart homes, smart cities, industrial automation, smart meters, and energy storage. It will continue to shape the future of electronics.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What does wireless power transfer allow you to do?",
            "choices": [
              "a) Use more cables",
              "b) Charge devices without cables",
              "c) Increase gasoline use",
              "d) Travel faster"
            ]
          },
          {
            "number": "2",
            "question": "Give two examples of wearable technology mentioned in the text.",
            "choices": []
          },
          {
            "number": "3",
            "question": "What is the difference between a smart grid and a microgrid?",
            "choices": []
          },
          {
            "number": "4",
            "question": "Why are electric vehicles becoming more popular?",
            "choices": [
              "a) They use more gasoline",
              "b) They are less sustainable",
              "c) They are supported by governments and reduce emissions",
              "d) They are cheaper than bicycles"
            ]
          },
          {
            "number": "5",
            "question": "What does IoT stand for and what does it do?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-discover-london",
    "title": "Discover London",
    "subtitle": "cities and culture",
    "category": "reading-exercises",
    "theme": "cities and culture",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about London's history, culture, and attractions.",
    "readingcompTitle": "Discover London",
    "topic": "cities and culture",
    "image": "/Images/london.webp",
    "vocabulary": [
      "capital - The most important city of a country, where the government is located.",
      "landmark - A famous building or place that is easy to recognize.",
      "attraction - Something interesting for tourists to visit or see.",
      "multicultural - Including people from many different countries and cultures.",
      "public transport - Buses, trains, and other shared ways of traveling in a city.",
      "the Tube - The London Underground, the city’s metro system.",
      "historic - Something old and important in history.",
      "modern - Something new and related to the present time.",
      "theatre - A place where plays and musicals are performed.",
      "neighborhood - A part of a city where people live."
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Discover London</h1>",
          "<p>London is the capital city of the United Kingdom and one of the most famous cities in the world. It has a population of over eight million people and is known for its history, culture, and diversity. Every year, millions of tourists visit London to experience its unique atmosphere.</p>",
          "<p>The city has many historic landmarks, such as the Tower of London, Buckingham Palace, and Big Ben. It also offers modern attractions like the London Eye, a giant wheel that gives visitors a wonderful view of the city. London is home to world-class museums and galleries, such as the British Museum and the National Gallery, which are free to visit.</p>",
          "<p>London is a truly multicultural city. People from all over the world live there, and this can be seen in the food, music, and traditions. Visitors can enjoy dishes from almost every country and experience different cultural festivals throughout the year.</p>",
          "<p>Getting around London is easy thanks to its public transport system. The Tube, buses, and trains connect every part of the city. However, London can also be busy and expensive, so visitors should plan carefully.</p>",
          "<p>London is a city that combines the old and the new. It is historic but also modern, traditional but also creative. Whether you are interested in history, shopping, theatre, or nightlife, London has something for everyone.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the capital of the United Kingdom?",
            "choices": [
              "a) Manchester",
              "b) London",
              "c) Edinburgh",
              "d) Birmingham"
            ]
          },
          {
            "number": "2",
            "question": "Which of these is a modern attraction in London?",
            "choices": [
              "a) Tower of London",
              "b) Big Ben",
              "c) London Eye",
              "d) Buckingham Palace"
            ]
          },
          {
            "number": "3",
            "question": "Why is London considered multicultural?",
            "choices": []
          },
          {
            "number": "4",
            "question": "What is the Tube?",
            "choices": [
              "a) A museum",
              "b) A theatre",
              "c) The Underground metro system",
              "d) A shopping center"
            ]
          },
          {
            "number": "5",
            "question": "Would you like to visit London? What would you most like to see or do there?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-dua-lipa-a-modern-pop-icon",
    "title": "Dua Lipa – A Modern Pop Icon",
    "subtitle": "music and celebrities",
    "category": "reading-exercises",
    "theme": "music and celebrities",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about the career of pop singer Dua Lipa.",
    "readingcompTitle": "Dua Lipa – A Modern Pop Icon",
    "topic": "music and celebrities",
    "image": "/Images/dua_lipa.webp",
    "vocabulary": [
      "album - A collection of songs released together.",
      "record deal - A contract with a music company to produce and release songs.",
      "debut - A first public appearance or performance.",
      "hit - A very popular song that becomes successful worldwide.",
      "catchy - Easy to remember and pleasant to listen to.",
      "empowering - Giving confidence and a sense of strength.",
      "energetic - Full of energy; lively and active.",
      "style - The special way a person dresses or presents themselves.",
      "award - A prize given for an achievement.",
      "platform - A way to share ideas or reach an audience, often through media."
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Dua Lipa – A Modern Pop Icon</h1>",
          "<p>Dua Lipa is one of the most successful pop singers of the last decade. She was born in London in 1995 to Kosovar-Albanian parents. Her father was also a musician, so she started singing at a young age. After uploading her songs to YouTube, she quickly attracted attention and signed her first record deal in 2015.</p>",
          "<p>Her debut album, <i>Dua Lipa</i>, came out in 2017 and included international hits like <i>New Rules</i>. This song made her famous around the world because of its catchy beat and empowering lyrics. Since then, she has released several more hits, such as <i>Don’t Start Now</i> and <i>Levitating</i>. Her music mixes pop, dance, and electronic sounds, which makes it perfect for radio stations and clubs.</p>",
          "<p>As a DJ at a hit radio station, I often play her tracks because they are guaranteed to get listeners in a good mood. Her songs are energetic, modern, and always sound fresh. Listeners often call in to request them, especially when they want something to dance to.</p>",
          "<p>Besides her music, Dua Lipa is also known for her unique style and strong personality. She has won many awards, including Grammy Awards, and she uses her platform to speak about important issues like women’s rights and equality.</p>",
          "<p>Dua Lipa shows how talent, hard work, and confidence can take a young artist to the top of the music industry. For me, as someone who works with music every day, she is one of the most exciting artists to play on the radio.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Where was Dua Lipa born?",
            "choices": [
              "a) New York",
              "b) London",
              "c) Pristina",
              "d) Paris"
            ]
          },
          {
            "number": "2",
            "question": "What was the title of her debut album?",
            "choices": [
              "a) New Rules",
              "b) Don’t Start Now",
              "c) Dua Lipa",
              "d) Future Nostalgia"
            ]
          },
          {
            "number": "3",
            "question": "Why are her songs popular for radio stations?",
            "choices": [
              "a) They are sad and slow.",
              "b) They are energetic and catchy.",
              "c) They are old classics.",
              "d) They are only instrumental."
            ]
          },
          {
            "number": "4",
            "question": "What important issues does Dua Lipa speak about?",
            "choices": []
          },
          {
            "number": "5",
            "question": "Would you like to play Dua Lipa’s songs if you were a DJ? Why or why not?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-bucket-lists-in-the-u-s",
    "title": "Bucket Lists in the U.S.",
    "subtitle": "travel and goals",
    "category": "reading-exercises",
    "theme": "travel and goals",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about what bucket lists are and why people make them.",
    "readingcompTitle": "Bucket Lists in the U.S.",
    "topic": "travel and goals",
    "image": "/Images/eiffel_tower.webp",
    "vocabulary": [
      "kick the bucket - An informal euphemism meaning 'to die'.",
      "euphemistic - A mild or indirect word used to replace one that may be harsh or unpleasant.",
      "mortality - The state of being subject to death; awareness that life is finite.",
      "aspiration - A strong desire, hope, or goal to achieve something.",
      "heel - The back part of the foot below the ankle.",
      "values - Beliefs or principles that are important to a person.",
      "symbolic - Serving as a symbol or representation of something else.",
      "risk averse - Unwilling to take risks; preferring safety and caution.",
      "signify - To represent or mean something.",
      "hoofs - The plural of hoof, referring to the feet of certain animals like horses or cattle.",
      "static - Not changing or moving; unchanging over time."
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>What is a Bucket List?</h1>",
          "<p><b>What does \"kicked the bucket\"* mean?</b><br>\"Kicked the bucket\" is a euphemistic phrase for death.</p>",
          "<p><b>What is a bucket list?</b><br>A bucket list is a list of the experiences or achievements that a person hopes to have or accomplish during their lifetime. A bucket list is an itemized list of goals people want to accomplish before they “kick the bucket” — or die.</p>",
          "<p><b>*Where did the term \"kicked the bucket\" come from?</b><br>The word bucket, from French 'buquet', denotes a beam used by butchers in the 1500s to hang up slaughtered animals by their heels or hooves. In the throes of death, the animals \"kicked the bucket\". Another possible explanation is that a person who commits suicide by hanging might stand on an inverted bucket and then kick the bucket to die.</p>",
          "<p><b>What is the value of a bucket list?</b><br>The focus of a bucket list is to live a life with hopes and aspirations. Making a bucket list allows us to reflect on our values and goals, and identify important milestones and experiences that we want to have in our lifetime.</p>",
          "<p><b>Is a bucket list used only in the context of death?</b><br>Not at all. The bucket list has now become a way to make a list of things a person wants to accomplish before a specific event in their lives. For example, many teenagers have a \"high school bucket list\" or a \"prom night bucket list\".</p>",
          "<p><b>What is the symbolic meaning of a bucket list?</b><br>It is a tangible recognition of our mortality. It allows us to reflect on what matters most to us, on our personal values, and to identify important life milestones and experiences that we want to have before a certain milestone in our life (e.g. high school graduation, college graduation, before retirement, before we die).</p>",
          "<p><b>What else does a bucket list signify?</b><br>It is a sign of hope and future orientation. It also motivates us to accomplish certain things with a specific timeline.</p>",
          "<p><b>Can I change the items on my bucket list?</b><br>A bucket list is not a static list. As we live our lives, our aspirations change. The goals and aspirations of our teenage years will be vastly different from our aspirations in our twenties and thirties. Young people often have daring deeds listed. As we get older, people become more risk averse and their bucket list focuses on goals like traveling and spending time with loved ones.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Do you have a bucket list? What is on it?",
            "choices": []
          },
          {
            "number": "2",
            "question": "Why do you think travel is so popular on bucket lists?",
            "choices": []
          },
          {
            "number": "3",
            "question": "What stops people from completing all the items on their bucket lists?",
            "choices": []
          },
          {
            "number": "4",
            "question": "How can a bucket list help you in your life?",
            "choices": []
          },
          {
            "number": "5",
            "question": "Should people update their bucket lists regularly? Why or why not?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-cell-phones-bad",
    "title": "Cell Phones Bad",
    "subtitle": "event disasters",
    "category": "reading-exercises",
    "theme": "event disasters",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about the pros and cons of cell phones.",
    "readingcompTitle": "Cell Phones Bad",
    "topic": "event disasters",
    "image": "/Images/cellphone1.webp",
    "vocabulary": [
      "hurting - fájni",
      "screen - képérnyő"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Are Cell Phones Good or Bad?</h1>",
          "<p>Cell phones are everywhere. People use them for messages, games, photos, and videos. But are they helping us or hurting us?</p>",
          "<p>Some people say phones are bad. Kids spend too much time looking at screens. Families talk less at dinner. People feel stressed without their phones.</p>",
          "<p>Others say phones are helpful. We can talk to friends far away, find information quickly, and stay safe in emergencies. Some students even use phones to learn.</p>",
          "<p>Maybe phones are not good or bad. It depends how we use them. Like books, TVs, or anything else—phones can help or harm us.</p>",
          "<p>What do you think? Are phones a problem, or are they just part of life today?</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Do you think cell phones are more helpful or harmful? Why?",
            "choices": []
          },
          {
            "number": "2",
            "question": "How much time do you spend on your phone each day? Is that too much?",
            "choices": []
          },
          {
            "number": "3",
            "question": "In what ways can phones improve learning or education?",
            "choices": []
          },
          {
            "number": "4",
            "question": "Should kids have their own phones? Why or why not?",
            "choices": []
          },
          {
            "number": "5",
            "question": "Can people live happily without phones today? Explain your answer.",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-festival-failures-fyre-and-woodstock-99",
    "title": "Festival Failures: Fyre and Woodstock ’99",
    "subtitle": "event disasters",
    "category": "reading-exercises",
    "theme": "event disasters",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about two famous music festival disasters.",
    "readingcompTitle": "Festival Failures: Fyre and Woodstock ’99",
    "topic": "event disasters",
    "image": "/Images/woodstock-99.webp",
    "vocabulary": [
      "disaster relief tents - tents used in disaster situations",
      "styrofoam  - packing material made of a hard foam like material",
      "pull out - cancel an appearance at an event",
      "cautionary tale - story to learn from",
      "venue - concert location/building",
      "shade - protection from the sun",
      "outrageous - crazy",
      "leaned heavily on - mostly used",
      "looted - stolen from"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Music festivals are meant to be unforgettable — for the music, the atmosphere, the memories. But sometimes, what people remember most is the disaster.</p>",
          "<p>In 2017, Fyre Festival promised guests a once-in-a-lifetime luxury experience: white sand beaches, top musical acts, gourmet food, and glamorous influencer-filled parties. Promoted heavily on social media by models and celebrities, the festival sold an image of paradise. But when guests arrived on the Bahamian island, they were greeted not by beachfront villas, but by disaster relief tents. The “gourmet meals” were cheese sandwiches in styrofoam boxes. Luggage was thrown out of the back of trucks. Artists had pulled out, the power failed, and flights off the island were delayed. The whole event quickly collapsed, and the organizer was later sentenced to prison for fraud.</p>",
          "<p>But Fyre wasn’t the first time a major festival turned into a cautionary tale.</p>",
          "<p>Back in 1999, Woodstock tried to recreate its 1969 legacy — peace, music, and love. Instead, the result was chaos. Held on a former military base in New York during a heatwave, the venue lacked proper shade, free drinking water, and clean bathrooms. Vendors charged outrageous prices — $4 for water, $12 for a slice of pizza. Security was weak, and the lineup leaned heavily on aggressive, high-energy rock acts. Over the three days, tensions rose. By the final night, fires had been set, vendor booths were looted, and multiple reports of violence emerged. What was supposed to be a celebration of unity turned into one of the most infamous music disasters in modern history.</p>",
          "<p>So what connects these two failures?</p>",
          "<p>Both festivals were victims of overpromising and underplanning. The organizers had strong branding but weak logistics. At Fyre, it was style over substance — a social media illusion with no real infrastructure behind it. At Woodstock ’99, organizers underestimated the effect of heat, crowds, and poor conditions. In both cases, safety and basic needs were ignored in favor of hype and profit.</p>",
          "<p>These events remind us that massive gatherings aren’t just about music. They’re about crowd management, preparation, and responsibility. When organizers forget that, even the best intentions can spiral into chaos.</p>",
          "<p>And yet, every few years, a new event comes along making big promises. The question is: have we learned anything?</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Do you think modern music festivals care more about marketing than safety? Why or why not?",
            "choices": []
          },
          {
            "number": "2",
            "question": "If you had been at Fyre Festival or Woodstock ’99, what would you have done differently?",
            "choices": []
          },
          {
            "number": "3",
            "question": "Why do you think people still trust expensive events like Fyre Festival, even after seeing past failures?",
            "choices": []
          },
          {
            "number": "4",
            "question": "Do you think the organizers of these events should be punished or just criticized? Explain.",
            "choices": []
          },
          {
            "number": "5",
            "question": "Would you ever attend a big music festival? Why or why not?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-colleague-catchup",
    "title": "Catching Up at the Office",
    "subtitle": "Business Dialogue",
    "category": "reading-exercises",
    "theme": "Business Dialogue",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read a dialogue between two colleagues catching up at the office.",
    "readingcompTitle": "Catching Up at the Office",
    "topic": "Business Dialogue",
    "image": "/Images/tork-head.webp",
    "vocabulary": [
      "promotion - előléptetés",
      "project - projekt",
      "deadline - határidő",
      "training - képzés",
      "position - pozíció",
      "update - frissítés",
      "role - szerep",
      "clients - ügyfelek",
      "travel - utazás",
      "busy - elfoglalt",
      "schedule - időbeosztás",
      "improve - javítani",
      "team - csapat",
      "tough - nehéz"
    ],
    "paragraphs": [
      {
        "text": [
          "<p><strong>Catching Up at the Office</strong></p>",
          "<p>Emma: Hi Daniel! I haven't seen you since February. How have you been?</p>",
          "<p>Daniel: Hey Emma! Yes, it's been a while. I've been really busy with a new project. I also got a promotion last month!</p>",
          "<p>Emma: That's great news! What's your new role?</p>",
          "<p>Daniel: I'm now the regional sales manager. It means more travel and bigger clients, but I enjoy the challenge.</p>",
          "<p>Emma: Sounds exciting. I've been doing some training to improve my presentation skills. Our team has more client meetings now, so it helps.</p>",
          "<p>Daniel: Good idea. Communication is key. We just finished a tough project with a very tight deadline.</p>",
          "<p>Emma: I know the feeling. It's been a busy year for all of us!</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "When did Emma last see Daniel?",
            "choices": [
              "A. Last week",
              "B. In February",
              "C. In December",
              "D. Yesterday"
            ]
          },
          {
            "number": "2",
            "question": "Why has Daniel been busy?",
            "choices": [
              "A. He changed companies",
              "B. He moved to another city",
              "C. He was working on a new project and got a promotion",
              "D. He was on vacation"
            ]
          },
          {
            "number": "3",
            "question": "What is Daniel's new position?",
            "choices": [
              "A. Project manager",
              "B. Team assistant",
              "C. Regional sales manager",
              "D. Marketing coordinator"
            ]
          },
          {
            "number": "4",
            "question": "Why did Emma do some training?",
            "choices": [
              "A. She wants to change jobs",
              "B. She needs better presentation skills for client meetings",
              "C. She wants to travel more",
              "D. She had free time"
            ]
          },
          {
            "number": "5",
            "question": "What kind of project did Daniel just finish?",
            "choices": [
              "A. A relaxing one",
              "B. A short holiday trip",
              "C. A project with a tight deadline",
              "D. A team-building activity"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-working-as-a-weekend-dj-at-a-hit-radio-station",
    "title": "Working as a Weekend DJ at a Hit Radio Station",
    "subtitle": "Workplace",
    "category": "reading-exercises",
    "theme": "Workplace",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about what it's like to work as a weekend radio DJ.",
    "readingcompTitle": "Working as a Weekend DJ at a Hit Radio Station",
    "topic": "Workplace",
    "image": "/Images/radio-dj.webp",
    "vocabulary": [
      "broadcast - sugároz",
      "playlist - lejátszási lista",
      "listeners - hallgatók",
      "advertisement - reklám",
      "microphone - mikrofon",
      "schedule - menetrend"
    ],
    "paragraphs": [
      {
        "text": [
          "<p><strong>Working as a Weekend DJ at a Hit Radio Station</strong></p>",
          "<p>I work as a weekend DJ at a popular hit radio station. My main job is to play music, talk to listeners, and keep the show entertaining. I also introduce songs, share news, and read advertisements on air.</p>",
          "<p>Each day, I start by preparing the playlist and checking the schedule. I also practice announcements and review any special segments for the show. During the broadcast, I talk to listeners, take song requests, and make sure the music flows smoothly. I sometimes interview guests or promote upcoming events.</p>",
          "<p>What I enjoy most about my job is the live interaction with listeners and the excitement of being on air. Every show is different, and I get to share music that I love while making people happy. It is also fun to work with a team of producers and sound engineers to make each broadcast successful.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main job of the weekend DJ?",
            "choices": [
              "A. To write music for the station",
              "B. To play music, talk to listeners, and keep the show entertaining",
              "C. To fix technical problems in the studio",
              "D. To sell advertising space"
            ]
          },
          {
            "number": "2",
            "question": "What does the DJ do before the show starts?",
            "choices": [
              "A. Take a break and relax",
              "B. Prepare the playlist, check the schedule, and practice announcements",
              "C. Interview listeners at home",
              "D. Deliver equipment to the studio"
            ]
          },
          {
            "number": "3",
            "question": "How does the DJ interact with listeners?",
            "choices": [
              "A. By sending emails after the show",
              "B. By talking live on air and taking song requests",
              "C. By writing letters to fans",
              "D. By posting on social media only"
            ]
          },
          {
            "number": "4",
            "question": "Who does the DJ work with to make the broadcast successful?",
            "choices": [
              "A. Marketing and sales",
              "B. Producers and sound engineers",
              "C. HR and legal teams",
              "D. Other DJs only"
            ]
          },
          {
            "number": "5",
            "question": "What does the DJ enjoy most about the job?",
            "choices": [
              "A. Listening to music at home",
              "B. Live interaction with listeners and sharing music",
              "C. Organizing office parties",
              "D. Working alone in the studio"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-working-in-it-billing-at-telekom",
    "title": "Working in IT Billing at Telekom",
    "subtitle": "Workplace",
    "category": "reading-exercises",
    "theme": "Workplace",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about what it's like to work in IT billing at Telekom.",
    "readingcompTitle": "Working in IT Billing at Telekom",
    "topic": "Workplace",
    "image": "/Images/telekom-billing.webp",
    "vocabulary": [
      "billing - számlázás",
      "system - rendszer",
      "clients - ügyfelek",
      "invoice - számla",
      "resolve - megold",
      "report - jelentés"
    ],
    "paragraphs": [
      {
        "text": [
          "<p><strong>Working in IT Billing at Telekom</strong></p>",
          "<p>I work in the IT billing department at Telekom. My main task is to manage the billing system, which includes creating invoices, checking payments, and solving any errors. I also support clients when they have questions about their bills.</p>",
          "<p>Each day starts by reviewing emails and system alerts. Sometimes I need to fix problems in the billing software, like incorrect charges or missing invoices. I also prepare reports for management to show how the billing system is performing.</p>",
          "<p>What I enjoy most about my job is that I learn new things every day. IT billing is complex, and I often work with colleagues from other departments, like customer service or finance, to make sure everything runs smoothly. It is rewarding to help clients understand their bills and resolve problems quickly.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main task of someone working in IT billing at Telekom?",
            "choices": [
              "A. Creating marketing campaigns",
              "B. Managing the billing system and invoices",
              "C. Selling new contracts",
              "D. Installing internet routers"
            ]
          },
          {
            "number": "2",
            "question": "What does the employee do at the beginning of the day?",
            "choices": [
              "A. Go to meetings with clients",
              "B. Review emails and system alerts",
              "C. Prepare presentations",
              "D. Update social media"
            ]
          },
          {
            "number": "3",
            "question": "Why does the employee sometimes fix problems in the billing software?",
            "choices": [
              "A. To create new invoices",
              "B. To correct errors like incorrect charges or missing invoices",
              "C. To train new employees",
              "D. To improve the office network"
            ]
          },
          {
            "number": "4",
            "question": "Which departments does the billing employee work with?",
            "choices": [
              "A. Customer service and finance",
              "B. Marketing and sales",
              "C. HR and legal",
              "D. Only the IT department"
            ]
          },
          {
            "number": "5",
            "question": "What does the employee enjoy most about their job?",
            "choices": [
              "A. Traveling for work",
              "B. Learning new things and solving problems",
              "C. Working alone",
              "D. Organizing office parties"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-working-as-an-account-manager-at-essity",
    "title": "Working as an Account Manager at Essity",
    "subtitle": "Workplace",
    "category": "reading-exercises",
    "theme": "Workplace",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about what it's like to work as an account manager at Essity.",
    "readingcompTitle": "Working as an Account Manager at Essity",
    "topic": "Workplace",
    "image": "/Images/tork-head.webp",
    "vocabulary": [
      "provides - biztosít",
      "hygiene - higiénia",
      "solutions - megoldások",
      "client - ügyfél",
      "improve - javít"
    ],
    "paragraphs": [
      {
        "text": [
          "<p><strong>Working as an Account Manager at Essity</strong></p>",
          "<p>I work as an account manager at Essity, a company that provides hygiene and health products. My job is to talk to clients, understand what they need, and help them choose the best products. These can include things like paper towels, soap dispensers, or medical supplies.</p>",
          "<p>Each day starts with checking emails and planning my meetings. I often visit customers to answer their questions and help solve any problems. Good communication is very important in this role. I also work with other teams at Essity, like marketing and logistics, to make sure everything runs smoothly.</p>",
          "<p>What I like most about my job is that every day is different. I get to meet many people and help them improve their workplaces. It feels good to know that our products make a real difference.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main job of an account manager at Essity?",
            "choices": [
              "A. To make health products",
              "B. To clean offices",
              "C. To help clients find the right products",
              "D. To deliver orders"
            ]
          },
          {
            "number": "2",
            "question": "What does the account manager do in the morning?",
            "choices": [
              "A. Visit clients",
              "B. Check emails and plan meetings",
              "C. Go to the warehouse",
              "D. Make new products"
            ]
          },
          {
            "number": "3",
            "question": "Why is communication important in this job?",
            "choices": [
              "A. To sell more products",
              "B. To travel more",
              "C. To solve problems and help clients",
              "D. To avoid work"
            ]
          },
          {
            "number": "4",
            "question": "Which teams does the account manager work with?",
            "choices": [
              "A. Only logistics",
              "B. Only marketing",
              "C. Marketing and logistics",
              "D. Sales and cleaning"
            ]
          },
          {
            "number": "5",
            "question": "What does the account manager enjoy most?",
            "choices": [
              "A. The office view",
              "B. Making products",
              "C. Meeting people and making a difference",
              "D. Working alone"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-account-manager-challenge",
    "title": "Handling a Difficult Client",
    "subtitle": "Workplace Challenge",
    "category": "reading-exercises",
    "theme": "Workplace Challenge",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read about how an account manager handled a difficult client.",
    "readingcompTitle": "Handling a Difficult Client",
    "topic": "Workplace Challenge",
    "image": "/Images/tork-head.webp",
    "vocabulary": [
      "handling - kezelése",
      "tough - nehéz",
      "delivery - szállítás",
      "demanding - igényes",
      "solution - megoldás",
      "expected - várt",
      "calm - nyugodt",
      "concerns - aggályok",
      "options - lehetőségek",
      "matched - illeszkedett",
      "patience - türelem",
      "respond - reagál",
      "respect - tisztelet"
    ],
    "paragraphs": [
      {
        "text": [
          "<p><strong>Handling a Difficult Client</strong></p>",
          "<p>Last month, I had a tough situation as an account manager at Essity. One of my clients was very demanding. He often called and sent long emails, asking for changes and updates every day. He wasn't happy with the delivery times and said the products were not what he expected.</p>",
          "<p>I stayed calm and listened carefully to his concerns. I spoke with our logistics team to speed up the delivery. I also offered different product options that better matched his needs. After a few days, things improved, and he started to trust me more.</p>",
          "<p>This experience taught me the importance of patience, good communication, and staying professional. Not every customer is easy, but if you show respect and try to solve the problem, most people will respond well.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What was the problem the account manager faced?",
            "choices": [
              "A. He lost a client",
              "B. A client was very demanding",
              "C. He had too many meetings",
              "D. He made a mistake with an order"
            ]
          },
          {
            "number": "2",
            "question": "How did the client act?",
            "choices": [
              "A. Calm and helpful",
              "B. Friendly and quiet",
              "C. Demanding and unhappy",
              "D. Late and confused"
            ]
          },
          {
            "number": "3",
            "question": "What did the account manager do to help?",
            "choices": [
              "A. He ignored the client",
              "B. He told the client to wait",
              "C. He called another company",
              "D. He listened, found new products, and talked to the logistics team"
            ]
          },
          {
            "number": "4",
            "question": "What happened after a few days?",
            "choices": [
              "A. The client left",
              "B. The problem got worse",
              "C. The client trusted the manager more",
              "D. The products were returned"
            ]
          },
          {
            "number": "5",
            "question": "What did the account manager learn?",
            "choices": [
              "A. Always change the product",
              "B. Some clients are not worth it",
              "C. Good communication and patience help solve problems",
              "D. Don’t talk to logistics"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-streaming-fatigue-too-much-of-a-good-thing",
    "title": "Streaming Fatigue: Too Much of a Good Thing?",
    "subtitle": "streaming",
    "category": "reading-exercises",
    "theme": "streaming",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about why too many streaming choices can be exhausting.",
    "readingcompTitle": "Streaming Fatigue: Too Much of a Good Thing?",
    "topic": "streaming",
    "image": "/Images/streamingfatigue1.webp",
    "vocabulary": [
      "streaming - online tartalom közvetítése",
      "fatigue - fáradtság, kimerültség",
      "overload - túlterheltség",
      "subscription - előfizetés",
      "binge - egyhuzamban sokat nézni/hallgatni",
      "content - tartalom",
      "decision-making - döntéshozatal",
      "cancel - lemondani",
      "curated - gondosan válogatott",
      "engaged - elkötelezett, figyelmes"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Streaming services have changed how we watch TV shows, movies, and listen to music. With platforms like Netflix and Spotify, we can enjoy content anytime, anywhere. But with so much choice, some people are feeling tired of it all. This is called <strong>streaming fatigue</strong>.</p>",
          "<p>Streaming fatigue happens when people feel overwhelmed by too many options. They spend more time browsing than watching or listening. Some even feel pressure to keep up with popular shows, which makes entertainment feel like work.</p>",
          "<p>There is also <strong>subscription fatigue</strong>. Many users now pay for multiple services, and managing them all can be confusing and expensive. As a result, some people are starting to cancel their subscriptions or take breaks.</p>",
          "<p>To fix this, some companies are offering <strong>curated</strong> content—smaller selections based on personal tastes. Music apps use playlists and recommendations to reduce decision-making stress and keep users <strong>engaged</strong>.</p>",
          "<p>Experts say the best way to avoid streaming fatigue is to take breaks, limit screen time, and only watch or listen when you truly want to—not just because everyone else is doing it.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is 'streaming fatigue'?",
            "choices": [
              "A. Getting bored of old movies",
              "B. Feeling tired from watching too many ads",
              "C. Being overwhelmed by too much content",
              "D. Having no access to streaming services"
            ]
          },
          {
            "number": "2",
            "question": "Why do some users cancel subscriptions?",
            "choices": [
              "A. They don't like TV shows anymore",
              "B. They forget their passwords",
              "C. They feel there are too few options",
              "D. They are tired of paying for many services"
            ]
          },
          {
            "number": "3",
            "question": "What does 'curated content' mean?",
            "choices": [
              "A. Content chosen randomly",
              "B. Ads shown on free platforms",
              "C. A limited selection based on user interests",
              "D. Only music and no videos"
            ]
          },
          {
            "number": "4",
            "question": "How do music apps help reduce decision stress?",
            "choices": [
              "A. By limiting access to music",
              "B. By offering free movies",
              "C. With playlists and smart suggestions",
              "D. By removing popular songs"
            ]
          },
          {
            "number": "5",
            "question": "What is one suggested way to avoid streaming fatigue?",
            "choices": [
              "A. Watch as many shows as possible",
              "B. Subscribe to every service",
              "C. Take breaks and limit screen time",
              "D. Follow every trending show"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-parents-at-a-football-match",
    "title": "Parents at a Football Match",
    "subtitle": "parents at a football match",
    "category": "reading-exercises",
    "theme": "parents at a football match",
    "level": "A2",
    "levelname": "A2 – Elementary",
    "time": "",
    "description": "Read a dialogue between two parents at a football match.", "readingcompTitle": "Parents at a Football Match",
    "topic": "parents at a football match",
    "image": "/Images/parents-football-match1.webp",
    "vocabulary": [
      "match - mérkőzés",
      "referee - játékvezető",
      "goal - gól",
      "defender - hátvéd",
      "midfielder - középpályás",
      "striker - csatár",
      "penalty - büntető",
      "substitute - cserejátékos",
      "coach - edző",
      "foul - szabálytalanság",
      "score a goal - loni egy gol"
    ],
    "paragraphs": [
      {
        "text": "<p><strong>Sarah:</strong> Hi, I’m Sarah. Is your child playing today?</p><p><strong>Mark:</strong> Yes! My son, Jake, is number 10. He plays as a striker. What about yours?</p><p><strong>Sarah:</strong> My daughter, Emily, is number 5. She’s a defender. It’s her first season playing.</p><p><strong>Mark:</strong> That’s great! Jake loves scoring goals, but defenders are so important to the team.</p><p><strong>Sarah:</strong> Yes, Emily is learning how to tackle and pass better. The coach says she’s improving.</p><p><strong>Mark:</strong> The referee is blowing the whistle… Oh no, a penalty for the other team!</p><p><strong>Sarah:</strong> That looked like a soft foul to me. But let’s see if our goalkeeper can save it.</p><p><strong>Mark:</strong> Fingers crossed! So, does Emily enjoy playing?</p><p><strong>Sarah:</strong> She loves it! She’s made so many friends on the team. How about Jake?</p><p><strong>Mark:</strong> He’s always been crazy about football. He even watches matches every weekend!</p><p>Sarah: That’s dedication! Oh, look—Emily just scored a goal!</p><p><strong>Mark:</strong> Nice one! This is turning into a really exciting match.</p>",
        "questions": [
          {
            "number": "1",
            "question": "What position does Jake play?",
            "choices": [
              "A. Defender",
              "B. Midfielder",
              "C. Striker",
              "D. Goalkeeper"
            ]
          },
          {
            "number": "2",
            "question": "What is Emily learning to improve?",
            "choices": [
              "A. Scoring goals",
              "B. Tackling and passing",
              "C. Playing in midfield",
              "D. Running faster"
            ]
          },
          {
            "number": "3",
            "question": "Why is there a penalty?",
            "choices": [
              "A. The goalkeeper touched the ball outside the box",
              "B. The referee stopped the game",
              "C. There was a foul",
              "D. The coach called for a break"
            ]
          },
          {
            "number": "4",
            "question": "How does Jake feel about football?",
            "choices": [
              "A. He watches matches every weekend",
              "B. He finds it boring",
              "C. He prefers basketball",
              "D. He only plays for fun"
            ]
          },
          {
            "number": "5",
            "question": "What does Sarah think about the penalty decision?",
            "choices": [
              "A. She thinks it was a fair decision",
              "B. She thinks it was a soft foul",
              "C. She agrees with the referee",
              "D. She is happy about it"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-talking-about-your-children",
    "title": "Talking About Your Children",
    "subtitle": "people description",
    "category": "reading-exercises",
    "theme": "people description",
    "level": "A2",
    "levelname": "A2 – Elementary",
    "time": "",
    "description": "Read a dialogue between colleagues talking about their children.",
    "readingcompTitle": "Talking About Your Children",
    "topic": "people description",
    "image": "/Images/talkingchildren1.webp",
    "vocabulary": [
      "join - csatlakozni",
      "kind - kedves",
      "especially - különösen",
      "colleague - kolléga",
      "getting to know - megismerni",
      "tall - magas",
      "curly - göndör",
      "straight - egyenes",
      "hobby - hobbi",
      "curious - kíváncsi"
    ],
    "paragraphs": [
      {
        "text": "<p>Anna: Hi, I’m Anna. I just joined the company last week. It’s nice to meet you!</p><p>James: Hi Anna, I’m James. Welcome to the team! How are you finding it so far?</p><p>Anna: Everyone has been very kind. I’m still getting to know the work, but it’s been good. How long have you been here?</p><p>James: About three years now. It’s a great place to work. So, do you have any children?</p><p>Anna: Yes, I do! I have two daughters. Lily is 10, and Emma is 7. What about you?</p><p>James: I have one son, Alex. He’s 8. Does Lily like school?</p><p>Anna: She loves it, especially art and music. Emma is more into sports. What about Alex?</p><p>James: He’s very active, too. He loves football, and he’s starting to learn how to play the piano. What do your daughters look like?</p><p>Anna: Lily is tall and has straight brown hair. Emma is shorter and has curly blond hair. And Alex?</p><p>James: He has short dark hair and big brown eyes. He’s very curious about everything. It’s fun to watch him learn new things!</p>",
        "questions": [
          {
            "number": "1",
            "question": "When did Anna join the company?",
            "choices": [
              "A. Last week",
              "B. Three years ago",
              "C. A month ago",
              "D. Last year"
            ]
          },
          {
            "number": "2",
            "question": "How many daughters does Anna have?",
            "choices": [
              "A. One",
              "B. Two",
              "C. Three",
              "D. None"
            ]
          },
          {
            "number": "3",
            "question": "What is Emma interested in?",
            "choices": [
              "A. Art and music",
              "B. Football",
              "C. Sports",
              "D. Playing piano"
            ]
          },
          {
            "number": "4",
            "question": "What does Alex look like?",
            "choices": [
              "A. He has blond hair and blue eyes.",
              "B. He has straight brown hair.",
              "C. He has short dark hair and big brown eyes.",
              "D. He has curly blond hair."
            ]
          },
          {
            "number": "5",
            "question": "What instrument is Alex starting to learn?",
            "choices": [
              "A. Guitar",
              "B. Piano",
              "C. Violin",
              "D. Drums"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-a-meeting-with-a-boss",
    "title": "A Meeting with a Boss",
    "subtitle": "health",
    "category": "reading-exercises",
    "theme": "health",
    "level": "A2",
    "levelname": "A2 – Elementary",
    "time": "",
    "description": "Read a dialogue between a boss and an account manager.",
    "readingcompTitle": "A Meeting with a Boss",
    "topic": "health",
    "image": "/Images/officerules1.webp",
    "vocabulary": [
      "product - termék",
      "client - ügyfél",
      "introduce - bemutat",
      "place (an order) - rendelést lead",
      "sample - minta",
      "target - cél",
      "follow up - utánkövet",
      "deadline - határidő",
      "meeting - találkozó",
      "sales - értékesítés",
      "work - munka",
      "team - csapat",
      "happy - boldog, elégedett",
      "problem - probléma",
      "solution - megoldás",
      "order - rendelés",
      "review - áttekint",
      "report - jelentés"
    ],
    "paragraphs": [
      {
        "text": "<p>Boss:  Good morning! How are you today?</p><p>Account Manager:  I’m fine, thank you. How about you?</p><p>Boss:  I’m good, thanks. Let’s talk about your work with Tork this month.</p><p>Account Manager:  Sure! I visited five clients last week. They are happy with the products.</p><p>Boss:  That’s great to hear! Did you introduce the new Tork hand towels?</p><p>Account Manager:  Yes, I did. Three clients placed orders for the new hand towels.</p><p>Boss:  Excellent! What about the others?</p><p>Account Manager:  They need more time to decide. One client asked for a sample.</p><p>Boss:  Good. Make sure they get the sample this week.</p><p>Account Manager:  I will.</p><p>Boss:  And how are the sales targets for this month?</p><p>Account Manager:  We are at 70% of the target.</p><p>Boss:  That’s not bad, but we need to reach 100%. Can you follow up with more clients?</p><p>Account Manager:  Yes, I will call them today and tomorrow.</p><p>Boss:  Perfect. Let me know if you need help.</p><p>Account Manager:  Thank you, Mr. Brown.</p><p>Boss:  Keep up the good work!</p><p>Account Manager:  I will. Thank you!</p>",
        "questions": [
          {
            "number": "1",
            "question": "How many clients did the account manager visit last week?",
            "choices": [
              "A. Five",
              "B. Three",
              "C. Two",
              "D. One"
            ]
          },
          {
            "number": "2",
            "question": "What product did the account manager introduce?",
            "choices": [
              "A. New Tork hand towels",
              "B. A sample of hand soap",
              "C. Paper towels",
              "D. Cleaning supplies"
            ]
          },
          {
            "number": "3",
            "question": "How many clients placed orders for the new product?",
            "choices": [
              "A. One",
              "B. Two",
              "C. Three",
              "D. Five"
            ]
          },
          {
            "number": "4",
            "question": "What percentage of the sales target has the team reached?",
            "choices": [
              "A. 50%",
              "B. 70%",
              "C. 80%",
              "D. 100%"
            ]
          },
          {
            "number": "5",
            "question": "What did the boss tell the account manager to do about clients who need more time?",
            "choices": [
              "A. Offer a discount",
              "B. Follow up and give them samples",
              "C. Ignore them",
              "D. Wait for their call"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-a-meeting-with-a-distributor",
    "title": "A Meeting with a Distributor",
    "subtitle": "health",
    "category": "reading-exercises",
    "theme": "health",
    "level": "A2",
    "levelname": "A2 – Elementary",
    "time": "",
    "description": "Read a dialogue between an account manager and a distributor.",
    "readingcompTitle": "A Meeting with a Distributor",
    "topic": "health",
    "image": "/Images/officerules2.webp",
    "vocabulary": [
      "review - áttekint",
      "work - munka",
      "product - termék",
      "customer - vásárló",
      "introduce - bemutat",
      "order - rendelés",
      "feedback - visszajelzés",
      "sample - minta",
      "test - teszt",
      "target - cél",
      "follow up - utánkövet",
      "support - támogat",
      "material - anyag",
      "month - hónap",
      "sales - értékesítés"
    ],
    "paragraphs": [
      {
        "text": "<p>Account Manager: Good morning! How are you today?</p><p>Distributor: Good morning! I’m fine, thank you. And you?</p><p>Account Manager: I’m doing well, thank you. Let’s review your work with Tork this month.</p><p>Distributor: Sure! Our customers are showing interest in the new products.</p><p>Account Manager: That’s great to hear! Have you introduced the new Tork hand towels to your top customers?</p><p>Distributor: Yes, I spoke to three of them. Two decided to place orders right away.</p><p>Account Manager: Excellent! What feedback did you get from the others?</p><p>Distributor: One customer is interested but asked for a sample to test first.</p><p>Account Manager: Good idea. I’ll make sure the sample is sent to them this week.</p><p>Distributor: Thank you. I’m confident they will like it.</p><p>Account Manager: I hope so too. How are we doing with sales targets this month?</p><p>Distributor: We have reached 65% so far.</p><p>Account Manager: That’s a solid start, but we need to push for 100%. Can you follow up with more customers?</p><p>Distributor: Yes, I will contact them today and tomorrow.</p><p>Account Manager: Great! If you need help or materials, let me know.</p><p>Distributor: I will. Thanks for your support!</p><p>Account Manager: Keep up the good work!</p><p>Distributor: Thank you! I will.</p>",
        "questions": [
          {
            "number": "1",
            "question": "How many customers decided to place orders for the new Tork hand towels?",
            "choices": [
              "A. One",
              "B. Two",
              "C. Three",
              "D. Four"
            ]
          },
          {
            "number": "2",
            "question": "What did one customer ask for before making a decision?",
            "choices": [
              "A. A brochure",
              "B. A discount",
              "C. A sample",
              "D. A meeting"
            ]
          },
          {
            "number": "3",
            "question": "What is the distributor's sales progress this month?",
            "choices": [
              "A. 50%",
              "B. 65%",
              "C. 75%",
              "D. 100%"
            ]
          },
          {
            "number": "4",
            "question": "Who will send the sample to the customer?",
            "choices": [
              "A. The distributor",
              "B. The account manager",
              "C. The customer",
              "D. A delivery service"
            ]
          },
          {
            "number": "5",
            "question": "What does the account manager offer to provide if needed?",
            "choices": [
              "A. More time",
              "B. Discounts",
              "C. Help or materials",
              "D. New products"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-discussing-yesterday-s-activities",
    "title": "Discussing Yesterday's Activities",
    "subtitle": "business conversation",
    "category": "reading-exercises",
    "theme": "business conversation",
    "level": "A2",
    "levelname": "A2 – Elementary",
    "time": "",
    "description": "Read a dialogue about what a distributor did yesterday.",
    "readingcompTitle": "Discussing Yesterday's Activities",
    "topic": "business conversation",
    "image": "/Images/yesterdaymeeting1.webp",
    "vocabulary": [
      "shipment - szállítmány",
      "order - rendelés",
      "deliver - kiszállítani",
      "meeting - találkozó",
      "warehouse - raktár",
      "check - ellenőrizni",
      "schedule - ütemezés",
      "customer - ügyfél",
      "confirm - megerősíteni",
      "delay - késedelem"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Account Manager: Good morning, Peter! How was your day yesterday?</p>",
          "<p>Distributor: Good morning! It was quite busy. I had a lot to do.</p>",
          "<p>Account Manager: Oh, really? What did you work on?</p>",
          "<p>Distributor: First, I checked the warehouse to make sure all shipments were ready.</p>",
          "<p>Account Manager: That sounds important. Did everything go as planned?</p>",
          "<p>Distributor: Mostly, yes. One order had a small delay, but we managed to fix it quickly.</p>",
          "<p>Account Manager: That’s good to hear. Did you have any meetings?</p>",
          "<p>Distributor: Yes, I met with a customer in the afternoon to confirm their next order.</p>",
          "<p>Account Manager: And was everything fine with their order?</p>",
          "<p>Distributor: Yes, they were happy with the schedule. We will deliver on Friday.</p>",
          "<p>Account Manager: Great job! Let’s keep everything on track.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What did the distributor do first yesterday?",
            "choices": [
              "A. Had a meeting",
              "B. Checked the warehouse",
              "C. Delivered an order",
              "D. Spoke to a customer"
            ]
          },
          {
            "number": "2",
            "question": "What problem did the distributor face?",
            "choices": [
              "A. A customer canceled an order",
              "B. There was a delay with one shipment",
              "C. The warehouse was closed",
              "D. They lost an order"
            ]
          },
          {
            "number": "3",
            "question": "When will the next order be delivered?",
            "choices": [
              "A. Monday",
              "B. Wednesday",
              "C. Friday",
              "D. Next week"
            ]
          },
          {
            "number": "4",
            "question": "Who did the distributor meet with in the afternoon?",
            "choices": [
              "A. A warehouse worker",
              "B. The account manager",
              "C. A customer",
              "D. A delivery driver"
            ]
          },
          {
            "number": "5",
            "question": "How did the customer feel about the schedule?",
            "choices": [
              "A. They were happy",
              "B. They wanted a faster delivery",
              "C. They were confused",
              "D. They canceled the order"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-following-up-on-an-order",
    "title": "Following Up on an Order",
    "subtitle": "business conversation",
    "category": "reading-exercises",
    "theme": "business conversation",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read a dialogue between an account manager and a client about an order.",
    "readingcompTitle": "Following Up on an Order",
    "topic": "business conversation",
    "image": "/Images/officerules3.webp",
    "vocabulary": [
      "order - rendelés",
      "delivery - kiszállítás",
      "install - telepíteni",
      "quality - minőség",
      "feedback - visszajelzés",
      "improve - javítani",
      "logistics - logisztika",
      "receive - megkapni",
      "process - folyamat",
      "service - szolgáltatás"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Anna (Account Manager): Good morning, Mr. Smith. How have you been?</p>",
          "<p>Mr. Smith (Client): Good morning, Anna. I’ve been well, thank you. How about you?</p>",
          "<p>Anna: I’m doing well too. I wanted to check in and see if you’ve received the latest order from us.</p>",
          "<p>Mr. Smith: Yes, we’ve just received it this morning. Everything looks good so far.</p>",
          "<p>Anna: That’s great to hear! Have you had a chance to test the new paper dispenser yet?</p>",
          "<p>Mr. Smith: Not yet, but we’ve installed it in the restrooms. Our team has already noticed the difference in quality.</p>",
          "<p>Anna: I’m glad to hear that. We’ve improved the design based on customer feedback.</p>",
          "<p>Mr. Smith: That’s good to know. I’ve spoken with my team, and they’ve said the new hand towels feel softer.</p>",
          "<p>Anna: Yes, we’ve changed the material to make them more absorbent. Have you had any issues with the delivery process?</p>",
          "<p>Mr. Smith: No, everything has arrived on time. Your logistics team has done a great job.</p>",
          "<p>Anna: I appreciate that! If you need anything else, just let me know.</p>",
          "<p>Mr. Smith: Thank you, Anna. We’ve always been happy with your service.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What did Anna want to check?",
            "choices": [
              "A. If Mr. Smith received the order",
              "B. If the restrooms are clean",
              "C. If the team is working",
              "D. If the towels are soft"
            ]
          },
          {
            "number": "2",
            "question": "When did Mr. Smith receive the order?",
            "choices": [
              "A. Yesterday",
              "B. This morning",
              "C. Last week",
              "D. He hasn’t received it yet"
            ]
          },
          {
            "number": "3",
            "question": "Where was the new paper dispenser installed?",
            "choices": [
              "A. In the kitchen",
              "B. In the restrooms",
              "C. In the warehouse",
              "D. In the office"
            ]
          },
          {
            "number": "4",
            "question": "How has the design been improved?",
            "choices": [
              "A. It is cheaper",
              "B. It is based on customer feedback",
              "C. It is smaller",
              "D. It has new colors"
            ]
          },
          {
            "number": "5",
            "question": "What did Mr. Smith say about the logistics team?",
            "choices": [
              "A. They did a great job",
              "B. They made a mistake",
              "C. They were late",
              "D. They forgot the order"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-quarterly-sales-review",
    "title": "Quarterly Sales Review",
    "subtitle": "business discussion",
    "category": "reading-exercises",
    "theme": "business discussion",
    "level": "B1",
    "levelname": "B1 – Intermediate",
    "time": "",
    "description": "Read a dialogue about quarterly sales results.",
    "readingcompTitle": "Quarterly Sales Review",
    "topic": "business discussion",
    "image": "/Images/qsalesreview1.webp",
    "vocabulary": [
      "quarter - negyedév",
      "results - eredmény",
      "target - cél",
      "increase - növekedés",
      "market share - piaci részesedés",
      "suggest - javasol",
      "challenges - kihívás",
      "performance - teljesítmény",
      "strategies - stratégia",
      "improve - javul",
      "revenue - bevétel",
      "recently - mostanaban",
      "reduced - csökkent",
      "feedback - visszajelzés",
      "review - átnéz",
      "forecast - előrejelzés"
    ],
    "paragraphs": [
      {
        "text": "<p><strong>David</strong>: Hi, Julia. Thanks for joining the meeting. How are you?</p><p><strong>Julia</strong>: Hi, David. I'm good, thanks. Looking forward to discussing the quarterly sales results.</p><p><strong>David</strong>: Yes, let’s get started. This quarter, we saw an increase in revenue by 8%, which is great. However, we didn’t fully meet our targets in the Central region.</p><p><strong>Julia</strong>: That’s true. We faced some challenges with supply chain delays, which affected sales. But overall, our market share has grown.</p><p><strong>David</strong>: That’s a positive sign. What strategies do you suggest for improving sales next quarter?</p><p><strong>Julia</strong>: I think we should focus more on customer feedback. Some clients mentioned they need faster delivery times.</p><p><strong>David</strong>: That makes sense. We could work with the logistics team to improve that. Also, we should look at our pricing strategy.</p><p><strong>Julia</strong>: Agreed. A competitor recently reduced prices, which may have impacted us. We need to stay competitive.</p><p><strong>David</strong>: Good point. I’ll share this with senior management. Let’s schedule a follow-up next month to review our progress.</p><p><strong>Julia</strong>: Sounds great. Thanks, David!</p>",
        "questions": [
          {
            "number": "1",
            "question": "By how much did revenue increase this quarter?",
            "choices": [
              "A. 5%",
              "B. 8%",
              "C. 10%",
              "D. 12%"
            ]
          },
          {
            "number": "2",
            "question": "Why did the Central region not meet its targets?",
            "choices": [
              "A. Low customer interest",
              "B. Supply chain delays",
              "C. High competition",
              "D. Reduced prices"
            ]
          },
          {
            "number": "3",
            "question": "What does Julia suggest to improve sales?",
            "choices": [
              "A. Increase advertising budget",
              "B. Offer discounts",
              "C. Focus on customer feedback",
              "D. Hire more sales representatives"
            ]
          },
          {
            "number": "4",
            "question": "What external factor affected Essity's sales?",
            "choices": [
              "A. A new government regulation",
              "B. A competitor reducing prices",
              "C. A change in leadership",
              "D. A global economic downturn"
            ]
          },
          {
            "number": "5",
            "question": "When will Julia and David meet again?",
            "choices": [
              "A. Next week",
              "B. Next month",
              "C. In three months",
              "D. At the end of the year"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-new-workplace-rule",
    "title": "New Workplace Rule",
    "subtitle": "Company Policy",
    "category": "reading-exercises",
    "theme": "Company Policy",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about a controversial new workplace rule and whether it's legal.",
    "readingcompTitle": "New Workplace Rule",
    "topic": "Company Policy",
    "image": "/Images/companypolicy1.webp",
    "vocabulary": [
      "insubordinates - disobedient employees",
      "lawsuit - legal action",
      "notice - warning"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Entrepreneur Criticizes New Workplace Rule, Says It Might Be Illegal</p>",
          "<p>A boss has been criticized for making a new rule at work that some people are calling \"crazy.\" Entrepreneur Ben Askins read the rule, sent by an anonymous worker, and said it was unfair. The new rule says that all workers must give three months' notice if they want to leave the company. The boss said this is because it takes that long to train someone new.</p>",
          "<p>In addition, if a worker says they are leaving, their pay will be reduced by $6 per hour. The boss explained that by giving three months' notice, the workers are agreeing to the pay cut. He said, \"Since you will be leaving, your reduction in pay will be no issue. That's how this works from now on. You can thank your insubordinates for this,\" he added.</p>",
          "<p>The new rule also says that workers who are leaving will have to do an extra 30 hours of overtime each week until the new person is fully trained.</p>",
          "<p>Ben Askins said, \"This will cause a lot of workers to leave.\" Others agreed and said that this rule might lead to a lawsuit.</p>",
          "<p><strong>Is this legal in Australia?</strong></p>",
          "<p>In Australia, bosses are allowed to lower pay in certain situations, but not like this. For notice periods, how much notice you need to give depends on how long you’ve worked at the company. In Australia, workers need to give:</p>",
          "<ul><li>one week if they've worked less than a year,</li><li>two weeks if they’ve worked one to three years,</li><li>three weeks if they’ve worked three to five years,</li><li>and four weeks if they’ve worked more than five years.</li></ul>",
          "<p>Workers who are over 45 years old and have been with the company for more than two years must be given one extra week of notice.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Do you think it’s fair for a boss to require three months’ notice to quit a job?",
            "choices": [
              "A. Yes, it's fair if training takes that long",
              "B. No, it's too long",
              "C. It depends on the job",
              "D. I'm not sure"
            ]
          },
          {
            "number": "2",
            "question": "How would you feel if your pay was reduced after you said you were quitting?",
            "choices": [
              "A. I would understand the decision",
              "B. I would feel angry or disrespected",
              "C. I wouldn't mind",
              "D. I would quit immediately"
            ]
          },
          {
            "number": "3",
            "question": "Do you agree with Ben Askins that this rule might cause more people to leave the company?",
            "choices": [
              "A. Yes, people won’t accept it",
              "B. No, people will follow the rule",
              "C. Maybe, it depends on other factors",
              "D. I don’t have an opinion"
            ]
          },
          {
            "number": "4",
            "question": "Is it reasonable to ask workers to do 30 hours of extra overtime while training someone?",
            "choices": [
              "A. Yes, if they’re paid for it",
              "B. No, that’s too much",
              "C. Only if the worker agrees",
              "D. It depends on the situation"
            ]
          },
          {
            "number": "5",
            "question": "Do you think this workplace rule should be legal?",
            "choices": [
              "A. Yes, companies should have freedom",
              "B. No, it’s too harsh on employees",
              "C. Only if workers agree in advance",
              "D. I’m not sure"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-air-traffic-delays",
    "title": "Air Traffic Delays",
    "subtitle": "air traffic delays",
    "category": "reading-exercises",
    "theme": "air traffic delays",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about why air traffic delays are getting worse in Europe.",
    "readingcompTitle": "Air Traffic Delays",
    "topic": "air traffic delays",
    "image": "/Images/airdelay1.webp",
    "vocabulary": [
      "disruption - break in service",
      "saturated - filled to capacity",
      "forecast - prediction",
      "posed - presented",
      "staff - employ",
      "constrained - limited",
      "volatile - unstable",
      "strain - pressure",
      "eye-watering - extremely high",
      "congested - overcrowded",
      "adhere - stick to",
      "part and parcel of - an essential part of"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>Air traffic control delays and disruption across Europe could be worse this summer than last, senior aviation figures have warned.</p>",
          "<p>The number of summer flights are forecast to exceed last year by more than 5% in what Eurocontrol head of air traffic management operations Steven Moore described as “a highly saturated network where any issue becomes a network problem”.</p>",
          "<p>Eurocontrol is the European air navigation safety body and at the core of managing air traffic disruption.</p>",
          "<p>Speaking on a Eurocontrol webinar, Moore noted air traffic in June to August last year was up 4.7% on the previous summer but delays increased by 47%, with a small number of air navigation service providers responsible for most delays.</p>",
          "<p>He said a forecast 5.2% increase in traffic this summer posed “a significant challenge in a constrained network” and warned: “It’s plain we won’t have a summer without delays.”</p>",
          "<p>Geneva airport chief operating officer Giovanni Russo agreed, saying: “We know there will be delays and disruption.”</p>",
          "<p>He insisted: “All players need to stick to their plans. That is most important. [But] plan according to what is possible [and] staff correctly.”</p>",
          "<p>However, Russo noted: “Days almost never go to plan. Something always goes wrong.”</p>",
          "<p>Jesus Garcia, air navigation systems coordinator at Spanish air traffic management body Enaire, said: “We’re suffering a perfect storm with unpredictable and volatile traffic flow. Growth after Covid has not been uniform.</p>",
          "<p>In addition, we’re suffering geopolitical conflict with increased miliary action and flight restrictions and increasing extreme weather phenomena. Together, it places a strain on capacity.”</p>",
          "<p>He argued: “It’s a multi-phased problem, not [due to] a single problem or actor.”</p>",
          "<p>Garcia also pointed out: “We need more air traffic controllers. [But] the problem with the availability of air traffic controllers is complex – training takes two to three years and recruitment is based on long-term planning not short-term demand. Recruitment for 2024 was based on 2021 traffic which was expected to be 15% down on 2019.”</p>",
          "<p>EasyJet chief operating officer David Morgan reported a 41% increase in delays in the first two months of this year, describing this as a “worrying sign we’re moving in the wrong direction” and noted: “The southeast axis across Europe remains one of the biggest concerns.”</p>",
          "<p>He said: “I can hardly remember a year when we’ve not had staffing issues. The cost of delays is eye-watering – it’s costing hundreds of millions to airlines.”</p>",
          "<p>Morgan said easyJet was looking at whether “we can fly around the most congested areas” and pointed out: “Everyone likes to talk about realistic schedules, [but] it’s not always easy. Airlines must adhere to a schedule or risk losing [airport] slots.”</p>",
          "<p>He acknowledged: “Delays are getting worse. They are part and parcel of summer, but staffing delays should be exceptional. Every year in peak summer period we’re struggling with staffing.”</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "By how much is summer air traffic expected to increase this year compared to last year?",
            "choices": [
              "A. 4.7%",
              "B. 5.2%",
              "C. 15%",
              "D. 41%"
            ]
          },
          {
            "number": "2",
            "question": "According to Steven Moore, what kind of network creates widespread problems when there is an issue?",
            "choices": [
              "A. A low-traffic network",
              "B. A rural network",
              "C. A highly saturated network",
              "D. A domestic-only network"
            ]
          },
          {
            "number": "3",
            "question": "What did Giovanni Russo say about the reliability of daily plans?",
            "choices": [
              "A. Days usually go according to plan",
              "B. Most delays are weather-related",
              "C. Days almost never go to plan",
              "D. There is always enough staff to follow plans"
            ]
          },
          {
            "number": "4",
            "question": "What is one of the reasons Jesus Garcia gave for the current strain on air traffic control capacity?",
            "choices": [
              "A. A decline in airline safety standards",
              "B. A sharp fall in post-Covid travel",
              "C. Uniform traffic growth across Europe",
              "D. Geopolitical conflicts and extreme weather"
            ]
          },
          {
            "number": "5",
            "question": "What challenge is associated with hiring more air traffic controllers?",
            "choices": [
              "A. They must relocate internationally",
              "B. Most are not interested in the job",
              "C. Training takes 2–3 years and depends on long-term planning",
              "D. There are no training programs available"
            ]
          },
          {
            "number": "6",
            "question": "What did David Morgan from easyJet describe as 'eye-watering'?",
            "choices": [
              "A. Weather conditions in southern Europe",
              "B. The cost of delays for airlines",
              "C. The cost of recruiting air traffic controllers",
              "D. Fuel prices for summer flights"
            ]
          },
          {
            "number": "7",
            "question": "What is the 'southeast axis' across Europe considered to be?",
            "choices": [
              "A. A quiet and well-managed region",
              "B. The most reliable air corridor",
              "C. One of the biggest concerns for delays",
              "D. An area with extra controller capacity"
            ]
          },
          {
            "number": "8",
            "question": "Why do airlines need to stick to schedules even when delays occur?",
            "choices": [
              "A. To ensure passenger comfort",
              "B. To avoid paying fines to Eurocontrol",
              "C. To prevent losing valuable airport slots",
              "D. Because air traffic controllers require it"
            ]
          },
          {
            "number": "9",
            "question": "Opinion: Do you think air travel will ever become delay-free during summer seasons? Why or why not?",
            "choices": []
          },
          {
            "number": "10",
            "question": "Discussion: In your opinion, what is the most effective way to reduce delays in European airspace?",
            "choices": []
          }
        ]
      }
    ]
  },
  {    "id": "reading-caffeine-the-workplace-drug-of-choice",
    "title": "Caffeine: The Workplace Drug of Choice",
    "subtitle": "Food",
    "category": "reading-exercises",
    "theme": "Food",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about the benefits and risks of caffeine.",
    "readingcompTitle": "Caffeine: The Workplace Drug of Choice",
    "topic": "Food",
    "image": "/Images/caffeine1.webp",
    "vocabulary": [
      "stimulant - élénkítőszer",
      "reduce - csökkent",
      "nervousness - idegesség",
      "tiredness - fáradtság",
      "withdrawal - elvonás",
      "severe - súlyos",
      "symptom - tünet",
      "dependence - függőség",
      "function - működik",
      "benefit - előny",
      "drawback - hátrány"
    ],
    "paragraphs": [
      {
        "text": "<p>Caffeine is the most widely used stimulant in the world. It is most common in coffee, but is also found in tea, sodas, and energy drinks. You can even find it in chewing gum, body creams, and bath bars. While caffeine gives us energy and keeps us awake, it also has some important medical uses.</p><h4>Medical Uses of Caffeine</h4><p>Caffeine can help premature babies who stop breathing (called apnea). It stabilizes their oxygen levels and helps them breathe better. In adults, caffeine improves energy, focus, and problem-solving skills. It also reduces mental and physical tiredness.</p><h4>The Problems with Caffeine</h4><p>Too much caffeine can cause problems, especially for people with anxiety. It can increase nervousness, fear, and cause trembling. If you take medicine for anxiety or panic, caffeine can make it less effective. Pregnant women should also limit caffeine because it hasn’t been studied enough for safety.</p><h4>Caffeine in Foods and Drinks</h4><p>Caffeine is often listed on drink labels, like soda or energy drinks. However, some products contain hidden caffeine. For example, guarana, a plant found in Brazil, has high amounts of caffeine and is often added to energy drinks. If you drink something with both coffee and guarana, you may be getting double the caffeine without realizing it.</p><h4>Caffeine Withdrawal</h4><p>If you stop using caffeine suddenly, you may feel withdrawal symptoms like headaches, tiredness, and a low mood. In more severe cases, people feel sick or very drowsy for a few days. Psychological symptoms, like feeling sad or unmotivated, can last longer.</p><h4>Caffeine Dependence</h4><p>Some people rely on caffeine so much that they can’t function without it. A study showed that caffeine withdrawal can lead to missing work, making mistakes, or feeling so tired you need to stay in bed. This shows that while caffeine has benefits, it also has real drawbacks.</p>",
        "questions": [
          {
            "number": "1",
            "question": "What is the most common source of caffeine mentioned in the text?",
            "choices": [
              "A. Water",
              "B. Coffee",
              "C. Fruits",
              "D. Tea"
            ],
            "correctAnswer": "B. Coffee"
          },
          {
            "number": "2",
            "question": "How does caffeine help preterm babies?",
            "choices": [
              "A. It increases their sleep time",
              "B. It helps them breathe better",
              "C. It enhances their brain development",
              "D. It causes breathing problems"
            ],
            "correctAnswer": "B. It helps them breathe better"
          },
          {
            "number": "3",
            "question": "What are some benefits of caffeine for adults?",
            "choices": [
              "A. It causes tiredness and low energy",
              "B. It improves focus and reduces fatigue",
              "C. It increases anxiety and stress",
              "D. It disrupts breathing and sleep"
            ],
            "correctAnswer": "B. It improves focus and reduces fatigue"
          },
          {
            "number": "4",
            "question": "What medication can caffeine interfere with?",
            "choices": [
              "A. Antibiotics",
              "B. Blood pressure medicine",
              "C. Benzodiazepines",
              "D. Pain relievers"
            ],
            "correctAnswer": "C. Benzodiazepines"
          },
          {
            "number": "5",
            "question": "Why is it hard to measure how much caffeine you consume?",
            "choices": [
              "A. Everyone processes caffeine the same way",
              "B. Caffeine is not in sodas or energy drinks",
              "C. Hidden sources of caffeine like guarana",
              "D. Caffeine is only found in tea and coffee"
            ],
            "correctAnswer": "C. Hidden sources of caffeine like guarana"
          },
          {
            "number": "6",
            "question": "What is one symptom of caffeine withdrawal?",
            "choices": [
              "A. Increased energy and focus",
              "B. Headache and tiredness",
              "C. Euphoria and alertness",
              "D. Restlessness and motivation"
            ],
            "correctAnswer": "B. Headache and tiredness"
          }
        ]
      }
    ]
  },
  {    "id": "reading-plans-are-stupid",
    "title": "Plans Are Stupid",
    "subtitle": "General",
    "category": "reading-exercises",
    "theme": "General",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read an opinion piece about whether making plans is useful.",
    "readingcompTitle": "Plans Are Stupid",
    "topic": "General",
    "image": "/Images/plansstuid1.webp",
    "vocabulary": [
      "encountered - met",
      "procrastinate - to delay doing something",
      "consequences - results of an action or decision",
      "faulty - not working or correct",
      "implies - suggests something without saying it directly",
      "accomplish - to succeed in doing something",
      "to put it bluntly - to say something very honestly",
      "routine - a regular way of doing things",
      "plays out - happen",
      "achieve - to reach a goal successfully",
      "frankly - honestly",
      "adjust - change something to make it better"
    ],
    "paragraphs": [
      {
        "text": "<p>Do you make plans? Do you think plans are stupid? I have known many who do.</p><p>As a teacher, parent and friend, I have encountered many who think that plans are unnecessary or just, plain stupid. Of course, most of the people who say plans are stupid are not talking about plans for everyday routine items, such as getting dressed, preparing meals, etc. No, they are saying that planning your life is useless.</p><p>Why plan when you don’t have any real control over life? Why plan when things never really turn out like you planned? I’ll admit that in my own life, things never turn out exactly like I plan them. So why plan?</p><p>What do I do that never works out? Well, I plan by making a goal, breaking it down into “pieces of action,” all designed to help me accomplish my goal over an estimated amount of time. And, then? Well, to put it bluntly, it doesn’t happen. So why plan?</p><p>What really happened with my goal and/or plan? Usually, when I accomplish my “much adjusted goal” and look back, I notice that some things took longer, some shorter, and frankly, some were not completed and didn’t need to be. So, why have a plan?</p><p>A plan is still important because, in my opinion, I would not have reached my goal, if I hadn’t made the plan. It’s almost like the saying, “rules are made to be broken.” In one sense, of course rules are not made to be broken, but in another sense, they are. What I mean is that most of the time, rules are made AND consequences are established at the same time. Why have consequences, if rules are not broken? In that sense, we have an example where plans (rules) are made to be broken.</p><p>So, we need to make plans even though they will not necessarily be played out as planned. Think of it as life itself. Your body seems to have a plan, but it never plays out as planned. Your body has a built-in plan of eat, sleep, exercise, etc., and then, for example, a cold virus sets it off-plan. The plan has to be adjusted in order to keep you living. You do so and life continues, BUT not as originally planned. Therefore, life has a plan but it never works out as originally planned. Wow!</p><p>Now, let me make clear that I am NOT saying we shouldn’t plan. I am saying that we should expect our plan to always be faulty. A plan, even if it is faulty, still allows us to achieve our goal. In fact, a plan helps us get started toward change.</p><p>One way of viewing life is as a series of changes. Life implies change. A plan can help us to not procrastinate. A plan can help us go in the direction we want to go. A plan can help us decide and be comfortable with a decision, especially when the situation requires a quick decision.</p><p>Why make plans? Perhaps, making plans actually improves one’s quality of life. Planning for quality? Uhmmmm…</p>",
        "questions": [
          {
            "number": "1",
            "question": "Do you agree with the author’s view that plans are necessary even though they may not work out as expected? Why or why not?",
            "choices": [
              "A. Yes, because plans provide direction and motivation.",
              "B. No, because plans are often a waste of time.",
              "C. It depends on the situation.",
              "D. I am unsure."
            ]
          },
          {
            "number": "2",
            "question": "The author compares plans to rules being 'made to be broken.' Do you think this analogy is accurate? Why?",
            "choices": [
              "A. Yes, because both plans and rules allow for flexibility.",
              "B. No, because rules and plans serve different purposes.",
              "C. Somewhat, but the comparison is too simplistic.",
              "D. I don’t understand the analogy."
            ]
          },
          {
            "number": "3",
            "question": "How do you feel about the idea that life itself has a plan but it never plays out as planned?",
            "choices": [
              "A. I agree; life is unpredictable and full of adjustments.",
              "B. I disagree; life is entirely random and unplanned.",
              "C. I partly agree; some aspects of life follow a plan.",
              "D. I am undecided."
            ]
          },
          {
            "number": "4",
            "question": "Do you believe that making plans can reduce procrastination and improve decision-making?",
            "choices": [
              "A. Yes, because plans create structure and clarity.",
              "B. No, because plans often cause unnecessary pressure.",
              "C. It depends on the individual and the situation.",
              "D. I’m not sure."
            ]
          },
          {
            "number": "5",
            "question": "Do you think planning can improve one’s quality of life, as the author suggests?",
            "choices": [
              "A. Yes, planning helps people achieve their goals.",
              "B. No, planning often leads to disappointment.",
              "C. Sometimes, but not always.",
              "D. I don’t think planning is related to quality of life."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-resolutions",
    "title": "Resolutions",
    "subtitle": "health",
    "category": "reading-exercises",
    "theme": "health",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about making New Year's resolutions and sticking to them.",
    "readingcompTitle": "Resolutions",
    "topic": "health",
    "image": "/Images/resolution1.webp",
    "vocabulary": [
      "bite off more than you can chew - To take on more than you can handle.",
      "get back on track - To return to the correct course or direction.",
      "learn the ropes - To understand the basics of something.",
      "clean slate - A fresh start, free from previous mistakes.",
      "take the bull by the horns - To confront a difficult situation directly.",
      "be ahead of the game - To be in a better position than others, often due to early preparation.",
      "brush up - To improve or refresh one's knowledge or skills.",
      "get your feet wet - To begin to gain experience in something new.",
      "pick up the pace - To increase speed or effort.",
      "fresh start - A new beginning, often after a setback or difficult period.",
      "biting off more than you can chew - Taking on too much work or responsibility.",
      "taking it one step at a time - To approach a task slowly and carefully, without rushing.",
      "Rome wasn’t built in a day - Big accomplishments take time and effort.",
      "throw in the towel - To give up or admit defeat.",
      "look on the bright side - To focus on the positive aspects of a situation.",
      "keep your eye on the prize - To stay focused on your goal, despite difficulties.",
      "take the plunge - To take a risk or do something that you have been hesitant to do.",
      "setting the world on fire - Achieving great success, often unexpectedly.",
      "think outside the box - To think creatively or in an unconventional way."
    ],
    "paragraphs": [
      {
        "text": "<p>The start of a new year is always a time for renewal. It's a chance to hit the reset button, turn over a new leaf, and make a fresh start. For many, New Year’s resolutions serve as a catalyst for change. People across the world take a step back and reflect on the past year, considering how they can do things differently moving forward. But how can we make the most of this opportunity?</p><p>The key is to set realistic goals and stick to them. It's easy to bite off more than you can chew when you get excited about the possibilities, but it’s important to keep your resolutions manageable. Whether you want to get back on track with your health, improve your work-life balance, or learn the ropes of a new skill, setting achievable goals is essential.</p><p>For those who want to make changes in their professional life, the new year offers a clean slate. It's a great time to take the bull by the horns and pursue new opportunities. By taking small steps towards career development, you’ll be ahead of the game in no time. It’s also an excellent opportunity to brush up on existing skills or get your feet wet with something new. In today’s fast-paced world, learning never stops, and the new year is the perfect time to pick up the pace.</p><p>When it comes to personal growth, the idea of renewal can be seen in many ways. Whether it’s focusing on your physical health, your mental well-being, or your relationships, this is the time to make a fresh start. It’s not about biting off more than you can chew, but about taking it one step at a time. Small actions can lead to big changes, and before you know it, you’ll have made significant progress.</p><p>Of course, Rome wasn’t built in a day, and neither are personal transformations. Achieving your resolutions takes time, patience, and perseverance. Don’t get discouraged if things don’t change overnight. You might face challenges along the way, but the key is not to throw in the towel too soon. Remember, consistency is what makes the difference.</p><p>Lastly, it’s important to keep a positive attitude. When you look on the bright side and keep your eye on the prize, your chances of success are much higher. Surround yourself with people who motivate you, and don’t be afraid to take the plunge into new experiences. Whether you succeed or stumble along the way, every step forward is a step towards personal renewal.</p><p>As you move forward with your resolutions, remember that it's not about setting the world on fire but about making steady progress in the right direction. Keep your feet on the ground, think outside the box, and embrace the opportunity for growth. The new year is yours for the taking—so don’t hesitate to make it count!</p>",
        "questions": [
          {
            "number": 1,
            "question": "What is one of the mental health benefits of regular exercise?",
            "choices": [
              "Increases stress",
              "Reduces stress",
              "Increases blood pressure",
              "Improves vision"
            ],
            "correctAnswer": "Reduces stress"
          },
          {
            "number": 2,
            "question": "What is one of the physical benefits of regular exercise?",
            "choices": [
              "Lowers metabolism",
              "Improves cardiovascular fitness",
              "Increases sleep deprivation",
              "Decreases energy levels"
            ],
            "correctAnswer": "Improves cardiovascular fitness"
          }
        ]
      }
    ]
  },
  {    "id": "reading-pets",
    "title": "Pets",
    "subtitle": "environment",
    "category": "reading-exercises",
    "theme": "environment",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about the pros and cons of allowing pets in the workplace.",
    "readingcompTitle": "Pets",
    "topic": "environment",
    "image": "/Images/pets2.webp",
    "vocabulary": [
      "recruitment - the process of finding people to work for a company",
      "retention - the ability to keep employees or customers",
      "ubiquitous - something that is present or found everywhere",
      "collaboration - working together with others to achieve a goal",
      "compassion - feeling sympathy and care for others",
      "sanitary - clean and free from germs or dirt",
      "liability - being responsible for something, especially in a legal sense",
      "productive - working efficiently and achieving good results",
      "distracting - something that makes it hard to focus or pay attention",
      "burnout - physical or mental exhaustion caused by too much work or stress"
    ],
    "paragraphs": [
      {
        "text": "<p>The benefits of allowing pets in the workplace</p><p>In most cases, allowing pets to come to work really means dogs. In fact, June 24 is National Take Your Dog to Work Day, which shows how popular canine companions are. However, some companies welcome cats, birds, and other animals.</p><p>Here are some of the top benefits of permitting employees to bring pets to work:</p> <h4>1. Pets in the office can help with employee recruitment and retention.</h4> <p>There’s no denying pets are beloved companions. Banfield Pet Hospital surveyed new pet owners and found 76 percent prefer spending time with their pet over their partner, friends, or family. Pets are so ubiquitous many job seekers evaluate potential pet-related employee benefits when considering accepting a position.</p><p>Allowing pets in the office can be a great way to improve employee recruitment and retention, especially if you’re managing a multigenerational workforce. According to Statista, 87 million American homes include a pet. And data from the American Pet Products Association (APPA) reveals 32 percent of millennials, 27 percent of Gen Xers, and 16 percent of Gen Zers own pets. This wide swath of pet ownership means your employees are very likely to have a pet and may appreciate saving on doggy daycare. They may also be more willing to stay late if they don’t have to rush home to tend to their furry companions.</p> <h4>2. Pets in the office can improve company morale.</h4> <p>Businesses that allow furry or feathered friends to come to work with employees tend to have creative, open working environments. When the corporate culture emphasizes creating an excellent experience for employees, workplace morale can skyrocket.</p><p>Allowing pets at work can show your staff how much you care about them, which can make them feel happier and more appreciated.</p><p>Did You Know? Allowing pets at work can improve employee engagement and productivity. The Habri report cited above revealed more than 90 percent of employees working in pet-friendly offices say they're fully engaged, compared to 65 percent in pet-free environments.</p> <h4>3. Pets in the office can facilitate employee bonding, which motivates good work.</h4> <p>Some employees may feel more comfortable speaking to a co-worker if there’s a pet around to break the ice. Having animals in an office environment has been shown to facilitate interactions between employees who wouldn’t usually venture outside their comfort zones. This tendency can eventually lead to increased workplace collaboration, teamwork, and innovation. Additionally, employees tend to have greater compassion and understanding for their co-workers when they have a personal bond with them.</p> <h4>4. Pets in the office can improve employee health.</h4> <p>Having furry friends around can have positive effects on employees’ well-being. For starters, office pets require employees to get up from their desks to walk or play with their animals, which is good for employees’ physical health.</p><p>Office pets can also reduce employees’ productivity-killing workplace stress, improving their overall mental health. Reducing employee stress should be a priority for all employers for numerous reasons, including the fact that reduced stress can improve workplace safety. Less stress often leads to fewer stress-related injuries and illnesses, thus reducing the accompanying healthcare costs.</p> <h4>5. Pets in the office can help prevent employee burnout.</h4> <p>Pets are known to make people happy. For this reason, their presence may ultimately help workers avoid employee burnout. It’s easy for employees to dive into their work for hours on end without taking any breaks. Having their pets (or co-workers’ pets) there can remind them to take occasional breaks, allowing them to mentally reset before continuing their workday. These built-in breaks can reduce the potential for employee burnout.</p><p>Although allowing pets in the workplace brings many potential benefits, it’s not always a viable option. Here are some reasons you may not want to allow your team to bring pets to work.</p> <h4>1. Employees may have allergies or a fear of animals.</h4> <p>The presence of an animal could understandably threaten someone’s well-being and sense of safety. According to the Asthma and Allergy Foundation of America, allergies can be considered official disabilities under the Americans with Disabilities Act. As such, they should be respected and accommodated in the workplace. Before allowing animals in the workplace, be sure to survey your employees to establish whether there are allergies to consider or strong personal preferences that would make animals in the workplace a difficult and uncomfortable experience for someone.</p> <h4>2. Pets may not be conducive to a safe or sanitary work environment.</h4> <p>Certain industries can’t permit animals on-site for sanitation or safety reasons — for example, if the workplace must be sterile. You wouldn’t want a shedding dog in a laboratory, a cafe, or anywhere sterile supplies, meals, or manufactured products are made or packaged.</p><p>Similarly, noisy or hazardous work environments are not appropriate for on-site pets. For example, construction sites, machining facilities, and mining operations can be very loud and disturbing to pets — not to mention that it’s dangerous for animals to wander loose and potentially get injured.</p> <h4>3. Pets may be distracting or otherwise curb productivity.</h4> <p>While workplace pets can improve employee bonding, morale, and motivation, they may also prove distracting and detrimental to overall productivity. This is especially true for employees with noisy or energetic pets that require extensive attention, although even the most docile animals require care, supervision, and breaks to relieve themselves. And while the occasional pet break can be great for overall productivity and well-being, when animal breaks take up too much time, their presence might be counterproductive.</p> <h4>4. Pets may increase your legal liability.</h4> <p>Animals in the workplace come with increased responsibility and liability. You may be held responsible for personal injury or property damage caused by employees’ pets or even customers’ pets at your place of business. Pets are ultimately unpredictable — property damage and injuries are possibilities with even the most mild-mannered animals in specific situations.</p> <h4>5. Your office landlord may not allow pets.</h4> <p>Regardless of your personal views on employee pets in the workplace, your office space’s rules may limit you. Some businesses might embrace the idea of having friendly, fun pets around to brighten everyone’s day, but building regulations might restrict the presence of animals. Always check with your building owner before allowing pets to roam the halls.</p>",
        "questions": [
          {
            "number": 1,
            "question": "What is one benefit of allowing pets in the workplace?",
            "choices": [
              "It helps increase employee stress levels",
              "It can improve employee recruitment and retention",
              "It creates a more competitive work environment",
              "It reduces the need for employee benefits"
            ],
            "correctAnswer": "It can improve employee recruitment and retention"
          },
          {
            "number": 2,
            "question": "Which of the following is a reason why pets in the office can improve employee morale?",
            "choices": [
              "Pets can reduce the need for breaks",
              "They help employees feel more appreciated and happy",
              "They make the office quieter",
              "They reduce productivity"
            ],
            "correctAnswer": "They help employees feel more appreciated and happy"
          },
          {
            "number": 3,
            "question": "Why might pets in the office lead to increased employee bonding?",
            "choices": [
              "Pets help break the ice and encourage conversations",
              "Pets distract employees from their work",
              "Pets reduce the number of breaks employees need",
              "Pets limit the number of personal discussions"
            ],
            "correctAnswer": "Pets help break the ice and encourage conversations"
          },
          {
            "number": 4,
            "question": "What is one downside to allowing pets in the workplace?",
            "choices": [
              "They improve employee bonding too much",
              "They may cause distractions and reduce productivity",
              "They make it harder for employees to take breaks",
              "They reduce workplace safety"
            ],
            "correctAnswer": "They may cause distractions and reduce productivity"
          },
          {
            "number": 5,
            "question": "What should companies consider before allowing pets in the workplace?",
            "choices": [
              "Whether pets can improve employee retention only",
              "All employees should have pets before allowing animals",
              "Allergies and safety concerns of employees",
              "The need to make the office environment more competitive"
            ],
            "correctAnswer": "Allergies and safety concerns of employees"
          }
        ]
      }
    ]
  },
  {    "id": "reading-breaking-bad-habits",
    "title": "Breaking Bad Habits",
    "subtitle": "Food",
    "category": "reading-exercises",
    "theme": "Food",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about strategies for breaking bad habits.",
    "readingcompTitle": "Breaking Bad Habits",
    "topic": "Food",
    "image": "/Images/habit1.webp",
    "vocabulary": [
      "Brace yourself - get ready",
      "discomfort - small pain",
      "to do the trick - to make something happen",
      "whip out - pull something out quickly",
      "keep somebody from doing something - prevent",
      "incremental - changing step by step",
      "right off the bat - immediately",
      "triggers - something that makes you change your behavior",
      "stumble - almost fall",
      "distract - take your attention away",
      "tempted - strong feeling of wanting something",
      "temptation - strong feeling of wanting something",
      "incentive - reason, motivation",
      "slip - almost fall"
    ],
    "paragraphs": [
      {
        "text": "<h4>1. Brace yourself for discomfort</h4><p>When you’re trying to break a habit, one of the most important things to do is to prepare yourself to feel uncomfortable.</p><h4>2. Start making a game plan</h4><p>Unfortunately, just thinking, “Hmm, I really should change,” isn’t usually enough to do the trick. (We wish!) Instead, whip out a piece of paper or a journal or open an app on your phone and start planning.</p><h4>3. Write down why you want to change … and why you don’t</h4><p>As part of your planning process, you should think about why you want to break this habit and what’s keeping you from breaking it.</p><h4>4. Set realistic, incremental goals</h4><p>When you’re trying to make a change, it’s common to take on more than you can handle — to go really, really big right off the bat. But that’s often not the best move.</p><h4>5. Identify your triggers</h4><p>You know you want to change, and you also know it’s not going to be easy. But what, exactly, keeps it from being easy? What has gotten in your way when you’ve tried to achieve this goal in the past? What has caused you to stumble or give up?</p><h4>6. Figure out how to distract yourself</h4><p>You don’t have to be like old Oscar Wilde! “Create a plan for what you’ll do when you feel triggered or tempted,” Duke says. “Having a plan that you write out in advance can help distract you from the unwanted behavior.”</p><h4>7. Replace bad habits with better ones</h4><p>In addition to distractions, you can build in some replacement behaviors — things you can do instead of your bad habit. For example:</p><h4>8. Keep temptation away</h4><p>One of the best ways to avoid temptation is to make sure temptation isn’t readily available to you at all.</p><h4>9. Create incentives</h4><p>Speaking of, incentives can help you motivate yourself to do uncomfortable things. You can identify something positive you’ll do for yourself once you hit your goals, or you can reward yourself along the way, like by throwing a dollar into a cookie jar every time you engage in a positive behavior.</p><h4>10. Speak kindly to yourself</h4><p>When you beat yourself up, you feel ... well, beaten up. Negative self-talk can have negative effects on your mental health, self-esteem and motivation, so instead of talking down to yourself when you slip, try to be gentle and encouraging.</p><h4>11. Remind yourself why you’re doing this</h4><p>Put a sticky note on your mirror. Create a new lock screen for your phone. Carry an index card in your wallet. Heck, mail yourself a card that you’ll receive in a few days!</p>",
        "questions": [
          {
            "number": "1",
            "question": "Which strategy do you think is most important for breaking a habit? Why?",
            "choices": [
              "A. Preparing for discomfort",
              "B. Setting realistic, incremental goals",
              "C. Keeping temptation away",
              "D. Speaking kindly to yourself"
            ]
          },
          {
            "number": "2",
            "question": "Do you believe creating a game plan is essential for making a change? Why or why not?",
            "choices": [
              "A. Yes, because it provides structure and focus.",
              "B. No, because change can happen without detailed planning.",
              "C. It depends on the type of habit.",
              "D. I am unsure."
            ]
          },
          {
            "number": "3",
            "question": "How effective do you think replacing bad habits with better ones can be?",
            "choices": [
              "A. Very effective, because it redirects behavior in a positive way.",
              "B. Somewhat effective, but it depends on the individual.",
              "C. Not effective, as habits are too ingrained.",
              "D. I don’t know."
            ]
          },
          {
            "number": "4",
            "question": "Do you think incentives are a good way to stay motivated while breaking a habit? Why or why not?",
            "choices": [
              "A. Yes, because rewards can reinforce positive behavior.",
              "B. No, because relying on incentives might not create lasting change.",
              "C. It depends on the type of incentive.",
              "D. I am unsure."
            ]
          },
          {
            "number": "5",
            "question": "What role do you think self-talk plays in habit change?",
            "choices": [
              "A. A significant role, because it influences motivation and self-esteem.",
              "B. A minor role, as actions are more important than words.",
              "C. It depends on the person and their mindset.",
              "D. I’m not sure."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-chat-productivity",
    "title": "Chat Productivity",
    "subtitle": "Chat",
    "category": "reading-exercises",
    "theme": "Chat",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about whether team communication apps improve productivity.",
    "readingcompTitle": "Chat Productivity",
    "topic": "Chat",
    "image": "/Images/chatapps.webp",
    "vocabulary": [
      "ubiquity - the fact of appearing everywhere or being very common",
      "beneficial - good or helpful for something",
      "proponents - people who support or argue in favor of something",
      "crowdsourcing - getting ideas or help from a large group of people, usually online",
      "millennials - people born approximately between 1981 and 1996",
      "deride - to mock or criticize something in an unkind way",
      "obscure - to make something unclear or difficult to understand",
      "scalability - the ability to handle more work or a larger number of participants without problems",
      "context-switching - the act of changing focus between different tasks or tools",
      "augment - to make something better by adding to it",
      "irrelevant - not related to the current topic or situation",
      "trivial - unimportant or small",
      "transformation - a big or important change in something"
    ],
    "paragraphs": [
      {
        "text": "<h1>Do team communication apps really improve productivity at work?</h1><p>The rise of applications like Microsoft Teams and Slack has been welcomed by many employees in the workplace, because of the ubiquity of similar apps in the consumer space. Outside of the workplace, Facebook Messenger and WhatsApp have taken over from SMS as the preferred means of text-style communication.</p><p>However, is this new technology as beneficial to knowledge workers as its proponents suggest? Let’s look at the pros and cons.</p><h1>The advantages</h1><h1>Remote working</h1><p>Instant messaging connects employees working off-site with those in the office. When someone is working from home or out ‘in the field’, they can check-in with colleagues just as seamlessly as if they were in the same room.</p><h1>Sharing ideas</h1><p>Instant messaging is great for crowdsourcing ideas and sharing knowledge. Post a problem and the network will solve it in front of your eyes.</p><p>What’s more, the best ideas can occur outside of work hours. With team communication apps you can quickly share ideas with colleagues as soon as they pop into your head.</p><h1>It’s how people want to communicate</h1><p>People love communicating via instant messaging. Why not let employees communicate at work how they do in their personal lives?</p><p>A recent study found that 49% of millennials want to use social tools at work; what’s more, 40% of millennials would happily pay to use these tools.</p><h1>The disadvantages</h1><h1>Addictive</h1><p>The same technology that many people deride for keeping us glued to our screens and constantly checking our phones is celebrated for its productivity value. Similarly, many employees complain of the pressure to be online and available at all times. Others say they need to keep checking feeds so as not to miss out on anything important.</p><h1>Distracting</h1><p>Team communication apps increase the noise and distraction that knowledge workers face every day. Every message sends another notification that can take employees away from core work.</p><h1>Confusing</h1><p>When communication is reduced to one liners and emojis, it can obscure the bigger picture. It becomes difficult to pick out important points from a long feed of informal responses.</p><h1>Scalability</h1><p>The biggest problem with chat is that it doesn’t scale. Research shows that chat breaks down as the number of participants grows, the pace of conversation exceeds a particular threshold, and when discussion threads become long. This limits chat to relatively small groups, which means that it, at best, can augment, but not replace email. As such, chat becomes yet one more place people need to go to get information.</p><h1>Context-switching</h1><p>Many businesses have introduced team communication apps without considering how these work in conjunction with their employees’ existing productivity tools, like email, document management systems, and project management tools. The result is that with the introduction of team communications apps, employees must switch back and forth between even more applications more frequently, losing work context more often and more frequently.</p><h1>Too much distraction?</h1><p>Team communication apps offer many benefits to businesses. They are great for informal discussions and internal communication in small groups. Yet, longer discussions with larger groups are confusing and soon break down, and the constant notifications employees receive are distracting. On top of this, the need to switch between applications takes people away from their core work, sometimes for completely irrelevant and trivial reasons.</p><p>What we need are digital experiences that remove context-switching altogether. We should consider new ways to bring relevant information to knowledge workers in context, as and when they require it, and in the same place that they are working. This should be the priority of CEOs and CTOs driving digital transformation at their organizations.</p>",
        "questions": [
          {
            "number": "1",
            "question": "Do you think team communication apps improve productivity in the workplace?",
            "choices": [
              "A. Yes, they help employees stay connected and share ideas.",
              "B. No, they create distractions and reduce focus.",
              "C. It depends on how they are used in the organization.",
              "D. I am unsure."
            ]
          },
          {
            "number": "2",
            "question": "Should companies encourage employees to use instant messaging tools for work communication?",
            "choices": [
              "A. Yes, because it mirrors how people prefer to communicate in their personal lives.",
              "B. No, because it can blur the boundaries between work and personal time.",
              "C. It depends on the type of work and company culture.",
              "D. I am unsure."
            ]
          },
          {
            "number": "3",
            "question": "Do you believe that the disadvantages of team communication apps outweigh their benefits?",
            "choices": [
              "A. Yes, because they can be addictive and distracting.",
              "B. No, because their benefits, like idea sharing and connectivity, are more significant.",
              "C. It depends on how the apps are managed within the team.",
              "D. I am unsure."
            ]
          },
          {
            "number": "4",
            "question": "Should organizations limit the use of team communication apps to small groups only?",
            "choices": [
              "A. Yes, because they don’t scale well for larger teams.",
              "B. No, they should be accessible to everyone regardless of group size.",
              "C. It depends on the nature of the team's work.",
              "D. I am unsure."
            ]
          },
          {
            "number": "5",
            "question": "How important is it for companies to integrate communication apps with existing productivity tools?",
            "choices": [
              "A. Very important, as it reduces context-switching and improves efficiency.",
              "B. Not important, as communication apps should function independently.",
              "C. It depends on the specific needs of the company and its employees.",
              "D. I am unsure."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-app-fatigue",
    "title": "App Fatigue",
    "subtitle": "Chat Platforms",
    "category": "reading-exercises",
    "theme": "Chat Platforms",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about how too many workplace apps cause fatigue and security risks.",
    "readingcompTitle": "App Fatigue",
    "topic": "Chat Platforms",
    "image": "/Images/appfatigue.webp",
    "vocabulary": [
      "staggering - incredibly suprising",
      "rely on - use",
      "abundance - a large quantity",
      "emerging - coming up",
      "constant barrage - a continuous flow of something, often used to describe notifications or events",
      "niche - small special market",
      "completely overwhelming - something that is so intense or significant that it is hard to manage or process",
      "severe consequences - serious and impactful results from a situation or action",
      "perpetually behind - always, never-endingly behind",
      "vulnerabilities - weaknesses that can be exploited",
      "fall into the trap of doing - make the mistake of doing",
      "hinder - hold back",
      "cope - deal with",
      "morale - organizational feeling"
    ],
    "paragraphs": [
      {
        "text": "<p>The number of apps and digital tools employees use daily is staggering. Many businesses now rely on 40 to 60 different tools, and 71% of employees say this abundance makes work more complex. However, a bigger issue is emerging: app fatigue. The constant barrage of notifications is not only exhausting but also a serious cybersecurity concern.</p><p>Over the past few years, the use of cloud apps has exploded. Communication tools like Teams and Slack, productivity platforms, and niche software are now standard in the workplace. While these tools increase efficiency, they also create completely overwhelming digital noise—alerts, updates, passwords, and notifications—leading to app fatigue.</p><p>App fatigue can have severe consequences for companies. First, it leads to ignored updates. Constant interruptions make employees feel perpetually behind, and they may skip installing important software updates. These updates often contain critical security patches, and neglecting them can create vulnerabilities. In 2017, the WannaCry ransomware attack exploited such unpatched systems, affecting hundreds of thousands of computers.</p><p>Another consequence of app overload is password reuse. With employees managing an average of 191 passwords, it’s easy to fall into the trap of reusing weak passwords. This significantly increases the risk of credential breaches, which are a major cause of cloud security incidents. Breached accounts can also harm productivity, as IT teams must spend valuable time addressing the issues, leaving employees unable to work.</p><p>Employees overwhelmed by app fatigue may also turn off notifications to cope. While some notifications, like message alerts, may seem unimportant, others—such as security warnings—are vital. Disabling these alerts can compromise security and hinder collaboration, as missed reminders about deadlines or meetings can lead to disorganization and missed opportunities.</p><p>Cognitive overload and burnout are further outcomes of app fatigue. The constant barrage of digital distractions reduces productivity and harms employee well-being, leading to burnout and higher turnover rates. This isn’t just a morale issue; it’s a costly problem for businesses.</p><p>While we can’t eliminate many of these tools, we must develop strategies to manage app fatigue. These tools are valuable—they save time, improve efficiency, and make our work easier. The key is to find a balance and regain control over the digital noise.</p>",
        "questions": [
          {
            "number": "1",
            "question": "Do you think the increasing number of apps in the workplace makes employees more productive?",
            "choices": [
              "A. Yes, more apps help employees get tasks done faster.",
              "B. No, too many apps create confusion and inefficiency.",
              "C. It depends on the type of apps used in the workplace.",
              "D. I am unsure."
            ]
          },
          {
            "number": "2",
            "question": "Do you believe that app fatigue affects employee morale?",
            "choices": [
              "A. Yes, it leads to stress and burnout.",
              "B. No, employees can handle multiple apps without issue.",
              "C. It depends on the workload and the number of apps used.",
              "D. I am unsure."
            ]
          },
          {
            "number": "3",
            "question": "Should companies limit the number of apps employees use to reduce app fatigue?",
            "choices": [
              "A. Yes, limiting apps would make work simpler and less stressful.",
              "B. No, employees need all the apps to perform their jobs effectively.",
              "C. It depends on the specific needs of each team or department.",
              "D. I am unsure."
            ]
          },
          {
            "number": "4",
            "question": "Do you think app notifications are a major contributor to employee stress?",
            "choices": [
              "A. Yes, constant notifications disrupt focus and cause stress.",
              "B. No, notifications are essential for staying updated on tasks.",
              "C. It depends on the frequency and type of notifications.",
              "D. I am unsure."
            ]
          },
          {
            "number": "5",
            "question": "Should companies invest in better cybersecurity training to avoid breaches caused by app fatigue?",
            "choices": [
              "A. Yes, better training would help employees handle apps securely.",
              "B. No, app fatigue is a separate issue from cybersecurity training.",
              "C. It depends on the security risks specific to the company.",
              "D. I am unsure."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-self-sevice-society",
    "title": "Self Sevice Society",
    "subtitle": "Automation",
    "category": "reading-exercises",
    "theme": "Automation",
    "level": "B2",
    "levelname": "B2 – Upper-Intermediate",
    "time": "",
    "description": "Read about how self-service technology may weaken community.",
    "readingcompTitle": "Self Sevice Society",
    "topic": "Automation",
    "image": "/Images/selfcheckout.webp",
    "vocabulary": [
      "substitute - to replace one thing with another",
      "efficiency - the ability to achieve a goal with minimal waste or effort",
      "innovative - introducing new ideas or methods",
      "perspective - a particular point of view",
      "eliminate - to remove or get rid of something",
      "relegate - to move to a lower rank or less prominent position",
      "exposure - the state of being in contact with something",
      "prejudice - preconceived opinion not based on reason or experience",
      "petition - a formal request for change",
      "redistributive - aimed at distributing resources more evenly",
      "disempowered - deprived of power or influence",
      "civic - relating to citizenship or community",
      "isolation - the state of being separated from others",
      "inequality - a lack of equality or fairness",
      "marginalized - treated as insignificant or peripheral"
    ],
    "paragraphs": [
      {
        "text": "<h1>A rise in self-service technologies may cause a decline in our sense of community</h1><p>Automation, once hidden behind closed doors in factories, is increasingly moving into public view. Customers can pay for groceries or clothing at a self-checkout machine, order fast food from a touchscreen kiosk or even pickup coffee from a 'robo-café.' These technologies, which substitute human contact for robot-based interactions, are examples of self-service technologies — innovative public-facing automation which 'enable customers to perform entire services on their own without direct assistance from employees.' While self-service technologies have the potential to improve efficiency, reduce costs and improve user experience, these technologies raise complex economic and political questions.</p><h1>Politics and exposure</h1><p>Much has been written from an economic perspective about whether automation will eliminate jobs, decrease wages, contribute to job growth, or 'create as many jobs as it destroys over time.' However, less attention has been given to thinking about how these technologies will affect our politics. Whether these new technologies replace jobs, relegate existing positions to non-public facing roles or create new employment opportunities, they will result in us interacting with fewer people than we have historically. Experiences with strangers can shape how we define our community and politics. If we no longer encounter cashiers or fast food employees, many of whom are temporary foreign workers, will our beliefs about immigration policies or minimum wage change? What do bike couriers think about bike lanes? How does a dental office receptionist feel about universal dental care, or a corner store clerk about crime rates?</p><p>However fleeting, exposure to people outside of your own socio-demographic groups affect attitudes positively. Existing research demonstrates that 'actual face-to-face interaction…typically reduces intergroup prejudice.' Exposure effects are also related to how we position ourselves within the world when compared to others. For instance, a recent South African study showed that 'willingness to sign a petition that calls for higher taxes on the wealthy increases in the presence of a high-status car.' Just seeing a BMW 3-series car near the petitioner made people more likely to favour wealth redistributive policies. Another study shows that being the victim of a crime increases political participation: 'Rather than becoming withdrawn or disempowered, crime victims tend to become more engaged in civic and political life.' We shape our politics based, at least partially, on what and who we have been exposed to.</p><h1>Identity and social interaction</h1><p>We build our sense of civic identity and opinions about government through social interactions. Political scientist Robert D. Putnam, who has studied civic engagement since the 1960s, argues Americans are less engaged in politics than they used to be and are more isolated, spending less time with friends, family and neighbours. Our social capital — which Putnam defines as the overarching belief about society that facilitates co-operation — diminishes when we lose opportunities to engage with people outside of our regular social networks.</p><p>This decline in social capital can be traced to changes in work and society more generally. Society, in other words, is becoming increasingly individualistic. Public-facing automation may further diminish our social capital by decreasing our interactions with other people. As we pay for parking at parking machines, rent bowling shoes and lanes through an app, or order food from touchscreen kiosks, we interact less with the people who work these jobs.</p><h1>Exacerbates inequality</h1><p>The impact of public-facing automation on social inequality also requires further study. Self-service technologies, particularly in the food services industry, may exacerbate social inequalities by limiting job opportunities for certain groups, such as those with different educational backgrounds or already-marginalized communities. As public-facing automation shifts workers away from public view, wages which reflect professional skill and customer service expectations may disappear. In the grocery industry, for instance, we may see a widening pay gap between technicians hired to upkeep self-checkout machines and the employees hired to stock shelves. The effects of increasing public-facing automation may not be well understood for years. In the meantime, as we seek to better understand the intersection between technology and society, we should ask: how will our sense of community and our political preferences change when we interact less with the people who work the jobs that self-service technologies replace?</p>",
        "questions": [
          {
            "number": "1",
            "question": "Do you think self-service technologies are reducing the sense of community? Why or why not?",
            "choices": [
              "A. Yes, because they reduce human interaction.",
              "B. No, because they make services more efficient.",
              "C. It depends on the specific situation.",
              "D. I am unsure."
            ]
          },
          {
            "number": "2",
            "question": "Should governments regulate automation to protect job opportunities?",
            "choices": [
              "A. Yes, to ensure fairness.",
              "B. No, let the market decide.",
              "C. It depends on the industry.",
              "D. I am unsure."
            ]
          },
          {
            "number": "3",
            "question": "Do you think automation increases or decreases social inequality?",
            "choices": [
              "A. It increases it by reducing job opportunities.",
              "B. It decreases it by creating new skilled roles.",
              "C. It has no significant effect.",
              "D. I am unsure."
            ]
          },
          {
            "number": "4",
            "question": "Is the convenience of self-service technologies worth the potential loss of social interaction?",
            "choices": [
              "A. Yes, because convenience is more important.",
              "B. No, because social interaction is essential.",
              "C. It depends on the context.",
              "D. I am unsure."
            ]
          },
          {
            "number": "5",
            "question": "How should society balance technological innovation with preserving social capital?",
            "choices": [
              "A. Focus on innovation and efficiency.",
              "B. Prioritize preserving social interaction.",
              "C. Strike a balance between the two.",
              "D. I am unsure."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-self-service-checkout-reddit",
    "title": "Self Service Checkout Reddit",
    "subtitle": "Automation",
    "category": "reading-exercises",
    "theme": "Automation",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read a Reddit thread about self-checkout frustrations.",
    "readingcompTitle": "Self Service Checkout Reddit",
    "topic": "Automation",
    "image": "/Images/selfcheckout.webp",
    "vocabulary": [
      "So much for - there goes something (expressing disapppointment at ruined plans)",
      "scales - appliance to measure weight",
      "berate - angrily criticize someone",
      "stormed off - angrily walk away",
      "abandoned - left behind",
      "shoving - pushing",
      "mess up - make a mistake"
    ],
    "paragraphs": [
      {
        "text": "<h1>OP: You hear people complain about self-checkouts often, but never about self-service gas stations.</h1><br><p><strong>Commenter 1:</strong> Because gas stations don’t have those stupid scales and give the message “place the item in the bagging area”</p><p><strong>Commenter 2:</strong> Or 'please wait for cashier'. So much for not talking to anybody on my shopping trip.</p><p><strong>Commenter 3:</strong> This ^ makes me wanna drop everything and leave</p><p><strong>Commenter 4:</strong> I have just left my purchase and walked out before. More than once. The best was when I was doing it and the self checkout only had room for one bag. I had about 3 bags of groceries. So once I filled a bag, I took it and put it in my cart which caused the whole machine to alarm and stop. The cashier starts to berate me angrily so I said f-$%@! this “literally” and just stormed off.</p><p><strong>Commenter 5:</strong> As an older guy now, half my self checkouts get abandoned if things don't work. Just walk out, the company doesn't have much invested in it anyway.</p><p><strong>Commenter 10:</strong> See I feel like we are at the point with our technology where robot/machine checkout should be able to just tell if I'm shoving a gift card, debit, credit or cash into it. Like come on! You tell me what I'm paying you with</p><p><strong>Commenter 11:</strong> If I mess up putting in my PIN number at the gas pump and have to hit cancel, I don't have to wait for an attendant to come put in their code.</p><p><strong>Commenter 17:</strong> Probably because most people don't know full service gas stations ever existed.</p>",
        "questions": [
          {
            "number": "1",
            "question": "Do you use self-service checkouts and, if so, do you like it?",
            "choices": [
              "A. Yes and yes",
              "B. Yes and no",
              "C. No and no",
              "D. What's a self-service checkout?"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-types-of-people-in-tech",
    "title": "Types of People in Tech",
    "subtitle": "Technology",
    "category": "reading-exercises",
    "theme": "Technology",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about different personality types you meet in tech.",
    "readingcompTitle": "Types of People in Tech",
    "topic": "Technology",
    "image": "/Images/techpeople1.webp",
    "vocabulary": [
      "eager - excited",
      "gifted - very clever",
      "peculiar - unusual or strange",
      "paranoid - excessively worried or anxious",
      "long for - really desire",
      "advocate - someone who publicly supports a cause",
      "contemplates - thinks deeply about something",
      "mediator - someone who helps resolve conflicts",
      "dexterous - skilled with hands or mind",
      "incognito - in disguise or anonymous",
      "mediocrity - the state of being average or unremarkable"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>As varied as programming languages can be, so are the people in the field of technology. This domain is full of different passions, styles, working habits, and even types of creativity. In this post, get to know more about the various types of peculiar but gifted people you will meet in the field of tech:</p>",
          "<p><strong>The Nervous Newbie</strong></p>",
          "<p>This new hire is shy to interact at first. He calls everyone sir or ma’am. Of all the others, he is the most paranoid about the processes, in the work or in the company as a whole, but very eager to learn. Also, he comes on time and leaves on time… for now.</p>",
          "<p><strong>The Charming Cheerer</strong></p>",
          "<p>It doesn’t take a lot to impress this gal. She is always so amazed at what her workmates come up with — an unconventional idea, a simple design, or a working code. This cheerleader always boosts the confidence of everyone, motivating them to do better every time. Being good at what she does, she is also always there to help especially the newbies.</p>",
          "<p><strong>The Active Advocate</strong></p>",
          "<p>He longs for change, and he knows he has to do something to make change happen. This is why he loves a good cause, and acts on an idea he knows would create an impact on the world he lives in. Whether it’s building the next animal shelter foundation or developing an application that would help generate income for local artists, the advocate is always motivated to make use of technology to empower their cause.</p>",
          "<p><strong>The Easygoing Extrovert</strong></p>",
          "<p>Most likely the loud one. Everyone knows that he has arrived, or that he has already left. He constantly asks people questions, work-related or not because he cares — a whole lot. His voice is what uplifts the spirit in the office, making everyone feel that they are part of the team. He is always out organizing or speaking at the next conference or meetup. He takes the most group selfies, and he has the most friends both in the online and offline sphere.</p>",
          "<p><strong>The Dexterous Designer</strong></p>",
          "<p>She looks so chill but delivers jaw-dropping outputs. It takes her long to finish, or even to start a design but that’s because she thoroughly contemplates the concept that would be perfectly illustrated in the design. One take is not enough, too — she keeps redoing, redesigning, adding or subtracting elements — just to get that ‘finally!’ look. She is not very organized with her place, but at least she is with her work.</p>",
          "<p><strong>The Ultimate Untouchable</strong></p>",
          "<p>“Don’t talk to me, I’m coding.” This is the Jedi. The master. The one with the most number of monitors than others and owns just one color sweater. Most of the time, he is also the mediator. Mobile development and/or web languages—Java, Python, CSS—name it, he knows it. He is the very serious type and codes like he is on a Mission Impossible scene.</p>",
          "<p><strong>The Hero Hacker</strong></p>",
          "<p>Being the ninja who masters web hacks (and incognito skills), he has a lot of tricks up his sleeve. The (good) hacker always sees a way to do things faster. His skills are needed for quality assurance and security measures. The tech world needs him. Everyone needs him. All he needs is a high-speed internet.</p>",
          "<p><strong>The Profound Perfectionist</strong></p>",
          "<p>She is outspoken about the things that could go wrong in a project and areas that could be improved on. She panics if a project delivery is delayed, pushes to get things done, and makes sure people are performing well — all because she wants the best for the team, and for the clients. She is the type we need to remind us not to settle for mediocrity.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Which type of tech person is most likely to push for social causes?",
            "choices": [
              "A. The Dexterous Designer",
              "B. The Active Advocate",
              "C. The Hero Hacker",
              "D. The Profound Perfectionist"
            ]
          },
          {
            "number": "2",
            "question": "Who in the office is likely to be the loudest and most social?",
            "choices": [
              "A. The Ultimate Untouchable",
              "B. The Easygoing Extrovert",
              "C. The Nervous Newbie",
              "D. The Hero Hacker"
            ]
          },
          {
            "number": "3",
            "question": "Which tech personality is most focused on code and prefers not to be disturbed?",
            "choices": [
              "A. The Charming Cheerer",
              "B. The Profound Perfectionist",
              "C. The Ultimate Untouchable",
              "D. The Active Advocate"
            ]
          },
          {
            "number": "4",
            "question": "What is the main characteristic of the Profound Perfectionist?",
            "choices": [
              "A. They are outspoken about problems and strive for the best results.",
              "B. They are easygoing and love socializing.",
              "C. They focus on designing visually appealing work.",
              "D. They enjoy hacking systems and finding security flaws."
            ]
          },
          {
            "number": "5",
            "question": "Which tech personality is described as someone who helps boost team confidence?",
            "choices": [
              "A. The Charming Cheerer",
              "B. The Active Advocate",
              "C. The Dexterous Designer",
              "D. The Nervous Newbie"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-types-of-developers",
    "title": "Types of Developers",
    "subtitle": "Technology",
    "category": "reading-exercises",
    "theme": "Technology",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about eight types of developer personalities.",
    "readingcompTitle": "Types of Developers",
    "topic": "Technology",
    "image": "/Images/techpeople1.webp",
    "vocabulary": [
      "broad - wide",
      "altruist - a person who is selflessly concerned for the well-being of others",
      "ambidextrous - able to use both hands equally well; adaptable in different areas",
      "ascetic - practicing strict self-discipline and minimalism",
      "austerity - the state of being tight with money or time",
      "egomaniac - someone obsessed with themselves and their own interests",
      "egotist - a person who is excessively self-centered or boastful",
      "extrovert - an outgoing person who enjoys social interaction",
      "introvert - a reserved person who prefers solitude over socializing",
      "misanthropic - having a general dislike or distrust of people",
      "obnoxious - very irritating",
      "minimalism - a lifestyle or design approach that emphasizes simplicity",
      "austerity - strict self-discipline and avoidance of luxury",
      "dread - really hate",
      "narcissistic - having an excessive interest in oneself and one's appearance",
      "coerce - to force someone to do something through pressure or threats",
      "intellectual prowess - great skill or ability in thinking and reasoning",
      "thrives - prospers or grows well in a particular environment",
      "automation - the use of technology to perform tasks without human intervention",
      "distrust - a lack of confidence or belief in others",
      "ruthless - having no pity or compassion for others",
      "self-denial - the act of refusing one's own desires for a greater purpose",
      "collaboration - working together with others to achieve a goal"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>8 Types of Developers</h1>",
          "<p>In this post we are going to take a look at some typical personality traits of developers. And based on their characteristics I have made some broad classifications of developers.</p>",
          "<p>I am not sure these classifications are truly meaningful or really exist in the workplace, it's just a curious exploration and answer to the question 'What if these type of Developers exist in the real world and what would be their typical behaviors and what would be their preferences?'</p>",
          "<h1>1. The Altruist Developer</h1>",
          "<p>The Altruist developer is the one who cares about others. They are the ones who generally contribute to open source and create many open source packages. They write good documentation and are generally responsible for a lot of internal tooling within teams and organizations.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2Fj6swy9fzzdx0x95jd073.webp' alt='The Altruist Developer' />",
          "<hr></hr>",
          "<h1>2. The Ambidextrous Developer</h1>",
          "<p>They could potentially be a FullStack Developer or a DevOps Engineer or an SRE. They are the go-getters in their teams. You can typically find these types of folks in early-stage startups or very small organizations.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2Fv9zucbfydvqe3fyqten2.webp' alt='The Ambidextrous Developer' />",
          "<hr></hr>",
          "<h1>3. The Ascetic Developer</h1>",
          "<p>Minimalism is the motto for these developers. They have the typical characteristics of self-denial, austerity, and lonely contemplation.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2Fqcbfhv65vdzssznzsxk3.webp' alt='The Ascetic Developer' />",
          "<hr></hr>",
          "<h1>4. The Ego Maniac Developer</h1>",
          "<p>The first and foremost question these types of developers have in their mind is 'What's in it for me?'. Their selfishness, greed, and ruthless desire for self-advancement might hurt other people in the team.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2Ffhajlz7rn40nqun5g44f.webp' alt='The Ego Maniac Developer' />",
          "<hr></hr>",
          "<h1>5. The Egotist Developer</h1>",
          "<p>These developers are obnoxious and typically carry narcissistic tendencies. They coerce their peers to approve their pull requests and love showing off their intellectual prowess.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2F6nwi35546v6h8s4ydmfd.webp' alt='The Egotist Developer' />",
          "<hr></hr>",
          "<h1>6. The Extrovert Developer</h1>",
          "<p>These developers love meetings and social interaction. They dread quiet offices and thrive in environments with high collaboration.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2Fc6hk2lkd3tfh6umrc54t.webp' alt='The Extrovert Developer' />",
          "<hr></hr>",
          "<h1>7. The Introvert Developer</h1>",
          "<p>They are the opposite of extroverts. They prefer working alone, avoid meetings, and hate pair programming.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2Fqkd2t4p0iqfuzmkole7o.webp' alt='The Introvert Developer' />",
          "<h1>8. The Misanthropic Developer</h1>",
          "<p>They distrust humans, prefer automation, and rely on machines more than people. They are big fans of shell scripting and prefer working independently.</p>",
          "<img src='https://media2.dev.to/dynamic/image/width=800%2Cheight=%2Cfit=scale-down%2Cgravity=auto%2Cformat=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2F07g577140qu3omjy3zq7.webp' alt='The Misanthropic Developer' />"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Which type of tech person do you most closely identify with?",
            "choices": [
              "A. Choose from list"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-planned-obsolescence-and-the-quality-of-goods",
    "title": "Planned Obsolescence and the Quality of Goods",
    "subtitle": "Quality",
    "category": "reading-exercises",
    "theme": "Quality",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read a discussion about product quality and planned obsolescence.",
    "readingcompTitle": "Planned Obsolescence and the Quality of Goods",
    "topic": "Quality",
    "image": "/Images/plannedobsolescence.webp",
    "vocabulary": [
      "planned obsolescence - conscious plan to make products that don't last",
      "aggravation - a situation that causes irritation or frustration",
      "warranty - a time-locked guarantee",
      "Don't get me wrong - don't misunderstand me",
      "durable - long-lasting",
      "malicious - evil",
      "shell out - pay a lot of money",
      "longevity -  lasting a long time",
      "lifespan - period of time a product will last",
      "cost-averse - against spending a lot of money"
    ],
    "paragraphs": [
      {
        "text": [
          "<p><strong>OP:</strong> Does anyone else think that the general quality of goods and products has declined in recent years? I feel like the never-ending capitalist goal of maximising profits has led us to look for lower costs and, therefore, lower quality.</p>",
          "<p><strong>Commenter 1:</strong> There is the 'planned obsolescence' part of this. As an engineer, I do not get told to pick parts that will die shortly after the warranty period is over. Don't get me wrong, the expected lifetime information is available on the parts I select and I have an idea of how the average user will interact with my design. So I could pick parts that would on average last just longer than the warranty. But that's not how it works. In fact, I get told how much I can spend on the design to meet the price point the company is aiming for and I pick the most durable part that meets spec that I can find. Engineers are not malicious, but we do have project requirements.</p>",
          "<p><strong>Commenter 2:</strong> Planned obsolescence does not exist in the form it's commonly portrayed. Nobody is asking an engineer 'make this thing so it will fail sooner.'</p>",
          "<p>The difference is that people ARE saying 'make this thing as cheap as possible. It needs to last 2 years.' And then the engineer says 'Okay here's a design. It'll last 2 years. For an extra dollar of material, I can make it last 10 years,' but then because they have competitors in the market, they need to make their product as cheap as possible, so they don't shell out for longevity.</p>",
          "<p>Nobody tries to make things break sooner, but because of other priorities coming ahead of lifespan, lifespan suffers.</p>",
          "<p>This ultimately comes to consumer choice. If you're out buying a new washing machine for your clothes, and there's a $500 option and a $2000 option, you're far, far more likely to go with the 500, even if the 2000 will last 10 times as long. People are cost-averse more than anything. And when you buy the 500, it's going to have plastic gears instead of metal gears. It's going to have lower quality electronic components. It's going to have a motor that will burn out after a few years. Unfortunately, the companies have kind of been forced into making products lower quality to meet the lower price demands of consumers.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Do you agree with the OP's opinion that the general quality of goods has declined in recent years? Why or why not?",
            "choices": [
              "A. Yes, quality has decreased due to profit-driven practices.",
              "B. No, products are just as good as they were before.",
              "C. Yes, but only certain products have seen a decline in quality.",
              "D. No, the market is improving the quality of goods."
            ]
          },
          {
            "number": "2",
            "question": "What do you think about the concept of 'buy cheap, buy twice'? Does it apply to your own experiences?",
            "choices": [
              "A. I agree, cheaper products often end up costing more in the long run.",
              "B. I disagree, cheap products can still last a long time.",
              "C. I think it depends on the product.",
              "D. I don't believe in this concept."
            ]
          },
          {
            "number": "3",
            "question": "How do you feel about planned obsolescence as described by the commenters? Do you think companies deliberately design products to break?",
            "choices": [
              "A. Yes, companies design products to fail after a short period.",
              "B. No, products fail due to accidental design flaws.",
              "C. I think companies do not care about how long products last.",
              "D. I believe companies focus on durability and long-term use."
            ]
          },
          {
            "number": "4",
            "question": "What are your thoughts on the idea of repairing things rather than replacing them, as seen in Hungary?",
            "choices": [
              "A. It's better to repair things and avoid waste.",
              "B. It's more convenient to replace things than to repair them.",
              "C. Repairing things is outdated and unnecessary.",
              "D. I prefer replacing things as soon as they break."
            ]
          },
          {
            "number": "5",
            "question": "How do you think engineers balance the need for durable products with the pressure to meet a price point?",
            "choices": [
              "A. Engineers prioritize durability over price to ensure customer satisfaction.",
              "B. Engineers try to find a balance between cost and quality.",
              "C. Engineers often sacrifice durability to meet lower price points.",
              "D. Engineers are not involved in decisions about product pricing."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-too-many-choices-a-problem-that-can-paralyze",
    "title": "Too Many Choices: A Problem That Can Paralyze",
    "subtitle": "Decision-Making and Choices",
    "category": "reading-exercises",
    "theme": "Decision-Making and Choices",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about how too many choices can paralyze decision-making.",
    "readingcompTitle": "Too Many Choices: A Problem That Can Paralyze",
    "topic": "Decision-Making and Choices",
    "image": "/Images/choice1.webp",
    "vocabulary": [
      "paralyze - to make someone unable to act or make decisions",
      "torture - to cause intense suffering or anxiety",
      "option - a thing that is or may be chosen",
      "agony - extreme physical or mental suffering",
      "common wisdom - generally accepted belief or opinion",
      "overload - an excessive amount of something",
      "bolster - to support or strengthen",
      "assortment - a collection of various kinds of things",
      "debilitating - causing serious weakness or incapacity"
    ],
    "paragraphs": [
      {
        "text": "<p>Take my younger son to an ice cream parlor or restaurant if you really want to torture him. He has to make a choice, and that’s one thing he hates. Would chocolate chip or coffee chunk ice cream be better? The cheeseburger or the turkey wrap? His fear, he says, is that whatever he selects, the other option would have been better.</p>",
        "questions": [
          {
            "number": "1",
            "question": "Why does the author's son hate making choices?",
            "choices": [
              "A. He dislikes ice cream.",
              "B. He fears making the wrong decision.",
              "C. He doesn’t like going to restaurants.",
              "D. He prefers not to eat."
            ]
          },
          {
            "number": "2",
            "question": "What does the author mean by 'torture' in this context?",
            "choices": [
              "A. Physical pain caused by food choices.",
              "B. The son's struggle with decision-making.",
              "C. A joke about parental behavior.",
              "D. Intense dislike for restaurants."
            ]
          }
        ]
      },
      {
        "text": "<p>Gabriel is not alone in his agony. Although it has long been the common wisdom in our country that there is no such thing as too many choices, as psychologists and economists study the issue, they are concluding that an overload of options may actually paralyze people or push them into decisions that are against their own best interest.</p>",
        "questions": [
          {
            "number": "3",
            "question": "What does 'common wisdom' imply about choices?",
            "choices": [
              "A. Choices are always beneficial.",
              "B. Too many options are harmful.",
              "C. Wisdom comes from experts.",
              "D. Making decisions is unnecessary."
            ]
          },
          {
            "number": "4",
            "question": "What effect can too many choices have, according to psychologists?",
            "choices": [
              "A. They make people more confident.",
              "B. They paralyze decision-making.",
              "C. They help people avoid regret.",
              "D. They improve decision quality."
            ]
          }
        ]
      },
      {
        "text": "<p>There is a famous jam study (famous, at least, among those who research choice), that is often used to bolster this point. Sheena Iyengar, a professor of business at Columbia University and the author of “The Art of Choosing,” (Twelve) to be published next month, conducted the study in 1995.</p>",
        "questions": [
          {
            "number": "5",
            "question": "What was the purpose of the jam study?",
            "choices": [
              "A. To prove jams are popular.",
              "B. To explore the effects of choice.",
              "C. To promote Wilkin & Sons.",
              "D. To sell more jam coupons."
            ]
          }
        ]
      },
      {
        "text": "<p>In a California gourmet market, Professor Iyengar and her research assistants set up a booth of samples of Wilkin & Sons jams. Every few hours, they switched from offering a selection of 24 jams to a group of six jams. On average, customers tasted two jams, regardless of the size of the assortment, and each one received a coupon good for $1 off one Wilkin & Sons jam.</p>",
        "questions": [
          {
            "number": "6",
            "question": "What was the difference in customer behavior when 24 jams were offered compared to 6?",
            "choices": [
              "A. More people purchased jam when fewer choices were offered.",
              "B. Customers avoided both booths equally.",
              "C. People sampled more jams from the larger assortment.",
              "D. There was no difference in customer behavior."
            ]
          }
        ]
      },
      {
        "text": "<p>Here’s the interesting part. Sixty percent of customers were drawn to the large assortment, while only 40 percent stopped by the small one. But 30 percent of the people who had sampled from the small assortment decided to buy jam, while only 3 percent of those confronted with the two dozen jams purchased a jar.</p>",
        "questions": [
          {
            "number": "7",
            "question": "Why did fewer customers buy jam from the larger assortment?",
            "choices": [
              "A. They found the larger assortment overwhelming.",
              "B. They disliked the taste of the jam.",
              "C. The smaller assortment had better flavors.",
              "D. They forgot to use their coupon."
            ]
          }
        ]
      },
      {
        "text": "<p>That study “raised the hypothesis that the presence of choice might be appealing as a theory,” Professor Iyengar said last year, “but in reality, people might find more and more choice to actually be debilitating.”</p>",
        "questions": [
          {
            "number": "8",
            "question": "What conclusion did Professor Iyengar draw from the study?",
            "choices": [
              "A. People enjoy having many choices.",
              "B. Too many choices can be paralyzing.",
              "C. Customers prefer gourmet products.",
              "D. Sampling increases sales."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-are-dogs-smarter-than-cats-science-has-an-answer",
    "title": "Are Dogs Smarter Than Cats? Science Has an Answer",
    "subtitle": "Animal Intelligence",
    "category": "reading-exercises",
    "theme": "Animal Intelligence",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about whether dogs are smarter than cats.",
    "readingcompTitle": "Are Dogs Smarter Than Cats? Science Has an Answer",
    "topic": "Animal Intelligence",
    "image": "/Images/dogscatssmart.webp",
    "vocabulary": [
      "contentious - causing fights",
      "neurons - nerve cells in the brain that transmit information",
      "cerebral cortex - the outer layer of the brain responsible for higher cognitive functions",
      "cognitive function - mental processes like thinking, learning, and remembering",
      "stimuli - external factors that cause a reaction in an organism",
      "problem-solving - the ability to find solutions to complex issues",
      "carnivorous - feeding on other animals",
      "clout - importance, respectability",
      "quantifiable - able to be measured or counted",
      "albeit - although it is",
      "controversial - causing public disagreement or debate",
      "motor skills - movements controlled by the brain, like walking or grasping",
      "structural complexity - the level of organization and intricacy in a structure"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>One of our most contentious debates may now have an answer. Dogs, it turns out, have about twice the number of neurons in their cerebral cortexes than what cats have, which suggests they could be about twice as intelligent.</p>",
          "<p>This finding was provisionally accepted for publication and will soon publish in the journal <em>Frontiers in Neuroanatomy</em>. A team of researchers from six different universities in the U.S., Brazil, Denmark, and South Africa contributed to the research.</p>",
          "<p><strong>Why Use Neurons?</strong></p>",
          "<p>\"Neurons are the basic information processing units,\" said Herculano-Houzel. \"The more units you find in the brain, the more cognitively capable the animal is.\"</p>",
          "<p>To get an idea of how many neurons dogs and cats might typically have, the team used three brains—one from a cat, one from a golden retriever, and one from a mixed-breed small dog. Two brains were used to study dogs because the canines vary so greatly in size.</p>",
          "<p>In each of the dogs' brains, despite varying in size, researchers found about 500 million neurons, more than double the 250 million found in the cat's brain.</p>",
          "<p>Based on the number of neurons found, they speculated that dogs have roughly the same intelligence as raccoons and lions, while domestic cats have comparable intelligence to bears.</p>",
          "<p><strong>The Measure of Intelligence</strong></p>",
          "<p>While the researchers may have added scientific clout to a household debate about cats and dogs, their work is part of a larger effort to use neurons as one quantifiable measure of intelligence.</p>",
          "<p>Herculano-Houzel argues that counting neurons is just one, albeit in her opinion the most effective to date, way to measure intelligence.</p>",
          "<p>\"It's not a larger body that explains the number of neurons you have,\" she said. \"You can have animals with similar-sized brains, and they have completely different numbers of neurons.\"</p>",
          "<p>The research team has so far focused on studying carnivorous land animals, but they hope to one day study marine mammals.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "According to the study, why are neurons important in measuring intelligence?",
            "choices": [
              "A. They determine the size of an animal's brain",
              "B. They process information and influence cognitive ability",
              "C. They help animals move their muscles",
              "D. They indicate the weight of an animal's brain"
            ]
          },
          {
            "number": "2",
            "question": "What method did researchers use to count neurons?",
            "choices": [
              "A. Measuring brain size",
              "B. Studying behavior patterns",
              "C. Turning the brain into a solution and analyzing the nuclei",
              "D. Observing animal problem-solving skills"
            ]
          },
          {
            "number": "3",
            "question": "What did researchers conclude about the intelligence of dogs and cats?",
            "choices": [
              "A. Cats are more intelligent than dogs",
              "B. Dogs have roughly twice as many neurons as cats, suggesting greater intelligence",
              "C. Cats and dogs have similar cognitive abilities",
              "D. Brain size is the only factor determining intelligence"
            ]
          },
          {
            "number": "4",
            "question": "How does Sarah Benson-Amram view brain size as a measure of intelligence?",
            "choices": [
              "A. It is the best way to determine intelligence",
              "B. There is little evidence that brain size always correlates with intelligence",
              "C. Brain size directly determines intelligence in all species",
              "D. Brain size only matters for marine mammals"
            ]
          },
          {
            "number": "5",
            "question": "What future research does the team hope to conduct?",
            "choices": [
              "A. Studying intelligence in marine mammals",
              "B. Analyzing primate brain structures",
              "C. Measuring brain weight in different species",
              "D. Testing the problem-solving skills of insects"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-recommended-steps-per-day-by-age",
    "title": "Recommended Steps per Day by Age",
    "subtitle": "Health & Fitness",
    "category": "reading-exercises",
    "theme": "Health & Fitness",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about how many steps per day you need by age.",
    "readingcompTitle": "Recommended Steps per Day by Age",
    "topic": "Health & Fitness",
    "image": "/Images/steps1.webp",
    "vocabulary": [
      "motivator - something that encourages action",
      "daunting - intimidating or discouraging",
      "mortality - the state of being subject to death",
      "variability - the quality of being changeable or inconsistent",
      "consistency - the quality of being stable and reliable",
      "longevity - long life or existence",
      "intensity - the level of effort or force",
      "baseline - the starting point for comparisons",
      "morbidity - the condition of being diseased or unhealthy",
      "cardiovascular - related to the heart and blood vessels"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Recommended Steps per Day by Age</h1>\n\n<p>Tracking how many steps we walk each day can be a great motivator. Having a number, like 10,000 steps a day, can help us act on moving more throughout our day and during intentional bouts of physical activity. Still, this number can sometimes seem daunting.</p>\n\n<h1>Let’s Look at the Research</h1>\n\n<p>Are there health benefits below 10,000 steps? Several studies suggest that with more steps per day, all-cause mortality risk (risk of death from any cause) progressively decreases before leveling off at:</p>\n\n<ul>\n<li>7,000-10,000 steps per day for adults aged 18-59 years</li>\n<li>6,000-8,000 steps per day for adults aged 60+ years</li>\n<li>7,500 steps per day for women aged 62-101 years.</li>\n</ul>\n\n<p>Though there is some variability between studies, there is a good amount of consistency to indicate that there may be a “sweet spot” for longevity somewhere between 6,000 – 10,000 steps depending on your age. Interestingly, there was no significant association between step intensity and mortality, meaning that the decrease in risk of premature death was seen regardless of walking intensity (pace).</p>\n\n<p>So, what if you don’t meet that step count? What if you’re starting a walking program? The science confirms that every bit counts and even small increases benefit your health.</p>\n\n<p>A review of 17 studies among adults 18 years and older found that for each 1,000-step increase a day from baseline, there was a notable risk reduction in all-cause mortality as well as cardiovascular disease morbidity and mortality.</p>\n\n<h1>Take More Steps During the Day</h1>\n\n<p>Try taking 1,000 more steps a day (or over the course of a few weeks) to add life to your years and years to your life! Keep adding a little more at a time – whatever your step goal – to benefit your health and longevity.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What is the main purpose of tracking daily steps?",
            "choices": [
              "A. To make walking more competitive",
              "B. To encourage more movement and physical activity",
              "C. To ensure everyone walks 10,000 steps a day",
              "D. To discourage low-intensity walking"
            ]
          },
          {
            "number": "2",
            "question": "According to research, what is the recommended step range for adults aged 60+?",
            "choices": [
              "A. 4,000-6,000 steps",
              "B. 6,000-8,000 steps",
              "C. 8,000-10,000 steps",
              "D. 10,000+ steps"
            ]
          },
          {
            "number": "3",
            "question": "What does the research suggest about walking intensity?",
            "choices": [
              "A. Higher intensity steps are necessary for health benefits",
              "B. Step intensity has no impact on reducing mortality risk",
              "C. Only slow-paced steps are beneficial",
              "D. The research does not mention intensity"
            ]
          },
          {
            "number": "4",
            "question": "What happens with each 1,000-step increase per day?",
            "choices": [
              "A. No significant health benefits",
              "B. Increased risk of cardiovascular disease",
              "C. Notable reduction in mortality risk",
              "D. Only improved fitness but no health benefits"
            ]
          },
          {
            "number": "5",
            "question": "What is the final recommendation in the text?",
            "choices": [
              "A. Set a fixed step goal and never change it",
              "B. Take fewer steps but at higher intensity",
              "C. Increase steps gradually to improve health and longevity",
              "D. Only walk when necessary to avoid overexertion"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-neighbor-issues",
    "title": "Neighbor issues",
    "subtitle": "Technology",
    "category": "reading-exercises",
    "theme": "Technology",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about common neighbor problems and what to do.",
    "readingcompTitle": "Neighbor issues",
    "topic": "Technology",
    "image": "/Images/neighbors1.webp",
    "vocabulary": [
      "peculiar - unusual or strange",
      "paranoid - excessively worried or anxious",
      "forte - a person's strong point or skill",
      "advocate - someone who publicly supports a cause",
      "contemplates - thinks deeply about something",
      "mediator - someone who helps resolve conflicts",
      "dexterous - skilled with hands or mind",
      "incognito - in disguise or anonymous",
      "mediocrity - the state of being average or unremarkable"
    ],
    "paragraphs": [
      {
        "text": [
          "<p><strong>Noisy neighbors</strong></p>",
          "<p>Your neighbor plays loud music late at night. What would you do?</p>",
          "<p><strong>Parking disputes</strong></p>",
          "<p>Your neighbor constantly parks in front of your driveway. What would you do?</p>",
          "<p><strong>Unfriendly or rude behavior</strong></p>",
          "<p>Your neighbor ignores you or is rude when you try to talk to them. What would you do?</p>",
          "<p><strong>Overgrown garden or messy yard</strong></p>",
          "<p>Your neighbor’s garden is overgrown, and it’s affecting your property. What would you do?</p>",
          "<p><strong>Pet problems</strong></p>",
          "<p>Your neighbor’s dog barks all night, or their cat keeps coming into your yard. What would you do?</p>",
          "<p><strong>Shared wall issues</strong></p>",
          "<p>Your neighbor is doing renovations that are noisy or damaging your shared wall. What would you do?</p>",
          "<p><strong>Unwanted visitors</strong></p>",
          "<p>Your neighbor frequently has noisy parties with guests who block your driveway. What would you do?</p>",
          "<p><strong>Smoking or strong smells</strong></p>",
          "<p>Your neighbor smokes, and the smell comes into your apartment. What would you do?</p>",
          "<p><strong>Borrowing without returning</strong></p>",
          "<p>Your neighbor keeps borrowing things but never returns them. What would you do?</p>",
          "<p><strong>Privacy issues</strong></p>",
          "<p>Your neighbor keeps looking over the fence or watching you too closely. What would you do?</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Which type of tech person is most likely to push for social causes?",
            "choices": [
              "A. The Dexterous Designer",
              "B. The Active Advocate",
              "C. The Hero Hacker",
              "D. The Profound Perfectionist"
            ]
          },
          {
            "number": "2",
            "question": "Who in the office is likely to be the loudest and most social?",
            "choices": [
              "A. The Ultimate Untouchable",
              "B. The Easygoing Extrovert",
              "C. The Nervous Newbie",
              "D. The Hero Hacker"
            ]
          },
          {
            "number": "3",
            "question": "Which tech personality is most focused on code and prefers not to be disturbed?",
            "choices": [
              "A. The Charming Cheerer",
              "B. The Profound Perfectionist",
              "C. The Ultimate Untouchable",
              "D. The Active Advocate"
            ]
          },
          {
            "number": "4",
            "question": "What is the main characteristic of the Profound Perfectionist?",
            "choices": [
              "A. They are outspoken about problems and strive for the best results.",
              "B. They are easygoing and love socializing.",
              "C. They focus on designing visually appealing work.",
              "D. They enjoy hacking systems and finding security flaws."
            ]
          },
          {
            "number": "5",
            "question": "Which tech personality is described as someone who helps boost team confidence?",
            "choices": [
              "A. The Charming Cheerer",
              "B. The Active Advocate",
              "C. The Dexterous Designer",
              "D. The Nervous Newbie"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-utensils-around-the-world",
    "title": "Utensils Around the World",
    "subtitle": "Technology",
    "category": "reading-exercises",
    "theme": "Technology",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about how people eat around the world.",
    "readingcompTitle": "Utensils Around the World",
    "topic": "Technology",
    "image": "/Images/utensils1.webp",
    "vocabulary": [
      "word - def"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>When we think about global cuisines, we often consider food in terms of difference. And the means by which people deliver food to their mouths whether by forks, spoons, chopsticks, flatbreads or fingers&nbsp;often signals that difference the most. Custom and culture can and do shape the variety of ways people eat.</p>",
          "<p>Yet eating around the world isn’t always about what’s different it’s also about what’s the same. Consider the words of Dr. Margaret Visser, cultural anthropologist and author of The Rituals of Dinner: “Food is never just something we eat . . . we use eating as a medium for social relationships: Satisfaction of the most individual of needs becomes a means of creating community.”</p>",
          "<p>Here’s a closer look at some of the most common ways people around the world eat their food and create community.</p>",
          "<p><span style=\"font-size:72px\"><span style=\"color:#A9A9A9\"><span style=\"font-family:georgia,serif\">1</span></span></span><span style=\"font-family:georgia,serif\"><span style=\"font-size:18px\"><span style=\"color:#A9A9A9\"><strong> </strong></span>FORKS, SPOONS &amp; KNIVES</span></span></p>",
          "<p><em><span style=\"font-family:georgia,serif\">*Pottery spoons by Gravesco Pottery</span></em></p>",
          "<p>In Thailand, most people eat with a spoon in the right hand and a fork in the left hand, using the fork to move food onto the spoon. Chopsticks tend to appear only alongside noodle dishes.</p>",
          "<p>In the Philippines, forks and spoons function in a similar way. But at a traditional Kamayan feast, a Tagalog word that translates to “by hand,” a bounty of Filipino dishes sits atop banana leaves and everyone around the table gathers bites of food and rice with their hands.</p>",
          "<p>Forks, knives and spoons may reign as the most common cutlery in the United States, but we still eat plenty of hand-held foods simply for the sake of convenience and speed.</p>",
          "<p>When we think about hamburgers, French fries and corn on the cob, eating with our hands doesn’t seem so different after all.</p>",
          "<p><span style=\"font-family:georgia,serif\"><span style=\"color:#A9A9A9\"><span style=\"font-size:72px\">2</span><span style=\"font-size:18px\">&nbsp;</span></span><span style=\"font-size:18px\">FINGERS &amp; FLATBREADS</span></span></p>",
          "<p><em><span style=\"font-family:georgia,serif\">*Yemeni food includes hummus, tabbouleh, lamb shawarma, rice, naan bread</span></em></p>",
          "<p><em><span style=\"font-family:georgia,serif\">*Ethiopian combination plate includes yebeg alecha, doro wett, sega wett, misir wett, spinach, yellow beans, carrots and green beans eaten on injera bread</span></em></p>",
          "<p>Although dining customs across India vary regionally, eating with the right hand is traditional and common. Roti and naan flatbreads also hold bites of food scooped from communal platters.</p>",
          "<p>Traditional Ethiopian cuisine is served with a large, slightly spongy flatbread called Injera, made from the ancient grain teff. The thin injera tears easily to scoop up individual bites.</p>",
          "<p>Yemeni cuisine favors two types of flatbreads: malooga and lahoh. Malooga often pairs with savory dishes, like those that include beans or eggs, while the spongier sourdough lahoh complements soups and curries.</p>",
          "<p><span style=\"font-family:georgia,serif\"><span style=\"color:#A9A9A9\"><span style=\"font-size:72px\">3</span></span><span style=\"font-size:18px\"><span style=\"color:#A9A9A9\"> </span>CHOPSTICKS</span></span></p>",
          "<p><em><span style=\"font-family:georgia,serif\">*Tofu vegetable pho with five-minute egg</span></em></p>",
          "<p>The word “chopsticks” appears to be an English-language version of the Chinese pidgin “chop chop,” meaning “quickly.\" Invented in China, chopsticks are popular in countries across East Asia, such as Korea, Vietnam, Cambodia, Malaysia and Singapore.</p>",
          "<p>Chopsticks are the most frequently used utensils in Japanese cuisine - just don’t stand the chopsticks up in a bowl of rice. What makes this move a social faux pas? In Japanese burial customs, the deceased’s rice bowl is placed next to the coffin with a pair of chopsticks standing upright. So instead, place the chopsticks together parallel to the edge of the table when not in use, or use a chopstick rest.</p>",
          "<p><span style=\"font-family:georgia,serif\"><span style=\"color:#A9A9A9\"><span style=\"font-size:72px\">4</span></span><span style=\"font-size:18px\"> EDIBLE CUTLERY</span></span></p>",
          "<p><em><span style=\"font-family:georgia,serif\">*Amuse bouche spoon by Gravesco Pottery GravescoPottery.com</span></em></p>",
          "<p><em><span style=\"font-family:georgia,serif\">*Edible spoons by Bocado BocadoProducts.com</span></em></p>",
          "<p>The disposal of single-use eating utensils contributes to the plastic waste crisis. To reduce the carbon footprint left by these wasteful utensils, companies around the world have started developing biodegradable and edible cutlery that can be eaten at the end of the meal.</p>",
          "<p>India-based company Bakeys molds spoons and forks from a mixture of sorghum, rice and wheat flours. The spoons even come in three flavors: savory, sweet and plain. U.S. company Bocado makes edible spoons targeted toward one-bite appetizers.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Which type of tech person is most likely to push for social causes?",
            "choices": [
              "A. The Dexterous Designer",
              "B. The Active Advocate",
              "C. The Hero Hacker",
              "D. The Profound Perfectionist"
            ]
          },
          {
            "number": "2",
            "question": "Who in the office is likely to be the loudest and most social?",
            "choices": [
              "A. The Ultimate Untouchable",
              "B. The Easygoing Extrovert",
              "C. The Nervous Newbie",
              "D. The Hero Hacker"
            ]
          },
          {
            "number": "3",
            "question": "Which tech personality is most focused on code and prefers not to be disturbed?",
            "choices": [
              "A. The Charming Cheerer",
              "B. The Profound Perfectionist",
              "C. The Ultimate Untouchable",
              "D. The Active Advocate"
            ]
          },
          {
            "number": "4",
            "question": "What is the main characteristic of the Profound Perfectionist?",
            "choices": [
              "A. They are outspoken about problems and strive for the best results.",
              "B. They are easygoing and love socializing.",
              "C. They focus on designing visually appealing work.",
              "D. They enjoy hacking systems and finding security flaws."
            ]
          },
          {
            "number": "5",
            "question": "Which tech personality is described as someone who helps boost team confidence?",
            "choices": [
              "A. The Charming Cheerer",
              "B. The Active Advocate",
              "C. The Dexterous Designer",
              "D. The Nervous Newbie"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-stereotypes-about-tech-workers",
    "title": "Stereotypes About Tech Workers",
    "subtitle": "Workplace Culture in Tech",
    "category": "reading-exercises",
    "theme": "Workplace Culture in Tech",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about common stereotypes about tech workers.",
    "readingcompTitle": "Stereotypes About Tech Workers",
    "topic": "Workplace Culture in Tech",
    "image": "/Images/techpeople1.webp",
    "vocabulary": [
      "stereotype - a widely held but oversimplified idea about a group of people",
      "introverted - preferring to spend time alone rather than with others",
      "workaholic - someone who works excessively and compulsively",
      "automation - the use of technology to perform tasks without human input",
      "agile - a flexible and iterative approach to project management",
      "disruptive - causing change in an industry by introducing innovation",
      "retrospective - a meeting to review past work and improve future performance",
      "idealistic - believing in high principles and goals, sometimes unrealistically",
      "casual - relaxed and informal",
      "loyal - showing strong support for something or someone"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Stereotypes About Tech Workers</h1><p>There are plenty of stereotypes about people who work at tech companies—some are based on truth, while others are exaggerated. Here are some of the most common ones:</p>",
          "<h1>Personality & Behavior</h1><p><strong>Introverted & Socially Awkward</strong> – Tech workers are often seen as quiet, preferring to code rather than engage in small talk.</p><p><strong>Workaholic</strong> – They live at their desks, working late nights and weekends.</p><p><strong>Overconfident 'Tech Bros'</strong> – Some believe male-dominated tech spaces breed arrogance and overconfidence.</p><p><strong>Idealistic & Change-the-World Mindset</strong> – Many tech employees are portrayed as believing their work is revolutionary, even when it's just another app.</p>",
          "<h1>Appearance & Lifestyle</h1><p><strong>Casual Dress Code</strong> – Hoodies, jeans, and sneakers (thanks, Mark Zuckerberg).</p><p><strong>Caffeine-Dependent</strong> – Coffee, energy drinks, or Soylent as fuel.</p><p><strong>Gamer & Sci-Fi Enthusiast</strong> – Many are thought to be obsessed with gaming, Star Wars, and anime.</p><p><strong>Health-Conscious (or the Opposite)</strong> – Either into yoga and standing desks or surviving on pizza and Red Bull.</p>",
          "<h1>Work & Skills</h1><p><strong>Super Smart but Lacking Common Sense</strong> – Brilliant at coding but struggles with everyday tasks like cooking or social interactions.</p><p><strong>Obsessed with Automation</strong> – They'd rather spend hours coding a script to avoid a 5-minute task.</p><p><strong>Only Knows One Programming Language</strong> – Often mocked for being overly loyal to Python, JavaScript, or some trendy framework.</p><p><strong>Startup Mentality</strong> – Works in a 'disruptive' company but mostly builds features no one asked for.</p>",
          "<h1>Office Culture</h1><p><strong>Open Offices & Free Snacks</strong> – Tech companies are depicted as having bean bags, ping-pong tables, and kombucha on tap.</p><p><strong>Too Many Meetings</strong> – Agile stand-ups, sprint planning, and retrospectives that seem endless.</p><p><strong>Remote Work Fanatics</strong> – Either fully remote or refuses to go to the office unless there’s free food.</p><p>Of course, these stereotypes don’t apply to everyone, but they’ve been reinforced by pop culture, memes, and real-life experiences. Which ones do you think are the most accurate?</p>",
          "<h1>Stereotypes About UX Researchers & Designers</h1><p><strong>'The Artsy One'</strong> – UX designers are seen as the creative, free-spirited people in a room full of engineers.</p><p><strong>'Post-it Note Fanatic'</strong> – Always conducting brainstorming sessions with walls covered in sticky notes.</p><p><strong>'Empathy Guru'</strong> – UX researchers are portrayed as hyper-focused on user feelings and experiences, sometimes to the point of being impractical.</p><p><strong>'Design Perfectionist'</strong> – Obsessed with pixel-perfect alignment and color schemes, even when users may not notice.</p><p><strong>Minimalist Aesthetic</strong> – Often wears sleek, monochrome outfits, usually paired with stylish glasses.</p><p><strong>Apple Everything</strong> – MacBook, iPad, iPhone—never an Android or Windows device in sight.</p><p><strong>Loves Coffee & Matcha</strong> – Seen as either a coffee connoisseur or someone who swears by matcha lattes.</p><p><strong>Always at a Café</strong> – Prefers working remotely from a trendy café rather than the office.</p><p><strong>Sketches Everything</strong> – Even if it’s a simple idea, they must sketch it out first.</p><p><strong>Figma Over Everything</strong> – Uses Figma for everything, even things it wasn’t meant for.</p><p><strong>Hates Developers’ Tweaks</strong> – Frustrated when devs change the perfect design because 'it’s easier to implement.'</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Which of these stereotypes do you think is the most accurate? Why?",
            "choices": [
              "A. Your opinion"
            ]
          },
          {
            "number": "2",
            "question": "Do you think tech company cultures encourage or break down these stereotypes? Explain your answer.",
            "choices": [
              "A. Your opinion"
            ]
          },
          {
            "number": "3",
            "question": "Do you think you would find more of these stereotypes if you worked at another tech company? Why or why not?",
            "choices": [
              "A. Your opinion"
            ]
          },
          {
            "number": "4",
            "question": "How do you think media and movies influence people's views on tech workers?",
            "choices": [
              "A. Your opinion"
            ]
          },
          {
            "number": "5",
            "question": "What other stereotypes about tech workers have you heard? Do you think they are fair?",
            "choices": [
              "A. Your opinion"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-neighbor-conflicts-and-etiquette",
    "title": "Neighbor Conflicts and Etiquette",
    "subtitle": "Neighbors and Community Living",
    "category": "reading-exercises",
    "theme": "Neighbors and Community Living",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about real neighbor conflicts and how to handle them.",
    "readingcompTitle": "Neighbor Conflicts and Etiquette",
    "topic": "Neighbors and Community Living",
    "image": "/Images/neighbors1.webp",
    "vocabulary": [
      "suburban - relating to a residential area outside a city",
      "small talk - casual conversation about unimportant topics",
      "porch - a covered shelter attached to the entrance of a house",
      "reclusive - avoiding the company of other people",
      "extension cord - a length of electrical cable that allows devices to be plugged in at a distance",
      "unplug - to disconnect from a power source",
      "landlord - a person who rents property to tenants",
      "yelling - shouting loudly in anger, excitement, or frustration",
      "mess - a situation that is confused or full of problems",
      "issue - an important topic or problem for debate or discussion"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>How to Handle a Neighbor Going Into My Mailbox</h1>",
          "<p>We live in a quiet suburban neighborhood. I have two neighbors living across from me that I don’t know too well. Aside from waving in passing or quick small talk, this is about all. A few days ago, their package got delivered to my house. It had their name and address on it. This was clearly a mistake, and I brought it over to their house as soon as I got home. First time I had been on their porch. The neighbor was thankful.</p>",
          "<p>The next day, their spouse walked out of their front door and directly over to my front porch. I know this because she was on my RING camera. She looked around, didn’t knock at all. She then proceeded to walk down my driveway and open my mailbox! On the camera, you can see her take out a package and walk back to her house! What does one even do in this situation? I don’t want to cause an issue with neighbors, but this is out of line?</p>",
          "<h1>Are We the A-Holes Here?</h1>",
          "<p>My partner (28F) and I (28M) recently moved into an apartment and have gotten two noise complaints in the past few months. The first was for loud walking. We got a carpet and started leaving our shoes at the door to fix this. Today we just received a call from our landlord that we had another complaint two days ago.</p>",
          "<p>On this day, we were out of the house all morning, until about 2:30 p.m. When we got home, we cooked dinner, watched the Superbowl, and went to bed before 10 p.m. We didn’t have anyone over. Neither of us cared about the game, so there was no yelling. There are only a couple of points throughout the day where it could have come from.</p>",
          "<p>Our dog occasionally will drop his bone on the ground, or we may accidentally kick one. We had music on for literally 10 minutes while cooking at 3 p.m. (it was a JBL clip at about half volume). The last thing we want to be are the annoying neighbors. We’ve had them before and don’t think anyone should have to deal with that. Our neighbor is reclusive, so it’s hard to talk to her and fix it that way. Does anyone have any advice on how to handle this one?</p>",
          "<h1>Neighbor Keeps Stealing Power</h1>",
          "<p>I live in Canada, and during the winters it gets cold enough that we have to plug in our vehicles when not in use.</p>",
          "<p>Well, in the back alley where we park the truck (no plug in on the front of the house, and street parking is a mess anyways), I’ve caught an extension cord being run from our extension cord, straight into the neighbor’s yard. The first time, they even unplugged our truck so they could use the cord.</p>",
          "<p>We just unplugged their cord and tossed it back into their yard, hoping that they would get the hint. But this morning, their extension cord was plugged in AGAIN. I’m unsure what to do in this situation. Do I call the police? Would they care? I can’t NOT have the cord back there, as the truck has to be plugged in. What should I do?</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What would be the best way to handle this situation?",
            "choices": [
              "A. Confront the neighbor aggressively",
              "B. Report the incident to the police",
              "C. Have a calm discussion with the neighbor",
              "D. Ignore it completely"
            ]
          },
          {
            "number": "2",
            "question": "What is the main concern of the couple?",
            "choices": [
              "A. They want to move to a different apartment",
              "B. They are worried about being noisy neighbors",
              "C. They are upset with their landlord",
              "D. They are looking for revenge against the neighbor"
            ]
          },
          {
            "number": "3",
            "question": "Why is the writer concerned about their extension cord?",
            "choices": [
              "A. It is expensive to replace",
              "B. The neighbor keeps unplugging their truck to use it",
              "C. It’s too short for their truck",
              "D. They are worried about an electrical fire"
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-plane-crashes-perception-vs-reality",
    "title": "Plane Crashes: Perception vs. Reality",
    "subtitle": "Aviation Safety",
    "category": "reading-exercises",
    "theme": "Aviation Safety",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about why plane crashes feel more common than they are.",
    "readingcompTitle": "Plane Crashes: Perception vs. Reality",
    "topic": "Aviation Safety",
    "image": "/Images/plane1.webp",
    "vocabulary": [
      "wreckage - the remains of something that has been destroyed",
      "deceiving - like a lie",
      "hold - stay the same",
      "tie - be equal to",
      "collision - an incident in which two or more objects crash into each other",
      "preliminary - initial or early data before final confirmation",
      "fatal - causing death",
      "statistical anomaly - a deviation from the usual or expected pattern in data",
      "trendline - a general direction in which something is developing or changing",
      "stand out - to be clear and obvious",
      "runway incursion - an incident where something is on the runway when it should not be",
      "mantra - a commonly repeated phrase or belief",
      "vigilant - keeping careful watch for potential danger or difficulties",
      "incursions - an unwanted or unexpected entry"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>It Feels Like There Are More Plane Crashes and Incidents. Here’s the Truth</h1>",
          "<p>Recent crashes, like Delta flight 4819 in Toronto and the American Airlines/US Army collision, may feel alarming. But overall statistics show that air travel is safer than ever.</p>",
          "<p>Preliminary data from January 2025 show a record low number of airplane accidents nationwide: 62 total, down from 80 in January 2024. Most involved private flights. Fatal accidents were just 10, including one commercial airliner.</p>",
          "<p>Commercial air travel in the US has seen fewer fatalities over the past 15 years than any previous period. For each mile traveled, flying remains far safer than driving.</p>",
          "<p>Long-term trends confirm this improvement: each of the past 12 years had fewer accidents than any previous year dating back to 1982. The 2020s have been the safest decade for fatal accidents, all under 300 annually, compared to over 600 in the early 1980s.</p>",
          "<p>Most crashes involve small private planes, which receive less media attention. Even serious near misses are rare: 23 serious runway incursions occurred in 2023, but only seven in 2024, the lowest in a decade.</p>",
          "<p>While no transportation is perfectly safe, air travel continues to improve in safety, and statistically, it remains one of the safest ways to travel.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "What does the article suggest about aviation safety trends?",
            "choices": [
              "A. Air travel has become significantly more dangerous.",
              "B. The number of airplane accidents has remained constant.",
              "C. Air travel has become safer over time.",
              "D. Aviation safety has worsened in the past decade."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-sports-hate",
    "title": "Sports Hate",
    "subtitle": "Sports",
    "category": "reading-exercises",
    "theme": "Sports",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about what it's like to dislike sports.",
    "readingcompTitle": "Sports Hate",
    "topic": "Sports",
    "image": "/Images/watchingsports5.webp",
    "vocabulary": [
      "wreckage - the remains of something that has been destroyed"
    ],
    "paragraphs": [
      {
        "text": [
          "<h2>7 Things You Will Relate to If You Hate Sports</h2>",
          "<p>Most of us played sports as kids, either because we wanted to or because we had to. Some people loved sports so much that they made it their career or kept playing to stay healthy. But others never liked sports at all.</p>",
          "<p>Some people are passionate about sports. They follow every game, support their favorite team, and even wear team jerseys or paint their faces. But if you don’t like sports, you might find all this confusing. And that’s okay! It’s completely normal not to enjoy sports, even if others don’t understand why. If you dislike sports, you might relate to these points.</p>",
          "<h3>1. You don’t understand the excitement</h3>",
          "<p>When there’s a big sports event like the World Cup, everyone seems excited—but you don’t get why. It feels like the whole world is focused on the game, and people even argue about their favorite teams. You just don’t see the point.</p>",
          "<h3>2. You hated PE classes</h3>",
          "<p>Thinking about school reminds you of those exhausting physical education classes. Running laps, doing exercises, and playing games you didn’t enjoy was not fun. And the body aches afterward? Unforgettable.</p>",
          "<h3>3. Sports talk is confusing</h3>",
          "<p>Being in a group of sports fans can be overwhelming. They talk about game strategies and player stats, and you have no idea what they mean. It’s hard to keep up, and honestly, you don’t really want to.</p>",
          "<h3>4. You can’t watch your favorite shows</h3>",
          "<p>If you live with sports fans, watching TV can be a struggle. When a big game is on, they take over the TV, and you have to fight to watch your favorite show—or anything that isn’t sports-related.</p>",
          "<h3>5. You have bad memories of playing sports</h3>",
          "<p>Watching a game reminds you of all the times you got hurt because you weren’t good at sports. You remember the pain and the embarrassment of those moments.</p>",
          "<h3>6. Big tournaments give you a headache</h3>",
          "<p>During major sports events, it feels like everything is about sports. People talk about it everywhere, and your social media is full of updates. It can be overwhelming, and you just want a break.</p>",
          "<h3>7. You struggle to join conversations</h3>",
          "<p>When your friends or colleagues talk about sports, you don’t have much to say. They know everything about the teams and players, while you can barely remember who won the last game. It can feel awkward, but you just don’t care enough to learn more.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Why do you think some people are so passionate about sports while others dislike them?",
            "choices": [
              "A. Because sports create a sense of belonging and excitement.",
              "B. Because people are influenced by their family and culture.",
              "C. Because sports are either fun or boring, and it's just personal preference.",
              "D. Because sports fans like competition, while others find it unnecessary."
            ]
          },
          {
            "number": "2",
            "question": "Do you think people who dislike sports feel excluded in social situations?",
            "choices": [
              "A. Yes, because many social events revolve around sports.",
              "B. No, because there are plenty of other topics to talk about.",
              "C. It depends on the country and how popular sports are there.",
              "D. No, because sports are not as important as people think."
            ]
          },
          {
            "number": "3",
            "question": "What do you think is the main reason some people dislike sports?",
            "choices": [
              "A. They find sports boring and repetitive.",
              "B. They had bad experiences with sports in school.",
              "C. They don’t understand the rules and strategies.",
              "D. They prefer other activities that don’t involve competition."
            ]
          },
          {
            "number": "4",
            "question": "How do you feel about the way sports dominate television and social media during big tournaments?",
            "choices": [
              "A. It’s exciting because it brings people together.",
              "B. It’s annoying because not everyone cares about sports.",
              "C. It’s normal because sports are an important part of culture.",
              "D. It’s overwhelming because sports get too much attention."
            ]
          },
          {
            "number": "5",
            "question": "Should schools make physical education optional for students who dislike sports?",
            "choices": [
              "A. Yes, because forcing students to play sports is unfair.",
              "B. No, because physical activity is important for everyone.",
              "C. Maybe, if students can choose another form of exercise.",
              "D. No, because sports teach valuable skills beyond fitness."
            ]
          }
        ]
      }
    ]
  },
  {    "id": "reading-extreme-weather-experiences",
    "title": "Extreme Weather Experiences",
    "subtitle": "Sports",
    "category": "reading-exercises",
    "theme": "Sports",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read four personal stories about surviving extreme weather.",
    "readingcompTitle": "Extreme Weather Experiences",
    "topic": "Sports",
    "image": "/Images/extrememweather1.webp",
    "vocabulary": [
      "huddled - in a sitting position, hiding from something",
      "peeked - looked just a little bit",
      "bolt - lighting bolt, one 'piece' of lightning",
      "snow drifts - piles of snow next to buildings or fences",
      "siblings - brothers and sisters"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>Extreme Weather Experiences</h1>",
          "<p>I was living by myself when Hurricane Charley went through Florida. I was huddled in my house with a candle (power had gone out) just listening to a hellish storm shake my old wood house. At one point I got up and peeked out the window. There were strange green flashes going off all over. I realized they were power transformers exploding.</p>",
          "<p>***</p>",
          "<p>Microburst winds in a thunderstorm. I knew the storm was coming, so I ran to my neighbor's house to make sure her dog was away (he was). As I turned to run back, the wind came out of nowhere, and in the few hundred feet I ran home, 6 or 7 trees and large branches came down all around me in a matter of seconds. One huge branch missed me by about 10 ft. The sound of the rush of wind, then cracking and crashing of trees is something I’ll never forget.</p>",
          "<p>***</p>",
          "<p>Walking home from a friend's house and nearly got hit by lightning. The bolt was really thick, bright, and loud and I could taste the burning air. I'd say it struck about 400 feet behind my house.</p>",
          "<p>***</p>",
          "<p>I'm from rural North Dakota and 10 years ago we had a blizzard that trapped my family and I in our home for 4 days. We had snow drifts 8 feet high, minus 30 degree weather and we lived 15 miles from town. My parents didn't tell us until we were adults, but there was only two days' worth of food in the house. My parents didn't eat anything for four days to make sure my siblings and I were fed.</p>"
        ],
        "questions": []
      }
    ]
  },
  {    "id": "reading-education",
    "title": "Education",
    "subtitle": "Sports",
    "category": "reading-exercises",
    "theme": "Sports",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about how tech employees really learn at work.",
    "readingcompTitle": "Education",
    "topic": "Sports",
    "image": "/Images/education1.webp",
    "vocabulary": [
      "evolve - change",
      "cited - said",
      "peer - person in the same group",
      "replicate - make again"
    ],
    "paragraphs": [
      {
        "text": [
          "<h1>How Tech Employees Really Learn: The Case Against Training Programs</h1>",
          "<p>Companies invest millions in workplace training, yet many employees admit they barely pay attention during these sessions. In the tech industry, where skills evolve rapidly, formal training programs often lag behind real-world needs. So, how do tech employees actually learn?</p>",
          "<p>A 2023 survey of software engineers found that less than <strong>20%</strong> believed company training significantly improved their skills. Instead, most developers cited <strong>online resources, trial and error, and peer collaboration</strong> as their primary learning methods. Platforms like <strong>Stack Overflow, GitHub, and YouTube tutorials</strong> provide instant answers to real problems—something corporate training struggles to replicate.</p>",
          "<p>Moreover, traditional training sessions are often <strong>too generic</strong> or <strong>not relevant to daily tasks</strong>. A backend engineer, for example, might be forced to sit through a general cybersecurity seminar that offers little practical value. Many tech employees feel that 'learning by doing' is far more effective than listening to lectures or clicking through e-learning modules.</p>",
          "<p>So why do companies keep investing in training? Critics argue that these programs exist <strong>more for compliance and HR checkboxes than actual skill development</strong>. While some targeted training—like security protocols or onboarding—can be useful, most developers would rather spend their time solving real problems.</p>",
          "<p>Should tech companies rethink their approach to employee learning? Or is training still a necessary part of professional development?</p>"
        ],
        "questions": []
      }
    ]
  },
  {    "id": "reading-changing-food-culture",
    "title": "Changing Food Culture",
    "subtitle": "Fast Food",
    "category": "reading-exercises",
    "theme": "Fast Food",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about how food culture has changed from traditional to modern.",
    "readingcompTitle": "Changing Food Culture",
    "topic": "Fast Food",
    "image": "/Images/foodculture1.webp",
    "vocabulary": [
      "traditional - old school",
      "households - families living together",
      "snackification - the trend of eating smaller, more frequent meals",
      "canteens - company cafeterias",
      "social spaces - areas for people to gather and interact",
      "variety - different types of food"
    ],
    "paragraphs": [
      {
        "text": [
          "<h2>Traditional vs. Modern Food Culture</h2>",
          "<p>In the past, people ate meals at fixed times, such as breakfast at 8, lunch at 12, and dinner at 6. Meals were planned and shared with family, helping give structure to daily life.</p>",
          "<p>Today, this has changed. Busy work schedules, flexible hours, and more single-person households mean people often eat alone, quickly, or while working. Traditional meals are now often replaced by snacks, meal bars, or smoothies.</p>",
          "<p>This change is sometimes called 'snackification.' Many companies now offer ready-to-eat meals, takeaway food, and meal kits to support this fast-paced lifestyle. The global market for ready-to-eat meals is worth hundreds of billions of dollars.</p>",
          "<p>During the COVID pandemic, people returned to home cooking. With restaurants closed, many cooked more often, using recipe apps and online classes. Today, some people still enjoy cooking special meals at home.</p>",
          "<p>As work and home life mix more, company canteens have become important social spaces. Some companies now even offer food cards for remote workers to buy meals outside.</p>",
          "<p>Our food culture continues to change. We now eat more flexibly and personally, but still want quality and variety.</p>"
        ],
        "questions": []
      }
    ]
  },
  {    "id": "reading-why-we-didn-t-open-a-bakery-storefront",
    "title": "Why We Didn't Open a Bakery Storefront",
    "subtitle": "Side Jobs",
    "category": "reading-exercises",
    "theme": "Side Jobs",
    "level": "C1",
    "levelname": "C1 – Advanced",
    "time": "",
    "description": "Read about why a baker decided not to open a retail storefront.",
    "readingcompTitle": "Why We Didn't Open a Bakery Storefront",
    "topic": "Side Jobs",
    "image": "/Images/bakery.webp",
    "vocabulary": [
      "monotonous - repetitive and dull",
      "inventory - the goods and materials a business holds for sale",
      "logistics - the detailed coordination of complex operations",
      "margins - the difference between the cost and selling price",
      "fallacy - a mistaken belief",
      "impulse buying - unplanned purchases made spontaneously",
      "curating - selecting or organizing something carefully",
      "panacea - a solution for all problems",
      "retrofit - to add new technology or features to older systems",
      "capital expenditure - money spent by a business on major physical goods or services",
      "make ends meet - to earn just enough to cover expenses",
      "hit or miss - unpredictable in success or failure",
      "black hole - something that consumes time or effort endlessly",
      "do well - to succeed or perform successfully",
      "pull the plug - to cancel or stop something completely",
      "time suck - something that consumes a lot of time with little return",
      "cash suck - something that consumes a lot of money with little return",
      "fallacy - a mistaken belief, especially one based on unsound reasoning",
      "hustle at the markets - to work hard and persistently selling at markets",
      "the hill you end up dying on - a challenge or commitment that leads to failure or exhaustion",
      "long slow drift - a gradual and often unwanted change",
      "on all the time - constantly energetic or performative in public settings",
      "drained me - exhausted me emotionally or physically",
      "out of business - no longer operating due to failure",
      "lucky time and place - fortunate timing and circumstances",
      "scale up - to grow a business or operation in size or capacity"
    ],
    "paragraphs": [
      {
        "text": [
          "<p>I had a small bakery, initially out of the home, then out of a rented commercial kitchen, and into other retail stores. We did well and were considering moving into our own retail space. In the end — it wasn't worth it for us. Here's why we decided against it:</p>",
          "<p>It's not baking anymore at a certain volume, it's just basic chemistry. It gets monotonous, and boring and repetitive, and if you feel that baking is an outlet for your creativity, there's a chance you're going to hate it after a while.</p>",
          "<p>You spend more time buying things than you do baking. Flour by cartload, sugar by the cartload, storing it, rotating it, learning how to keep inventory, figuring out if retail vs commercial is cheaper, trying to get a massive food supplier to deal with you — it can turn into a ton of logistics.</p>",
          "<p>Inevitably, when you look at your margins, you realize that to make ends meet and make a profit, you have to start buying different ingredients. Your cake, for example, is great because you make it with a really nice oil, but buying that in bulk is expensive. You start thinking about maybe using canola oil. It can be a long slow drift into the kind of bakery you didn't ever want to be.</p>",
          "<p>Farmer's markets are not fun. They're tiring, you have to like customer-facing events, and you have to be 'on' all the time. I liked baking, but the people — holy hell, the people drained me like I didn't believe was possible. Where I am, there's a fallacy that markets are a stepping stone to retail, but everyone with a store still has to hustle at the markets every weekend.</p>",
          "<p>Retail storefront is a HUGE cash and time suck. Better locations mean more rent, and you have to get the customers to come to you specifically. Your success in markets was that the customers came to the event — that is a BIG distinction. Also, some landlords simply didn’t want another bakery.</p>",
          "<p>Social media can be really hit or miss. Sometimes it works, sometimes it doesn't. It can be a black hole of time and effort without much return. You can’t rely on it completely.</p>",
          "<p>Everything has to scale up — your mixer, your oven, your counter. Nothing at home is big enough. You need commercial-grade everything, which costs money.</p>",
          "<p>As a contrast to the success stories: There was a bakery in my city that was loved. They did everything right, moved into a retail store, bought new equipment — and were out of business in a year. A coffee place took over and did fine, mostly due to lucky timing.</p>",
          "<p>I wouldn’t undo the experience, but I wouldn’t do it again. The lessons I learned helped me in other sectors, but they were tough. If you’re starting small, you're doing it right. The retail storefront should be the LAST step — that’s where the risk gets very high.</p>"
        ],
        "questions": [
          {
            "number": "1",
            "question": "Why did the writer ultimately decide against opening a retail bakery?",
            "choices": [
              "A. They didn't enjoy baking anymore",
              "B. The market was too competitive",
              "C. The risk and long-term commitment were too high",
              "D. They preferred to sell at farmer's markets"
            ]
          },
          {
            "number": "2",
            "question": "What does the author say about farmer's markets?",
            "choices": [
              "A. They are the best way to make money",
              "B. They are fun and easy",
              "C. They require energy and are exhausting",
              "D. They are the perfect step before retail"
            ]
          },
          {
            "number": "3",
            "question": "What lesson did the author learn about social media?",
            "choices": [
              "A. It guarantees success if used properly",
              "B. It is a waste of time for all businesses",
              "C. It can be unpredictable and unreliable",
              "D. It works well for bakeries"
            ]
          },
          {
            "number": "4",
            "question": "Why did the author mention another bakery in the city?",
            "choices": [
              "A. To show that retail stores always fail",
              "B. To illustrate how even popular bakeries can fail",
              "C. To promote a local business",
              "D. To explain how coffee shops are more profitable"
            ]
          },
          {
            "number": "5",
            "question": "What advice does the author give to people starting a small bakery?",
            "choices": [
              "A. Avoid farmer's markets",
              "B. Take on debt early",
              "C. Start with a retail store",
              "D. Make the retail storefront the final step"
            ]
          }
        ]
      }
    ]
  }
];
